/* Klaro Journal — web app layer.
 *
 * Talks to the same Klaro API the mobile app uses (devise_token_auth,
 * /api/v3/collection_ui) so the journal is one dataset across app + web.
 * Pure grouping/filter/sort/merge logic lives in journal-logic.js.
 *
 * Auth is a silent handshake: Liquid renders a per-customer journal token
 * (customer metafield, only visible to the logged-in customer) and the page
 * exchanges it for Klaro API tokens — no second login.
 *
 * States:
 *   shopify-logged-out → redirect to the store login
 *   no journal token   → "setting up" (new-customer race: metafield lags
 *                        account creation by a few seconds; auto-retry)
 *   handshake          → exchange metafield token for API tokens, silent
 *   connected          → journal UI
 *   API 401            → clear tokens, re-handshake once, then error card
 */
(function () {
  'use strict';

  var L = window.JournalLogic;
  var CONFIG = window.KLARO_JOURNAL_CONFIG || {};
  var AUTH_STORAGE_KEY = 'klaro:webJournal:auth';
  var PREFS_STORAGE_KEY = 'klaro:webJournal:prefs';

  var apiOverridden = false;
  var forceReadOnly = false;
  var apiBase = (function () {
    try {
      var override = localStorage.getItem('klaroApiBase');
      var qs = new URLSearchParams(location.search);
      if (qs.get('ro') === '1') forceReadOnly = true;
      // Dev harness: ?jt=<token> injects a journal token minted against the
      // local stack (journal-dev.html only — Liquid supplies it in prod).
      if (qs.get('jt')) CONFIG.journalToken = qs.get('jt');
      if (qs.get('api') === 'local') { apiOverridden = true; return 'http://localhost:4000'; }
      if (override) { apiOverridden = true; return override; }
    } catch (e) {}
    return CONFIG.apiBase || 'https://api.klarolabel.com';
  })();

  // Read-only: fetch + render only, no PUT/PATCH ever reaches the API.
  // Set via config (production safety) unless the API was explicitly
  // overridden to the local test stack; ?ro=1 forces it anywhere.
  var READ_ONLY = forceReadOnly || (CONFIG.readOnly === true && !apiOverridden);

  // ?demoOrders=1 — swap in a fake purchase history built from REAL
  // klarocigars products (titles/vendors/prices/images pulled from the live
  // catalog) so the order-import flow can be exercised on accounts with no
  // orders. Never active without the param.
  var DEMO_ORDERS = [
    { name: '#2381', processedAt: 1781913600000, items: [
      { title: 'Fuente Fuente Opus X Society - Edición De La Sociedad', vendor: 'Arturo Fuente', quantity: 2, price: 90.0, type: 'Cigars', image: 'https://cdn.shopify.com/s/files/1/0574/4649/8463/files/10-cigars-box-cigarsdirect.jpg?v=1774589424&width=120' },
      { title: 'Chateau Diadem Splendor', vendor: 'Dapper', quantity: 1, price: 90.0, type: 'Cigars', image: 'https://cdn.shopify.com/s/files/1/0574/4649/8463/files/splendor.webp?v=1780601784&width=120' },
    ] },
    { name: '#2214', processedAt: 1777680000000, items: [
      { title: 'Aladino Corojo Reserva', vendor: 'JRE', quantity: 3, price: 45.0, type: 'Cigars', image: 'https://cdn.shopify.com/s/files/1/0574/4649/8463/files/aladino.png?v=1779116998&width=120' },
      { title: 'Kristoff Cameroon', vendor: 'Kristoff', quantity: 2, price: 49.5, type: 'Cigars', image: 'https://cdn.shopify.com/s/files/1/0574/4649/8463/files/kristoffcameroon.jpg?v=1780958963&width=120' },
      { title: 'Overland Pro Lighter - Black', vendor: 'Klaro Cigars', quantity: 1, price: 84.99, type: 'Lighter', image: 'https://cdn.shopify.com/s/files/1/0574/4649/8463/files/MainListingImage_1a38ef88-605f-410a-9ca3-60cc97c9b99a.png?v=1780751228&width=120' },
    ] },
    { name: '#2073', processedAt: 1773446400000, items: [
      { title: 'Crux Marblehead', vendor: 'Crux Cigars', quantity: 5, price: 36.0, type: 'Cigars', image: 'https://cdn.shopify.com/s/files/1/0574/4649/8463/files/marblehead_ef6525a9-d364-4d88-817f-bec3ed29eccd.webp?v=1780604955&width=120' },
      { title: 'Cavalier Domaine Rouge', vendor: 'Cavalier Geneve', quantity: 1, price: 73.0, type: 'Cigars', image: 'https://cdn.shopify.com/s/files/1/0574/4649/8463/files/domaine.jpg?v=1780602695&width=120' },
      { title: 'Aladino Corojo Reserva', vendor: 'JRE', quantity: 2, price: 45.0, type: 'Cigars', image: 'https://cdn.shopify.com/s/files/1/0574/4649/8463/files/aladino.png?v=1779116998&width=120' },
    ] },
  ];
  try {
    if (new URLSearchParams(location.search).get('demoOrders') === '1') {
      CONFIG.orders = DEMO_ORDERS;
      CONFIG.ordersCount = DEMO_ORDERS.length;
    }
  } catch (e) {}

  // ── KlaroAPI ───────────────────────────────────────────────────────────

  var KlaroAPI = {
    getStoredAuth: function () {
      try {
        var raw = localStorage.getItem(AUTH_STORAGE_KEY);
        if (!raw) return null;
        var parsed = JSON.parse(raw);
        if (parsed && parsed.accessToken && parsed.client && parsed.uid) return parsed;
      } catch (e) {}
      return null;
    },

    storeAuth: function (auth) {
      try { localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth)); } catch (e) {}
    },

    clearAuth: function () {
      try { localStorage.removeItem(AUTH_STORAGE_KEY); } catch (e) {}
    },

    // The API returns errors in several shapes: a plain string, an array,
    // or an object with full_messages. Normalize all three.
    errorMessage: function (body, fallback) {
      var errors = body && body.errors;
      if (typeof errors === 'string' && errors.trim()) return errors;
      if (Array.isArray(errors) && errors.length && typeof errors[0] === 'string') return errors[0];
      if (errors && Array.isArray(errors.full_messages) && errors.full_messages.length) {
        return errors.full_messages[0];
      }
      return fallback;
    },

    // Exchange the Liquid-rendered journal token (customer metafield) for
    // devise API tokens. Tokens ride in the response body so nothing
    // depends on CORS header exposure.
    handshake: function () {
      if (!CONFIG.journalToken) {
        var noToken = new Error('No journal token');
        noToken.noToken = true;
        return Promise.reject(noToken);
      }
      return fetch(apiBase + '/journal/handshake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ token: CONFIG.journalToken }),
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (body) {
          if (!res.ok) {
            var err = new Error(KlaroAPI.errorMessage(body, 'Could not connect your journal.'));
            err.status = res.status;
            throw err;
          }
          var tokens = body.tokens || {};
          var auth = {
            accessToken: tokens['access-token'],
            client: tokens.client,
            uid: tokens.uid || (body.user && body.user.email) || CONFIG.customerEmail,
            expiry: tokens.expiry,
            customerId: CONFIG.customerId != null ? String(CONFIG.customerId) : null,
          };
          if (!auth.accessToken || !auth.client) {
            throw new Error('Could not read auth tokens from the server response.');
          }
          KlaroAPI.storeAuth(auth);
          return auth;
        });
      });
    },

    // Merge a Klaro app account under a different email into this one.
    // Ownership of the other account is proven by its password.
    mergeAccount: function (email, password) {
      var auth = KlaroAPI.getStoredAuth();
      if (!auth) {
        var noAuth = new Error('Not connected');
        noAuth.status = 401;
        return Promise.reject(noAuth);
      }
      return fetch(apiBase + '/journal/merge_account', {
        method: 'POST',
        headers: {
          'access-token': auth.accessToken,
          client: auth.client,
          uid: auth.uid,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email, password: password }),
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (body) {
          if (!res.ok) {
            var err = new Error(KlaroAPI.errorMessage(body, 'Could not link that account.'));
            err.status = res.status;
            throw err;
          }
          return body;
        });
      });
    },

    apiFetch: function (path, options) {
      options = options || {};
      if (READ_ONLY && options.method && options.method !== 'GET') {
        var roErr = new Error('Read-only mode — nothing is written to this account.');
        roErr.readOnly = true;
        return Promise.reject(roErr);
      }
      var auth = KlaroAPI.getStoredAuth();
      if (!auth) {
        var noAuth = new Error('Not connected');
        noAuth.status = 401;
        return Promise.reject(noAuth);
      }
      var headers = {
        'access-token': auth.accessToken,
        client: auth.client,
        uid: auth.uid,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      };
      return fetch(apiBase + path, {
        method: options.method || 'GET',
        headers: headers,
        body: options.body != null ? JSON.stringify(options.body) : undefined,
      }).then(function (res) {
        if (res.status === 401) {
          var err = new Error('Session expired');
          err.status = 401;
          throw err;
        }
        if (!res.ok) {
          var httpErr = new Error('Request failed (' + res.status + ')');
          httpErr.status = res.status;
          throw httpErr;
        }
        if (res.status === 204) return null;
        return res.json().catch(function () { return null; });
      });
    },

    getCollectionUI: function () {
      return KlaroAPI.apiFetch('/api/v3/collection_ui');
    },

    // Same merge-on-save semantics as the app's collectionUIPersistence:
    // re-GET the remote blob, merge (newer id wins), PUT the merged state.
    //
    // One web-specific addition: deletion tombstones. The app's merge
    // treats absence as "nothing to merge", so a deleted entry would be
    // resurrected from the remote copy on every save. The web page
    // reloads remote state constantly, so deletes carry an id tombstone
    // (deletedJournalEntryIds, stored in the same blob — the app
    // ignores the key but preserves it through its own merges).
    saveCollectionUI: function (localState) {
      return KlaroAPI.getCollectionUI()
        .catch(function () { return null; })
        .then(function (remoteState) {
          var nextState =
            remoteState && typeof remoteState === 'object' && Object.keys(remoteState).length > 0
              ? L.mergeCollectionUIState(remoteState, localState)
              : localState;
          var tombstones = mergeTombstones(remoteState, localState);
          if (tombstones.length > 0) {
            nextState.deletedJournalEntryIds = tombstones;
            nextState.journalEntries = (nextState.journalEntries || []).filter(function (e) {
              return !e || tombstones.indexOf(e.id) === -1;
            });
          }
          return KlaroAPI.apiFetch('/api/v3/collection_ui', {
            method: 'PUT',
            body: { collection_ui: nextState },
          }).then(function (saved) {
            var result = saved && typeof saved === 'object' ? saved : nextState;
            // Residual-write guard: entries we hold locally must exist
            // in what the server accepted.
            var savedIds = new Set(
              (result.journalEntries || []).map(function (e) { return e.id; })
            );
            var missing = (localState.journalEntries || []).filter(function (e) {
              return e && e.id && !savedIds.has(e.id);
            });
            if (missing.length > 0) {
              throw new Error('Save verification failed — some entries were not persisted.');
            }
            return result;
          });
        });
    },

    // Route names differ between deployed backend versions: newer builds
    // expose /api/v3/storage_units, production currently serves the
    // /humidors naming. Fall back on 404.
    getStorageUnits: function () {
      return KlaroAPI.apiFetch('/api/v3/storage_units').catch(function (err) {
        if (err.status !== 404) throw err;
        return KlaroAPI.apiFetch('/humidors');
      });
    },

    getUnitCollections: function (unitId) {
      return KlaroAPI.apiFetch('/api/v3/storage_units/' + unitId + '/collections').catch(function (err) {
        if (err.status !== 404) throw err;
        return KlaroAPI.apiFetch('/api/v3/humidors/' + unitId + '/collections').catch(function (err2) {
          if (err2.status !== 404) throw err2;
          return KlaroAPI.apiFetch('/humidors/' + unitId + '/collections');
        });
      });
    },

    updateCollection: function (collectionId, data) {
      return KlaroAPI.apiFetch('/api/v3/collections/' + collectionId, {
        method: 'PATCH',
        body: { collection: data },
      }).catch(function (err) {
        if (err.status !== 404) throw err;
        return KlaroAPI.apiFetch('/collections/' + collectionId, {
          method: 'PATCH',
          body: { collection: data },
        });
      });
    },
  };

  // Union of remote + local deletion tombstones, newest-last, capped so
  // the blob doesn't grow without bound.
  var MAX_TOMBSTONES = 300;
  var mergeTombstones = function (remoteState, localState) {
    var seen = new Set();
    var merged = [];
    ((remoteState && remoteState.deletedJournalEntryIds) || [])
      .concat((localState && localState.deletedJournalEntryIds) || [])
      .forEach(function (id) {
        if (!id || seen.has(id)) return;
        seen.add(id);
        merged.push(id);
      });
    return merged.slice(-MAX_TOMBSTONES);
  };

  // ── State ──────────────────────────────────────────────────────────────

  var state = {
    view: 'boot', // boot | shopify-login | pending | error | loading | journal
    errorMessage: '',
    collectionUI: null,
    inventoryRecords: [],
    storageUnits: [],
    timeRange: 'all',
    search: '',
    searchOpen: false,
    sort: Object.assign({}, L.DEFAULT_JOURNAL_SORT),
    filters: Object.assign({}, L.DEFAULT_JOURNAL_FILTERS),
    viewMode: L.DEFAULT_JOURNAL_VIEW_MODE,
    openSheet: null, // 'sort' | 'filter' | 'entry' | 'detail' | 'stick' | 'orders'
    editingEntry: null,
    editingStick: null, // group whose cigar details are being edited
    stickPrefill: null, // seeds the Add sheet (e.g. from a wishlist row)
    detailGroup: null,
    saving: false,
    // Preset views, mirroring the app's system tabs (In Box | Journal):
    // 'journal' = everything ever smoked, 'onhand' = currently in stock,
    // 'ordered' = purchased on klarocigars.com, 'wishlist' = the store
    // wishlist (Swym) — cigars they want but haven't logged yet.
    sourceTab: 'journal',
    autoImportedCount: 0,
    // Store wishlist, read via the Swym SDK the storefront already runs.
    wishlist: { status: 'idle', items: [] }, // idle|loading|ready|unavailable
    swymItems: [], // normalized Swym list items (merged with fallback adds)
    walkthroughSeenV: 0, // last intro-tour version dismissed on this device
  };

  var loadPrefs = function () {
    try {
      var raw = localStorage.getItem(PREFS_STORAGE_KEY);
      if (!raw) return;
      var prefs = JSON.parse(raw);
      // The walkthrough flag is per-device; everything else is layout state
      // that belongs to whoever saved it — a different customer on a shared
      // browser starts from defaults instead of someone else's stale tab.
      if (typeof prefs.walkthroughSeenV === 'number') state.walkthroughSeenV = prefs.walkthroughSeenV;
      else if (prefs.walkthroughSeen === true) state.walkthroughSeenV = 1; // pre-versioning flag
      var configCustomerId = CONFIG.customerId != null ? String(CONFIG.customerId) : null;
      if (prefs.customerId && configCustomerId && prefs.customerId !== configCustomerId) {
        savePrefs(); // stamp defaults for this customer (keeps the walkthrough flag)
        return;
      }
      if (prefs.viewMode && ['compact', 'comfortable', 'grid'].indexOf(prefs.viewMode) !== -1) {
        state.viewMode = prefs.viewMode;
      }
      if (prefs.sort && prefs.sort.key) state.sort = prefs.sort;
      if (prefs.timeRange) state.timeRange = prefs.timeRange;
      if (['journal', 'onhand', 'favorites', 'ordered', 'wishlist'].indexOf(prefs.sourceTab) !== -1) {
        state.sourceTab = prefs.sourceTab;
      }
    } catch (e) {}
  };

  var savePrefs = function () {
    try {
      localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify({
        customerId: CONFIG.customerId != null ? String(CONFIG.customerId) : null,
        viewMode: state.viewMode,
        sort: state.sort,
        timeRange: state.timeRange,
        sourceTab: state.sourceTab,
        walkthroughSeenV: state.walkthroughSeenV,
      }));
    } catch (e) {}
  };

  // Debounced remote save (500ms quiet period, like the app).
  var saveTimer = null;
  var queueSave = function () {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(flushSave, 500);
  };
  var flushSave = function () {
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
    if (READ_ONLY || !state.collectionUI) return Promise.resolve();
    state.saving = true;
    return KlaroAPI.saveCollectionUI(state.collectionUI)
      .then(function (saved) {
        // Merge, don't replace: edits made while the save was in flight
        // (quick favorite, stepper taps) must survive the response landing.
        var merged = L.mergeCollectionUIState(saved, state.collectionUI);
        var tombstones = merged.deletedJournalEntryIds || [];
        if (tombstones.length > 0) {
          merged.journalEntries = (merged.journalEntries || []).filter(function (e) {
            return !e || tombstones.indexOf(e.id) === -1;
          });
        }
        state.collectionUI = merged;
        state.saving = false;
        renderList();
      })
      .catch(function (err) {
        state.saving = false;
        if (err.status === 401) return handleUnauthorized();
        showToast(err.message || 'Could not save to your Klaro account.', true);
      });
  };

  // On 401: clear tokens and re-run the silent handshake once — the journal
  // token is durable, so a fresh exchange normally recovers the session
  // without the user noticing. If the handshake itself fails, show the
  // error card (no loop).
  var rehandshakeAttempted = false;
  var handleUnauthorized = function () {
    KlaroAPI.clearAuth();
    if (!rehandshakeAttempted && CONFIG.journalToken) {
      rehandshakeAttempted = true;
      KlaroAPI.handshake()
        .then(function () { loadData(); })
        .catch(function (err) { showErrorView(err); });
      return;
    }
    showErrorView(new Error('Your session expired. Refresh the page to reconnect.'));
  };

  var showErrorView = function (err) {
    state.view = 'error';
    state.errorMessage = (err && err.message) || 'Something went wrong.';
    render();
  };

  // ── DOM helpers / icons ────────────────────────────────────────────────

  var root = null;

  var esc = function (value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  var svg = function (path, size, viewBox) {
    return (
      '<svg width="' + (size || 16) + '" height="' + (size || 16) + '" viewBox="' +
      (viewBox || '0 0 24 24') + '" fill="currentColor" aria-hidden="true"><path d="' +
      path + '"/></svg>'
    );
  };

  var ICONS = {
    star: 'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z',
    filter: 'M4 5h16v2l-6 6v6l-4-2v-4L4 7z',
    sort: 'M8 5l-4 4h3v10h2V9h3L8 5zm8 14l4-4h-3V5h-2v10h-3l4 4z',
    search: 'M15.5 14h-.79l-.28-.27a6.5 6.5 0 1 0-.7.7l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0A4.5 4.5 0 1 1 14 9.5 4.5 4.5 0 0 1 9.5 14z',
    viewCompact: 'M3 5h18v3H3zM3 10.5h18v3H3zM3 16h18v3H3z',
    viewComfortable: 'M3 4h18v7H3zM3 13h18v7H3z',
    viewGrid: 'M3 3h8v8H3zm10 0h8v8h-8zM3 13h8v8H3zm10 0h8v8h-8z',
    marker: 'M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 14.5 9 2.5 2.5 0 0 1 12 11.5z',
    archive: 'M3 4h18v4H3zm2 5h14v11H5zm4 3v2h6v-2z',
    archiveCheck: 'M3 4h18v4H3zm2 5h14v11H5zm3.8 5.2l2 2 4.4-4.4-1.2-1.2-3.2 3.2-.8-.8z',
    close: 'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z',
    check: 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z',
    plus: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z',
    book: 'M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm-6 2.5L14.5 3v7l-2.5-1.5L9.5 10V3z',
    edit: 'M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z',
    photo: 'M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z',
    cigar: 'M4 9.5h11.5a2.5 2.5 0 0 1 0 5H4A2.5 2.5 0 0 1 4 9.5zm14.75.75a1.75 1.75 0 1 1 0 3.5 1.75 1.75 0 0 1 0-3.5zM20 5.5c.83.83.83 2.17 0 3l-.7-.7c.44-.45.44-1.16 0-1.6zm2 0c1.66 1.66 1.66 3.34 0 5l-.7-.7c1.27-1.27 1.27-2.33 0-3.6z',
    bookmark: 'M17 3H7a2 2 0 0 0-2 2v16l7-3 7 3V5a2 2 0 0 0-2-2z',
    sync: 'M12 4V1L8 5l4 4V6a6 6 0 0 1 6 6c0 1-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0 0 20 12a8 8 0 0 0-8-8zm0 14a6 6 0 0 1-6-6c0-1 .25-1.97.7-2.8L5.24 7.74A7.93 7.93 0 0 0 4 12a8 8 0 0 0 8 8v3l4-4-4-4v3z',
    cart: 'M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.49 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z',
  };

  var starsHtml = function (rating, size) {
    size = size || 12;
    var html = '<span class="j-stars">';
    for (var i = 1; i <= 5; i++) {
      html +=
        '<span style="color:' + (i <= rating ? 'var(--j-accent)' : 'var(--j-star-off)') + '">' +
        svg(ICONS.star, size) + '</span>';
    }
    return html + '</span>';
  };

  // Site chrome — bridges back to klarocigars.com from every view.
  // The drawer mirrors the store's live main menu (CONFIG.menu is
  // rendered server-side from linklists['main-menu']).
  var LOGO_URL = 'https://klarocigars.com/cdn/shop/files/Logo_3x_bd451afb-8bcb-43d4-87dd-39919f903444_170x@2x.png';
  // "KLARO / JOURNAL" lockup (Ian's supplied wordmark) — white letters,
  // orange slash. Inlined so it stays crisp on the dark journal chrome and
  // needs no extra request; fills are baked in (journal is always dark).
  var JOURNAL_WORDMARK = '<svg class="j-wordmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 194.92 19.46" role="img" aria-label="Klaro Journal">' +
    '<path fill="#fff" d="M80.79,17.01c-4.34,0-7.46-3.24-7.46-7.24v-.04c0-4,3.16-7.28,7.5-7.28s7.46,3.24,7.46,7.24v.04c0,4-3.16,7.28-7.5,7.28M85.06,9.73c0-2.41-1.77-4.42-4.26-4.42s-4.22,1.97-4.22,4.38v.04c0,2.41,1.77,4.42,4.26,4.42s4.22-1.97,4.22-4.38v-.04Z"/>' +
    '<polygon fill="#fff" points="27.92 13.95 22.12 13.95 22.12 3.45 22.44 2.69 22.12 2.69 21.65 2.69 19.48 2.69 19.02 2.69 18.7 2.69 19.02 3.45 19.02 16.02 18.7 16.77 19.02 16.77 19.49 16.77 29.12 16.77 27.92 13.95"/>' +
    '<path fill="#fff" d="M43.53,3.5l.36-.85h-4.31l.36.85-5.67,13.32h3.78l-.32-.74.98-2.41h5.95l.98,2.4-.32.75h3.86l-5.67-13.32ZM39.83,10.93l1.87-4.56,1.87,4.56h-3.74Z"/>' +
    '<path fill="#fff" d="M64.12,11.74c1.79-.66,3.02-2.09,3.02-4.36v-.04c0-2.89-1.99-4.64-5.31-4.64h-6.75l.32.75v12.57l-.32.76h3.74l-.32-.75v-3.76h2.43l2.52,3.76-.32.75h4.44l-3.44-5.03ZM61.63,9.53h-3.14v-4.04h3.08c1.51,0,2.43.68,2.43,2.01v.04c0,1.19-.87,1.99-2.37,1.99"/>' +
    '<polygon fill="#fff" points="7.13 8.67 12.86 2.69 9.34 2.69 9.13 2.69 9.03 2.69 8.85 2.69 8.25 2.69 8.52 3.34 3.42 8.85 3.42 3.45 3.74 2.69 3.42 2.69 2.96 2.69 .78 2.69 .32 2.69 0 2.69 .32 3.44 .32 16.02 0 16.77 .32 16.77 .78 16.77 2.96 16.77 3.42 16.77 3.74 16.77 3.42 16.01 3.42 12.47 5.04 10.78 8.87 16.03 8.56 16.77 9.21 16.77 9.34 16.77 9.41 16.77 9.92 16.77 13.13 16.77 7.13 8.67"/>' +
    '<path fill="#fff" d="M113.54,12.75c.43.51,1,.97,1.85.97,1.21,0,1.97-.84,1.97-2.06v-6.84h1.22v6.85c0,2.13-1.37,3.13-3.12,3.13-1.04,0-1.88-.34-2.56-1.13l.63-.93Z"/>' +
    '<path fill="#fff" d="M128.29,4.66c2.9,0,4.87,2.18,4.87,5.07s-1.97,5.08-4.87,5.08-4.87-2.18-4.87-5.08,1.96-5.07,4.87-5.07ZM128.29,5.74c-2.21,0-3.6,1.69-3.6,3.99s1.4,3.99,3.6,3.99,3.6-1.71,3.6-3.99-1.41-3.99-3.6-3.99Z"/>' +
    '<path fill="#fff" d="M138.02,4.82h1.22v5.97c0,1.79.97,2.93,2.78,2.93s2.78-1.13,2.78-2.93v-5.97h1.22v5.99c0,2.44-1.34,4-4,4s-4-1.57-4-3.99v-6Z"/>' +
    '<path fill="#fff" d="M156.94,14.63l-2.5-3.9h-1.96v3.9h-1.22V4.82h3.94c1.79,0,3.07,1.15,3.07,2.96s-1.21,2.74-2.54,2.85l2.65,4h-1.44ZM155.06,5.91h-2.57v3.75h2.57c1.16,0,1.96-.78,1.96-1.88s-.79-1.87-1.96-1.87Z"/>' +
    '<path fill="#fff" d="M170.1,14.63l-5.72-7.91v7.91h-1.22V4.82h1.25l5.65,7.75v-7.75h1.22v9.81h-1.18Z"/>' +
    '<path fill="#fff" d="M183.56,14.63l-.87-2.18h-4.97l-.87,2.18h-1.35l3.94-9.81h1.52l3.96,9.81h-1.35ZM180.21,5.91l-2.13,5.46h4.25l-2.12-5.46Z"/>' +
    '<path fill="#fff" d="M189.14,14.63V4.82h1.22v8.72h4.56v1.09h-5.78Z"/>' +
    '<path fill="#ff7500" d="M97.32,19.46L103.84,0h1.24l-6.52,19.46h-1.24Z"/>' +
    '</svg>';
  var siteBarHtml = function () {
    // Same icon set as the main site header (stroke style and all), minus
    // the light/dark toggle — the journal is always dark.
    var strokeIcon = function (inner) {
      return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + inner + '</svg>';
    };
    return (
      '<div class="j-site-bar">' +
        '<div class="j-site-bar-inner">' +
          '<button class="j-menu-btn" id="j-menu-btn" aria-label="Menu">' +
            svg('M3 6h18v2H3zm0 5h18v2H3zm0 5h18v2H3z', 20) +
          '</button>' +
          '<a class="j-logo-link j-logo-link--wordmark" href="/" aria-label="Klaro Journal">' + JOURNAL_WORDMARK + '</a>' +
          '<div class="j-site-icons">' +
            '<button class="j-site-icon" id="j-site-wishlist" type="button" aria-label="Wishlist">' +
              strokeIcon('<path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>') + '</button>' +
            '<a class="j-site-icon active" href="/pages/journal" aria-label="Cigar Journal">' +
              strokeIcon('<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>') + '</a>' +
            '<a class="j-site-icon" href="/account" aria-label="Account">' +
              strokeIcon('<circle cx="12" cy="8" r="4"/><path d="M4 21v-1a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v1"/>') + '</a>' +
            '<a class="j-site-icon" href="/cart" aria-label="Cart">' +
              strokeIcon('<path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>') + '</a>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  };

  // Escape closes the topmost overlay (drawer first, then any sheet) —
  // same effect as its X button.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (document.getElementById('j-drawer-wrap')) return closeDrawer();
    if (document.getElementById('j-walk-wrap')) return closeWalkthrough();
    if (state.openSheet) closeSheet();
  });

  var closeDrawer = function () {
    var wrap = document.getElementById('j-drawer-wrap');
    if (!wrap) return;
    wrap.querySelector('.j-drawer-scrim').classList.remove('open');
    wrap.querySelector('.j-drawer').classList.remove('open');
    setTimeout(function () { wrap.remove(); }, 260);
  };

  var openDrawer = function () {
    if (document.getElementById('j-drawer-wrap')) return;
    var menu = Array.isArray(CONFIG.menu) ? CONFIG.menu : [];

    var itemsHtml = menu.map(function (item, i) {
      var children = Array.isArray(item.children) ? item.children : [];
      if (children.length === 0) {
        return (
          '<a class="j-drawer-item" href="' + esc(item.url || '#') + '">' + esc(item.title) + '</a>'
        );
      }
      return (
        '<div class="j-drawer-group" data-group="' + i + '">' +
          '<button class="j-drawer-item j-drawer-toggle" data-toggle-group="' + i + '">' +
            '<span>' + esc(item.title) + '</span>' +
            '<span class="j-drawer-caret">' + svg('M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z', 18) + '</span>' +
          '</button>' +
          '<div class="j-drawer-children">' +
            (item.url && item.url !== '#'
              ? '<a class="j-drawer-child" href="' + esc(item.url) + '">All ' + esc(item.title) + '</a>'
              : '') +
            children.map(function (child) {
              return '<a class="j-drawer-child" href="' + esc(child.url || '#') + '">' + esc(child.title) + '</a>';
            }).join('') +
          '</div>' +
        '</div>'
      );
    }).join('');

    // Top utility row mirrors the store drawer's (mobile): Home back to
    // the storefront + Wishlist tab jump. No theme toggle here — the
    // journal is always dark; the toggle lives in the store menu.
    var strokeUtilIcon = function (inner) {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + inner + '</svg>';
    };
    var utilityHtml =
      '<div class="j-drawer-utility">' +
        '<a class="j-drawer-utility-btn" href="/">' +
          strokeUtilIcon('<path d="M3 10.5L12 3l9 7.5"/><path d="M5 9.5V21h14V9.5"/><path d="M9.5 21v-6h5v6"/>') +
          'Home</a>' +
        '<button class="j-drawer-utility-btn" id="j-drawer-wishlist">' +
          strokeUtilIcon('<path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>') +
          'Wishlist</button>' +
      '</div>';

    var wrap = document.createElement('div');
    wrap.id = 'j-drawer-wrap';
    wrap.innerHTML =
      '<div class="j-drawer-scrim"></div>' +
      '<nav class="j-drawer">' +
        '<div class="j-drawer-head">' +
          '<img class="j-logo" src="' + LOGO_URL + '" alt="Klaro Cigars">' +
          '<button class="j-drawer-close" aria-label="Close menu">' + svg(ICONS.close, 20) + '</button>' +
        '</div>' +
        utilityHtml +
        '<div class="j-drawer-body">' + itemsHtml + '</div>' +
        '<div class="j-drawer-foot">' +
          '<a class="j-drawer-item" href="/account">Account</a>' +
          '<a class="j-drawer-item j-drawer-current" href="/pages/journal">Cigar Journal</a>' +
          '<button class="j-drawer-item" id="j-drawer-howto">How the journal works</button>' +
          (KlaroAPI.getStoredAuth() && state.view === 'journal' && !READ_ONLY
            ? '<button class="j-drawer-item" id="j-drawer-merge">Link another account</button>'
            : '') +
        '</div>' +
      '</nav>';
    document.body.appendChild(wrap);

    var scrim = wrap.querySelector('.j-drawer-scrim');
    var drawer = wrap.querySelector('.j-drawer');
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        scrim.classList.add('open');
        drawer.classList.add('open');
      });
    });
    scrim.addEventListener('click', closeDrawer);
    wrap.querySelector('.j-drawer-close').addEventListener('click', closeDrawer);
    var mergeBtn = wrap.querySelector('#j-drawer-merge');
    if (mergeBtn) {
      mergeBtn.addEventListener('click', function () {
        closeDrawer();
        openSheet('merge');
      });
    }
    var howtoBtn = wrap.querySelector('#j-drawer-howto');
    if (howtoBtn) {
      howtoBtn.addEventListener('click', function () {
        closeDrawer();
        openWalkthrough();
      });
    }
    var wishBtn = wrap.querySelector('#j-drawer-wishlist');
    if (wishBtn) {
      wishBtn.addEventListener('click', function () {
        // Same semantics as the site bar's bookmark: jump to the
        // Wishlist tab, inert before the journal has rendered.
        closeDrawer();
        if (state.view !== 'journal' || !state.collectionUI) return;
        tabTouched = true;
        state.sourceTab = 'wishlist';
        savePrefs();
        renderJournal();
      });
    }
    drawer.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-toggle-group]');
      if (!toggle) return;
      wrap.querySelector('[data-group="' + toggle.getAttribute('data-toggle-group') + '"]')
        .classList.toggle('expanded');
    });
  };

  var strengthClass = function (strength) {
    switch (String(strength || '').toLowerCase()) {
      case 'mild': return 'j-strength-mild';
      case 'mild-medium': return 'j-strength-mild-medium';
      case 'medium': return 'j-strength-medium';
      case 'medium-full': return 'j-strength-medium-full';
      case 'full': return 'j-strength-full';
      default: return '';
    }
  };

  // Display labels for strength chips match the app (values stay full).
  var shortStrengthLabel = function (value) {
    if (value === 'Mild-Medium') return 'Mild-Med';
    if (value === 'Medium-Full') return 'Med-Full';
    return value;
  };

  var toastTimer = null;
  var showToast = function (message, isError) {
    var toast = document.getElementById('j-toast');
    if (!toast) return;
    toast.textContent = message;
    toast.className = 'j-toast visible' + (isError ? ' error' : '');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toast.className = 'j-toast'; }, 3200);
  };

  // ── Boot / data loading ────────────────────────────────────────────────

  var PENDING_RETRY_KEY = 'klaro:webJournal:pendingRetries';
  var MAX_PENDING_RETRIES = 5;
  var FRESH_RELOAD_KEY = 'klaro:webJournal:freshReload';

  var boot = function () {
    root = document.getElementById('klaro-journal-root');
    if (!root || !L) return;
    loadPrefs();

    // Not signed into the store → straight to the store login (which
    // returns here). The shopify-login card stays as a fallback in case
    // the redirect is blocked.
    if (!CONFIG.customerEmail) {
      // A logged-out CONFIG can also be a stale cached render of this page
      // (Safari serves it from cache since the response has no
      // Cache-Control) — for a customer who IS logged in, bouncing to
      // /account/login just loops back here via /account. Burn one
      // cache-busting reload to force a fresh Liquid render before
      // trusting the logged-out state.
      // The ?fresh param itself is the primary attempt marker — a browser
      // with storage blocked must still never reload-loop.
      var freshAttempted = /[?&]fresh=/.test(location.search);
      try { freshAttempted = freshAttempted || sessionStorage.getItem(FRESH_RELOAD_KEY) === '1'; } catch (e) {}
      if (!freshAttempted) {
        try { sessionStorage.setItem(FRESH_RELOAD_KEY, '1'); } catch (e) {}
        location.replace('/pages/journal?fresh=' + Date.now());
        return;
      }
      // Straight to the store login (return_url brings them back) — no
      // intermediate card. The card renders only if navigation stalls.
      location.replace(CONFIG.loginUrl || '/account/login?return_url=/pages/journal');
      setTimeout(function () {
        state.view = 'shopify-login';
        render();
      }, 1200);
      return;
    }
    try {
      sessionStorage.removeItem(FRESH_RELOAD_KEY);
      // Drop the cache-buster from the address bar (only when it is the
      // sole param — never touch ?jt/?api/?ro overrides).
      if (/^\?fresh=\d+$/.test(location.search)) {
        history.replaceState(null, '', location.pathname);
      }
    } catch (e) {}

    // Session follows the store login: tokens cached for a different
    // customer (shared browser, switched accounts) are discarded.
    var auth = KlaroAPI.getStoredAuth();
    var configCustomerId = CONFIG.customerId != null ? String(CONFIG.customerId) : null;
    if (auth && configCustomerId && auth.customerId && auth.customerId !== configCustomerId) {
      KlaroAPI.clearAuth();
      auth = null;
    }
    if (auth) {
      try { sessionStorage.removeItem(PENDING_RETRY_KEY); } catch (e) {}
      loadData();
      return;
    }

    if (!CONFIG.journalToken) {
      // Logged in but no metafield token yet — brand-new customer whose
      // token is seconds away (webhook), or an account predating the
      // backfill. Auto-reload a few times, then leave a manual retry.
      var retries = 0;
      try { retries = Number(sessionStorage.getItem(PENDING_RETRY_KEY) || 0); } catch (e) {}
      state.view = 'pending';
      state.pendingExhausted = retries >= MAX_PENDING_RETRIES;
      render();
      if (!state.pendingExhausted) {
        try { sessionStorage.setItem(PENDING_RETRY_KEY, String(retries + 1)); } catch (e) {}
        setTimeout(function () { location.reload(); }, 4000);
      }
      return;
    }

    try { sessionStorage.removeItem(PENDING_RETRY_KEY); } catch (e) {}
    state.view = 'loading';
    render();
    KlaroAPI.handshake()
      .then(function () { loadData(); })
      .catch(function (err) { showErrorView(err); });
  };

  var loadData = function () {
    state.view = 'loading';
    render();

    var collectionUIPromise = KlaroAPI.getCollectionUI();
    var inventoryPromise = KlaroAPI.getStorageUnits().then(function (units) {
      units = Array.isArray(units)
        ? units
        : (units && (units.storage_units || units.humidors)) || [];
      state.storageUnits = units;
      return Promise.all(
        units.map(function (unit) {
          return KlaroAPI.getUnitCollections(unit.id).then(function (items) {
            items = Array.isArray(items) ? items : (items && items.collections) || [];
            return items.map(function (item) {
              return {
                item: item,
                storageUnit: unit,
                storageUnitId: unit.id,
                boxName: unit.name || '',
              };
            });
          }).catch(function () { return []; });
        })
      ).then(function (lists) {
        return lists.reduce(function (acc, list) { return acc.concat(list); }, []);
      });
    });

    Promise.all([collectionUIPromise, inventoryPromise])
      .then(function (results) {
        var blob = results[0];
        state.collectionUI =
          blob && typeof blob === 'object'
            ? blob
            : { journalEntries: [], tagLibrary: [], recentSearches: [], savedViews: [], perStorageUnit: {} };
        if (!Array.isArray(state.collectionUI.journalEntries)) state.collectionUI.journalEntries = [];
        if (!Array.isArray(state.collectionUI.tagLibrary)) state.collectionUI.tagLibrary = [];
        var tombstones = state.collectionUI.deletedJournalEntryIds || [];
        if (tombstones.length > 0) {
          state.collectionUI.journalEntries = state.collectionUI.journalEntries.filter(function (e) {
            return !e || tombstones.indexOf(e.id) === -1;
          });
        }
        state.inventoryRecords = results[1];
        autoImportOrders();
        // A restored wishlist tab is judged after the wishlist loads (it has
        // its own loading state); every other tab is judged right now.
        if (state.sourceTab !== 'wishlist') validateInitialTab();
        state.view = 'journal';
        render();
        resolveUploadPhotoUrls();
        loadWishlist();
      })
      .catch(function (err) {
        if (err.status === 401) return handleUnauthorized();
        if (err.status === 404) {
          KlaroAPI.clearAuth();
          showErrorView(new Error('This server does not support journal sync yet.'));
          return;
        }
        showErrorView(new Error('Could not load your journal (' + (err.message || 'network error') + ').'));
      });
  };

  // ── Intro walkthrough ──────────────────────────────────────────────────
  // First visit on a device gets a short "how it works" tour; replay any
  // time via "How the journal works" in the menu drawer. Seen-state lives
  // in local prefs (not the synced blob) so each device shows it once.

  // Bump when the tour changes enough that everyone should see it once
  // more (devices that dismissed an older version re-show automatically).
  var WALK_VERSION = 2;

  // Each slide can spotlight the control it describes (first visible
  // selector wins) — the element is lifted above the scrim with a glow.
  var WALK_SLIDES = [
    {
      icon: 'book',
      title: 'Welcome to your Cigar Journal',
      copy: 'Every cigar you smoke, rate, and remember — in one place. It’s the same journal as the Klaro app, tied to your klarocigars.com account.',
    },
    {
      icon: 'star',
      title: 'Log every smoke',
      copy: 'Tap Add Cigar to rate a stick, jot tasting notes, and tag the moment. Log the same cigar again any time — its history builds beneath it.',
      target: ['#j-add-top', '#j-fab'],
    },
    {
      icon: 'archiveCheck',
      title: 'Purchases import automatically',
      copy: 'Anything you order on klarocigars.com lands in your journal on its own, details pre-filled. The Purchases tab keeps your order history a tap away.',
      target: ['[data-stat-tab="ordered"]'],
    },
    {
      icon: 'filter',
      title: 'Organize and find',
      copy: 'Flip between On Hand, Journal, Favorites, Purchases, and Wishlist. Filter by strength or tags, sort your way, and pick a view density.',
      target: ['.j-toolbar'],
    },
    {
      icon: 'sync',
      title: 'Synced with the Klaro app',
      copy: 'Entries made here show up in the Klaro app and vice versa — same account, same journal. Humidor inventory counts toward On Hand automatically.',
      target: ['[data-stat-tab="onhand"]'],
    },
  ];

  // Lift the described control above the walkthrough scrim (or clear the
  // current one when called with nothing).
  var walkSpotlight = function (selectors) {
    var prev = document.querySelector('.j-walk-spotlight');
    if (prev) prev.classList.remove('j-walk-spotlight');
    if (!selectors) return;
    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (el && el.getBoundingClientRect().width > 0) {
        el.classList.add('j-walk-spotlight');
        try { el.scrollIntoView({ block: 'nearest' }); } catch (e) {}
        return;
      }
    }
  };

  var walkthroughAutoShown = false;

  var closeWalkthrough = function () {
    if (state.walkthroughSeenV < WALK_VERSION) {
      state.walkthroughSeenV = WALK_VERSION;
      savePrefs();
    }
    walkSpotlight(null);
    var wrap = document.getElementById('j-walk-wrap');
    if (!wrap) return;
    wrap.querySelector('.j-walk-scrim').classList.remove('open');
    wrap.querySelector('.j-walk').classList.remove('open');
    setTimeout(function () { wrap.remove(); }, 260);
  };

  var openWalkthrough = function () {
    if (document.getElementById('j-walk-wrap')) return;
    var at = 0;

    var wrap = document.createElement('div');
    wrap.id = 'j-walk-wrap';
    wrap.innerHTML =
      '<div class="j-walk-scrim"></div>' +
      '<div class="j-walk" role="dialog" aria-modal="true" aria-label="How the journal works">' +
        '<button class="j-walk-close" aria-label="Close">' + svg(ICONS.close, 18) + '</button>' +
        '<div class="j-walk-body">' +
          '<div class="j-walk-art" id="j-walk-art"></div>' +
          '<h3 class="j-walk-title" id="j-walk-title"></h3>' +
          '<p class="j-walk-copy" id="j-walk-copy"></p>' +
        '</div>' +
        '<div class="j-walk-dots" id="j-walk-dots"></div>' +
        '<div class="j-walk-nav">' +
          '<button class="j-btn j-btn-secondary" id="j-walk-back">Back</button>' +
          '<button class="j-btn j-btn-primary" id="j-walk-next">Next</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(wrap);

    var card = wrap.querySelector('.j-walk');
    var body = wrap.querySelector('.j-walk-body');
    var art = wrap.querySelector('#j-walk-art');
    var title = wrap.querySelector('#j-walk-title');
    var copy = wrap.querySelector('#j-walk-copy');
    var dots = wrap.querySelector('#j-walk-dots');
    var back = wrap.querySelector('#j-walk-back');
    var next = wrap.querySelector('#j-walk-next');

    var show = function (index, dir) {
      var to = Math.max(0, Math.min(WALK_SLIDES.length - 1, index));
      if (to === at && art.innerHTML) return;
      at = to;
      var slide = WALK_SLIDES[at];
      var last = at === WALK_SLIDES.length - 1;
      art.innerHTML = svg(ICONS[slide.icon], 30);
      title.textContent = slide.title;
      copy.textContent = slide.copy;
      dots.innerHTML = WALK_SLIDES.map(function (_, i) {
        return '<button class="j-walk-dot' + (i === at ? ' active' : '') +
          '" data-walk-dot="' + i + '" aria-label="Step ' + (i + 1) + '"></button>';
      }).join('');
      back.textContent = at === 0 ? 'Skip' : 'Back';
      next.textContent = last ? 'Start journaling' : 'Next';
      walkSpotlight(slide.target);
      // Re-trigger the slide-in animation on the swapped content.
      body.classList.remove('j-walk-in-left', 'j-walk-in-right');
      void body.offsetWidth;
      body.classList.add(dir < 0 ? 'j-walk-in-left' : 'j-walk-in-right');
    };

    wrap.querySelector('.j-walk-scrim').addEventListener('click', closeWalkthrough);
    wrap.querySelector('.j-walk-close').addEventListener('click', closeWalkthrough);
    back.addEventListener('click', function () {
      if (at === 0) return closeWalkthrough();
      show(at - 1, -1);
    });
    next.addEventListener('click', function () {
      if (at === WALK_SLIDES.length - 1) return closeWalkthrough();
      show(at + 1, 1);
    });
    dots.addEventListener('click', function (e) {
      var dot = e.target.closest('[data-walk-dot]');
      if (!dot) return;
      var to = Number(dot.getAttribute('data-walk-dot'));
      show(to, to < at ? -1 : 1);
    });

    // Arrow keys page through while the tour is up; listener retires with it.
    var onKey = function (e) {
      if (!document.getElementById('j-walk-wrap')) {
        return document.removeEventListener('keydown', onKey);
      }
      if (e.key === 'ArrowRight') show(at + 1, 1);
      else if (e.key === 'ArrowLeft') show(at - 1, -1);
    };
    document.addEventListener('keydown', onKey);

    // Swipe between slides on touch.
    var touchX = null;
    card.addEventListener('touchstart', function (e) {
      touchX = e.touches[0].clientX;
    }, { passive: true });
    card.addEventListener('touchend', function (e) {
      if (touchX == null) return;
      var dx = e.changedTouches[0].clientX - touchX;
      touchX = null;
      if (Math.abs(dx) < 48) return;
      show(at + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
    }, { passive: true });

    show(0, 1);
    var scrim = wrap.querySelector('.j-walk-scrim');
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        scrim.classList.add('open');
        card.classList.add('open');
      });
    });
  };

  // ── Renderers ──────────────────────────────────────────────────────────

  var render = function () {
    if (!root) return;
    switch (state.view) {
      case 'shopify-login': return renderShopifyLogin();
      case 'pending': return renderPending();
      case 'error': return renderError();
      case 'loading': return renderLoading();
      case 'journal': return renderJournal();
      default: return renderLoading();
    }
  };

  var renderShopifyLogin = function () {
    root.innerHTML =
      siteBarHtml() +
      '<div class="j-auth-wrap">' +
        '<div class="j-auth-card">' +
          '<h2>Your Cigar Journal</h2>' +
          '<p>Taking you to sign in…</p>' +
          '<a class="j-btn j-btn-primary" href="' + esc(CONFIG.loginUrl || '/account/login') + '" style="text-decoration:none">Sign in</a>' +
          '<p class="j-auth-hint">New here? <a href="' + esc(CONFIG.registerUrl || '/account/register') + '">Create an account</a> — your journal comes with it.</p>' +
        '</div>' +
      '</div>';
  };

  var renderPending = function () {
    root.innerHTML =
      siteBarHtml() +
      '<div class="j-auth-wrap">' +
        '<div class="j-auth-card">' +
          '<h2>Setting up your journal</h2>' +
          (state.pendingExhausted
            ? '<p>Your journal is still being prepared. This usually takes under a minute for new accounts.</p>' +
              '<button class="j-btn j-btn-primary" id="j-pending-retry">Try again</button>'
            : '<div class="j-loading"><div class="j-spinner"></div>One moment — preparing your journal…</div>') +
        '</div>' +
      '</div>';
    var retry = document.getElementById('j-pending-retry');
    if (retry) {
      retry.addEventListener('click', function () {
        try { sessionStorage.removeItem(PENDING_RETRY_KEY); } catch (e) {}
        location.reload();
      });
    }
  };

  var renderError = function () {
    root.innerHTML =
      siteBarHtml() +
      '<div class="j-auth-wrap">' +
        '<div class="j-auth-card">' +
          '<h2>Journal unavailable</h2>' +
          '<p class="j-auth-error visible">' + esc(state.errorMessage) + '</p>' +
          '<button class="j-btn j-btn-primary" id="j-error-retry">Try again</button>' +
        '</div>' +
      '</div>';
    document.getElementById('j-error-retry').addEventListener('click', function () {
      location.reload();
    });
  };

  var renderLoading = function () {
    root.innerHTML =
      siteBarHtml() +
      '<div class="j-loading"><div class="j-spinner"></div>Loading journal...</div>' +
      '<div class="j-skeleton"></div><div class="j-skeleton"></div><div class="j-skeleton"></div>';
  };

  // Current stock for a group across every source (app boxes + web seed).
  var groupOnHandCount = function (group) {
    var total = 0;
    (group.items || []).forEach(function (boxItem) {
      var record = state.inventoryRecords.find(function (r) {
        return r.item && boxItem && String(r.item.id) === String(boxItem.id);
      });
      if (record) total += Math.max(0, parseInt(record.item.quantity, 10) || 0);
    });
    var seed = findStickSeedEntry(group);
    if (seed) total += Math.max(0, parseInt(seed.snapshot && seed.snapshot.quantity, 10) || 0);
    return total;
  };

  // Favorites: journal-side cigars carry favorite on their web snapshot
  // (same field the stick form writes); inventory-only groups get a
  // web-side overlay list in collection_ui (webFavoriteKeys) since their
  // app profile isn't writable from here.
  var webFavoriteKeys = function () {
    if (!Array.isArray(state.collectionUI.webFavoriteKeys)) {
      state.collectionUI.webFavoriteKeys = [];
    }
    return state.collectionUI.webFavoriteKeys;
  };

  var isFavoriteGroup = function (group) {
    if (!group) return false;
    if (group.favorite === true) return true;
    return webFavoriteKeys().indexOf(
      L.buildJournalSnapshotSignature(group.snapshot || {})
    ) !== -1;
  };

  var toggleGroupFavorite = function (group) {
    var next = !isFavoriteGroup(group);
    var host = findStickSeedEntry(group) ||
      (group.itemId == null
        ? (L.entriesForJournalGroup(state.collectionUI.journalEntries, group)[0] || null)
        : null);
    if (host) {
      host.snapshot = Object.assign({}, host.snapshot, { favorite: next });
      host.updatedAt = Date.now();
    }
    var keys = webFavoriteKeys();
    var key = L.buildJournalSnapshotSignature(group.snapshot || {});
    var at = keys.indexOf(key);
    if (next && at === -1 && !host) keys.push(key);
    if (!next && at !== -1) keys.splice(at, 1);
    group.favorite = next;
    // App-side sync: inventory-backed cigars carry favorite in the item
    // profile — write it through so the app's Favorites tab agrees. Full
    // profile is re-sent because the server normalizes partial payloads
    // to empty defaults (a favorite-only write would blank the profile).
    (group.items || []).forEach(function (boxItem) {
      var record = state.inventoryRecords.find(function (r) {
        return r.item && boxItem && String(r.item.id) === String(boxItem.id);
      });
      if (!record) return;
      var rich = L.resolveCigarData(record.item);
      if (rich.favorite === next) return;
      rich.favorite = next;
      var notes = L.serializeCigarNotes(rich);
      record.item.notes = notes;
      if (record.item.cigar_profile && typeof record.item.cigar_profile === 'object') {
        record.item.cigar_profile.favorite = next;
      }
      KlaroAPI.updateCollection(record.item.id, { notes: notes }).catch(function (err) {
        if (err.status === 401) return handleUnauthorized();
      });
    });
    // Favoriting a store-linked cigar also saves it to the store wishlist —
    // that list drives marketing emails, so favorites should feed it.
    // Additive only: unfavoriting never pulls anyone off the list.
    if (next) {
      var favHandle = (group.snapshot && group.snapshot.storeHandle) ||
        ((orderHistoryForGroup(group) || {}).handle) || null;
      if (favHandle) {
        addToStoreWishlist({
          handle: favHandle,
          title: L.getJournalDisplayTitle(group),
          vendor: (group.snapshot && group.snapshot.brand) || '',
          image: imageUrlFor(groupImage(group)) || null,
        });
      }
    }
    queueSave();
    refreshHeaderStats();
    return next;
  };

  var visibleGroups = function () {
    var groups = L.computeVisibleGroups({
      journalEntries: state.collectionUI.journalEntries,
      inventoryRecords: state.inventoryRecords,
      timeRange: state.timeRange,
      search: state.search,
      filters: state.filters,
      sort: state.sort,
    });
    if (state.sourceTab === 'ordered') {
      groups = groups.filter(isOrderedGroup);
    } else if (state.sourceTab === 'onhand') {
      groups = groups.filter(function (g) { return groupOnHandCount(g) > 0; });
    } else if (state.sourceTab === 'favorites') {
      groups = groups.filter(isFavoriteGroup);
    }
    return groups;
  };

  // Parent totals for the header — journal-wide (filters/tabs don't shrink
  // them): distinct cigars, all entries, and on-hand across every source
  // (app humidors + web-side seed counts).
  var journalTotals = function () {
    var groups = L.buildJournalCigarGroups({
      journalEntries: state.collectionUI.journalEntries,
      inventoryRecords: state.inventoryRecords,
    });
    var entries = groups.reduce(function (sum, g) { return sum + (g.historyCount || 0); }, 0);
    var onHand = state.inventoryRecords.reduce(function (sum, record) {
      return sum + Math.max(0, parseInt(record.item && record.item.quantity, 10) || 0);
    }, 0);
    (state.collectionUI.journalEntries || []).forEach(function (e) {
      if (e && e.initialRating === true && e.synthetic !== true) {
        onHand += Math.max(0, parseInt(e.snapshot && e.snapshot.quantity, 10) || 0);
      }
    });
    return {
      cigars: groups.length,
      entries: entries,
      onHand: onHand,
      favorites: groups.filter(isFavoriteGroup).length,
      purchased: groups.filter(isOrderedGroup).length,
    };
  };

  // A restored tab that renders empty while another tab holds the account's
  // data reads like broken sync — the classic case is an account that just
  // linked with app-side cigars, landing on a stale (or another customer's)
  // empty tab. Until the user picks a tab themselves, land on the data.
  // Returns true when the tab was switched.
  var tabTouched = false;
  var validateInitialTab = function () {
    if (tabTouched) return false;
    var t = journalTotals();
    var counts = {
      journal: t.cigars,
      onhand: t.onHand,
      favorites: t.favorites,
      ordered: ordersAvailable() ? t.purchased : 0,
      wishlist: state.wishlist.status === 'ready' ? state.wishlist.items.length : 0,
    };
    var changed = false;
    if (!(counts[state.sourceTab] > 0)) {
      var fallback = ['journal', 'onhand', 'favorites', 'ordered'].find(function (tab) {
        return counts[tab] > 0;
      });
      var next = fallback || 'journal';
      if (next !== state.sourceTab) {
        state.sourceTab = next;
        changed = true;
      }
    }
    // A stale narrow time range hides a content-bearing tab just as
    // completely as the wrong tab — widen it once at landing. Only ever
    // runs before the first interaction, so a range the user picks in
    // this session is never overridden.
    if (state.sourceTab !== 'wishlist' && counts[state.sourceTab] > 0 &&
        state.timeRange !== 'all' && !state.search &&
        L.countJournalFilters(state.filters) === 0 &&
        visibleGroups().length === 0) {
      state.timeRange = 'all';
      changed = true;
    }
    if (changed) savePrefs();
    return changed;
  };

  // The header pills ARE the preset tabs (app system-tab parity): on hand
  // (current stock), journal (every smoke ever), favorites, purchases.
  var headerStatsHtml = function () {
    var t = journalTotals();
    var stat = function (icon, n, label, tab) {
      return '<button type="button" class="j-header-stat' +
        (state.sourceTab === tab ? ' active' : '') + '" data-stat-tab="' + tab + '">' +
        '<span class="j-header-stat-icon">' + svg(icon, 12) + '</span>' +
        '<b>' + n + '</b> ' + label + '</button>';
    };
    return stat(ICONS.cigar, t.onHand, 'on hand', 'onhand') +
      stat(ICONS.book, t.entries, 'journal', 'journal') +
      stat(ICONS.star, t.favorites, 'favorites', 'favorites') +
      (ordersAvailable() || state.sourceTab === 'ordered'
        ? stat(ICONS.archiveCheck, t.purchased, 'purchases', 'ordered')
        : '') +
      (state.wishlist.status !== 'unavailable' || state.sourceTab === 'wishlist'
        ? stat(ICONS.bookmark, state.wishlist.items.length, 'wishlist', 'wishlist')
        : '');
  };

  var refreshHeaderStats = function () {
    var el = document.getElementById('j-header-stats');
    if (el) el.innerHTML = headerStatsHtml();
  };

  // "{First name}'s Journal", renameable — the custom title lives in
  // collection_ui (webJournalTitle) so it follows the account.
  var journalTitle = function () {
    var custom = (state.collectionUI && state.collectionUI.webJournalTitle || '').trim();
    if (custom) return custom;
    var first = (CONFIG.customerFirstName || '').trim();
    return first ? first + '’s Journal' : 'Your Journal';
  };

  var renderJournal = function () {
    var sortLabel = (L.JOURNAL_SORT_OPTIONS.find(function (o) {
      return o.key === state.sort.key && o.dir === state.sort.dir;
    }) || L.JOURNAL_SORT_OPTIONS[0]).label;
    var filterCount = L.countJournalFilters(state.filters);

    root.innerHTML =
      siteBarHtml() +
      '<header class="j-header">' +
        '<div>' +
          '<div class="j-header-eyebrow">Klaro Cigar Journal</div>' +
          '<h1>' + esc(journalTitle()) +
            (READ_ONLY
              ? '<span class="j-readonly-pill">Read-only</span>'
              : '<button class="j-title-edit" id="j-title-edit" aria-label="Rename your journal">' + svg(ICONS.edit, 15) + '</button>') +
          '</h1>' +
          '<div class="j-header-stats" id="j-header-stats">' + headerStatsHtml() + '</div></div>' +
        '<div class="j-header-actions">' +
          '<button class="j-add-top" id="j-add-top">' + svg(ICONS.plus, 14) + 'Add Cigar</button>' +
        '</div>' +
      '</header>' +
      '<div class="j-controls">' +
      // The wishlist isn't dated smoke history — the time filter would lie.
      (state.sourceTab === 'wishlist' ? '' :
      '<div class="j-time-filter" id="j-time-filter">' +
        L.JOURNAL_TIME_RANGES.map(function (range) {
          return (
            '<button class="j-time-segment' + (state.timeRange === range.key ? ' active' : '') +
            '" data-range="' + range.key + '">' + range.label + '</button>'
          );
        }).join('') +
      '</div>') +
      '<div class="j-toolbar">' +
        '<button class="j-tool-btn" id="j-filter-btn">' + svg(ICONS.filter, 15) +
          '<span class="j-filter-badge' + (filterCount > 0 ? ' visible' : '') + '">' + filterCount + '</span></button>' +
        '<button class="j-tool-btn" id="j-sort-btn">' + svg(ICONS.sort, 15) + esc(sortLabel) + '</button>' +
        '<button class="j-tool-btn" id="j-search-btn">' + svg(ICONS.search, 15) + '</button>' +
        '<div class="j-view-switcher">' +
          '<button class="j-view-seg' + (state.viewMode === 'compact' ? ' active' : '') + '" data-mode="compact" title="Compact">' + svg(ICONS.viewCompact, 15) + '</button>' +
          '<button class="j-view-seg' + (state.viewMode === 'comfortable' ? ' active' : '') + '" data-mode="comfortable" title="Comfortable">' + svg(ICONS.viewComfortable, 15) + '</button>' +
          '<button class="j-view-seg' + (state.viewMode === 'grid' ? ' active' : '') + '" data-mode="grid" title="Grid">' + svg(ICONS.viewGrid, 15) + '</button>' +
        '</div>' +
      '</div>' +
      '</div>' +
      '<div class="j-search-row' + (state.searchOpen ? ' visible' : '') + '">' +
        '<input id="j-search-input" type="search" placeholder="Search notes, cigars, boxes…" value="' + esc(state.search) + '">' +
      '</div>' +
      '<div id="j-list-region"></div>' +
      '<button class="j-fab" id="j-fab">' + svg(ICONS.plus, 18) + 'Add Cigar</button>' +
      '<div id="j-sheet-region"></div>';

    var timeFilter = document.getElementById('j-time-filter');
    if (timeFilter) timeFilter.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-range]');
      if (!btn) return;
      state.timeRange = btn.getAttribute('data-range');
      savePrefs();
      renderJournal();
    });
    // The header pills are the preset tabs.
    document.getElementById('j-header-stats').addEventListener('click', function (e) {
      var pill = e.target.closest('[data-stat-tab]');
      if (!pill) return;
      tabTouched = true;
      state.sourceTab = pill.getAttribute('data-stat-tab');
      savePrefs();
      renderJournal();
    });
    document.getElementById('j-filter-btn').addEventListener('click', function () { openSheet('filter'); });
    document.getElementById('j-sort-btn').addEventListener('click', function () { openSheet('sort'); });
    document.getElementById('j-search-btn').addEventListener('click', function () {
      state.searchOpen = !state.searchOpen;
      if (!state.searchOpen) state.search = '';
      renderJournal();
      if (state.searchOpen) {
        var input = document.getElementById('j-search-input');
        if (input) input.focus();
      }
    });
    root.querySelector('.j-view-switcher').addEventListener('click', function (e) {
      var btn = e.target.closest('[data-mode]');
      if (!btn) return;
      state.viewMode = btn.getAttribute('data-mode');
      savePrefs();
      renderJournal();
    });
    var searchInput = document.getElementById('j-search-input');
    if (searchInput) {
      searchInput.addEventListener('input', function () {
        state.search = searchInput.value;
        renderList();
      });
    }
    var onAddClick = function () {
      if (READ_ONLY) {
        showToast('Read-only mode — adding entries is disabled while production writes are off.');
        return;
      }
      state.editingStick = null;
      openSheet('stick');
    };
    var fab = document.getElementById('j-fab');
    if (fab) fab.addEventListener('click', onAddClick);
    var addTop = document.getElementById('j-add-top');
    if (addTop) addTop.addEventListener('click', onAddClick);
    var titleEdit = document.getElementById('j-title-edit');
    if (titleEdit) {
      titleEdit.addEventListener('click', function () {
        var next = prompt('Name your journal', journalTitle());
        if (next == null) return;
        next = next.trim().slice(0, 60);
        // Empty resets to the default "{First name}'s Journal".
        state.collectionUI.webJournalTitle = next;
        queueSave();
        renderJournal();
        showToast(next ? 'Journal renamed' : 'Journal name reset');
      });
    }

    renderList();

    // First visit on this device: run the intro tour once the journal is
    // actually on screen. Re-renders don't retrigger it.
    if (state.walkthroughSeenV < WALK_VERSION && !walkthroughAutoShown) {
      walkthroughAutoShown = true;
      setTimeout(openWalkthrough, 450);
    }
  };

  var renderList = function () {
    var region = document.getElementById('j-list-region');
    if (!region) return;
    refreshHeaderStats();
    if (state.sourceTab === 'wishlist') return renderWishlist(region);
    var groups = visibleGroups();
    var totalEntries = (state.collectionUI.journalEntries || []).length;

    if (groups.length === 0) {
      var isEmpty = totalEntries === 0;
      var searching = state.search.trim().length > 0;
      // Each preset tab explains what feeds it — a generic "no entries"
      // on, say, Favorites reads like the app sync is broken.
      var tabEmpty = {
        onhand: ['Nothing on hand', 'Cigars in your Klaro humidors (and quantities you log here) count as on hand automatically.'],
        favorites: ['No favorites yet', 'Star a cigar here or favorite it in the Klaro app — both show up on this tab.'],
        ordered: ['No purchases yet', 'Cigars you order on klarocigars.com are imported here automatically.'],
      }[state.sourceTab];
      // First run — nothing in the journal at all. This is the default
      // state for a fresh account: pitch the journal, hand over the pen,
      // and name the two hands-free ways entries appear on their own.
      if (isEmpty && !searching && !tabEmpty) {
        region.innerHTML =
          '<div class="j-empty j-empty-hero">' +
            '<span class="j-empty-hero-icon">' + svg(ICONS.book, 26) + '</span>' +
            '<h3>Start your journal</h3>' +
            '<p>Rate every cigar you smoke, keep tasting notes, and build a history of what you loved — synced with the Klaro app.</p>' +
            '<button class="j-empty-add" id="j-empty-add" type="button">' + svg(ICONS.plus, 15) + 'Add your first cigar</button>' +
            '<div class="j-empty-paths">' +
              '<span class="j-empty-path">' + svg(ICONS.cart, 15) +
                '<span><a href="/collections/cigar-collection">Shop cigars</a> — anything you order on klarocigars.com is added automatically.</span></span>' +
              '<span class="j-empty-path">' + svg(ICONS.sync, 15) +
                '<span>Have the Klaro app? Cigars you log there show up here on their own.</span></span>' +
            '</div>' +
          '</div>';
        var emptyAdd = document.getElementById('j-empty-add');
        if (emptyAdd) emptyAdd.addEventListener('click', function () {
          // Reuse the header/FAB handler so read-only + edit state stay
          // in one place.
          var opener = document.getElementById('j-add-top') || document.getElementById('j-fab');
          if (opener) opener.click();
        });
        return;
      }
      region.innerHTML =
        '<div class="j-empty">' + svg(ICONS.book, 44) +
          '<h3>' + (searching ? 'No matches' : tabEmpty ? tabEmpty[0] : 'Nothing in this range') + '</h3>' +
          '<p>' + (searching
            ? 'Nothing matches “' + esc(state.search.trim()) + '” — try a different search.'
            : tabEmpty
              ? tabEmpty[1]
              : 'Try a wider time range or add a cigar to get started.') + '</p>' +
        '</div>';
      return;
    }

    // Auto-import confirmation — purchased cigars were just added on load
    // (the store-side touch the iOS app can't make under Apple's rules).
    var calloutHtml = '';
    if (state.autoImportedCount > 0) {
      var addedRows = buildOrderImportRows().filter(function (row) { return row.inJournal; });
      var preview = addedRows.slice(0, 3).filter(function (row) { return row.image; });
      calloutHtml =
        '<div class="j-orders-callout" id="j-orders-callout">' +
          (preview.length
            ? '<span class="j-orders-callout-thumbs">' + preview.map(function (row) {
                return '<img src="' + esc(row.image) + '" alt="" loading="lazy">';
              }).join('') + '</span>'
            : '') +
          '<span class="j-orders-callout-body">' +
            '<span class="j-orders-callout-title">Added ' + state.autoImportedCount + ' cigar' +
              (state.autoImportedCount === 1 ? '' : 's') + ' from your orders</span>' +
            '<span class="j-orders-callout-sub">Everything you’ve ordered on klarocigars.com is in your journal — ready to rate.</span>' +
          '</span>' +
          '<button class="j-orders-callout-x" id="j-callout-dismiss" type="button" aria-label="Dismiss">' + svg(ICONS.close, 14) + '</button>' +
        '</div>';
    }

    var tagLibrary = state.collectionUI.tagLibrary || [];
    region.innerHTML =
      calloutHtml +
      '<div class="j-list' +
        (state.viewMode === 'grid' ? ' grid' : state.viewMode === 'compact' ? ' compact' : '') + '">' +
      groups.map(function (group, index) {
        return cardHtml(group, index, tagLibrary);
      }).join('') +
      '</div>';

    var calloutDismiss = document.getElementById('j-callout-dismiss');
    if (calloutDismiss) {
      calloutDismiss.addEventListener('click', function () {
        state.autoImportedCount = 0;
        var callout = document.getElementById('j-orders-callout');
        if (callout) callout.remove();
      });
    }

    region.querySelector('.j-list').addEventListener('click', function (e) {
      var card = e.target.closest('[data-group-index]');
      if (!card) return;
      var group = groups[Number(card.getAttribute('data-group-index'))];
      if (!group) return;
      state.detailGroup = group;
      openSheet('detail');
    });
  };

  var cardHtml = function (group, index, tagLibrary) {
    var title = L.getJournalDisplayTitle(group);
    var dateLabel = L.formatEntryDate(group.occurredAt || group.createdAt);
    var preview = L.notesPreview(group.notes, 140);
    var contextPreview = L.notesPreview(group.contextNotes, 100);
    var snapshot = group.snapshot || {};
    var latest = L.sortEntriesByOccurredDesc(group.entries)[0];
    var entryTags = ((latest && latest.tagIds) || [])
      .map(function (id) { return tagLibrary.find(function (t) { return t.id === id; }); })
      .filter(Boolean);
    var historyLabel = group.historyCount > 0
      ? group.historyCount + ' ' + (group.historyCount === 1 ? 'entry' : 'entries')
      : '';
    var reducedQtyCount = Number((latest && latest.reducedQtyCount) || 0);
    var sClass = strengthClass(snapshot.strength);
    var compact = state.viewMode === 'compact';
    var grid = state.viewMode === 'grid';
    var photoUrl = compact ? '' : imageUrlFor(groupImage(group));

    var html = '<article class="j-card' + (compact ? ' compact' : '') +
      (photoUrl && !grid ? ' has-thumb' : '') + '" data-group-index="' + index + '">';
    if (grid) {
      // Grid is the photo-forward gallery: full-bleed 1:1 product shot.
      html += '<div class="j-grid-strength-bar ' + (sClass || 'j-strength-none') + '"></div>';
      if (photoUrl) {
        html += '<div class="j-card-photo"><img src="' + esc(thumbUrl(photoUrl, 800)) + '" alt="" loading="lazy"></div>';
      }
    } else if (photoUrl) {
      html += '<div class="j-card-thumb"><img src="' + esc(thumbUrl(photoUrl, 400)) + '" alt="" loading="lazy"></div>';
    }
    html += '<div class="j-card-header"><div class="j-card-name">' + esc(title) + '</div>' +
      '<div class="j-card-date">' + esc(dateLabel) + '</div></div>';
    if (group.rating > 0) html += starsHtml(Math.round(group.rating));
    if (preview) html += '<div class="j-card-notes">' + esc(preview) + '</div>';
    if (contextPreview) {
      html += '<div class="j-card-context">' + svg(ICONS.marker, 13) + '<span>' + esc(contextPreview) + '</span></div>';
    }

    var metaChips = [];
    if (snapshot.strength && sClass) {
      metaChips.push('<span class="j-chip j-chip-strength ' + sClass + '">' + esc(snapshot.strength) + '</span>');
    }
    if (snapshot.brand) metaChips.push('<span class="j-chip j-chip-brand">' + esc(snapshot.brand) + '</span>');
    if (isOrderedGroup(group)) {
      var orderHistory = orderHistoryForGroup(group);
      var orderCount = orderHistory ? orderHistory.lines.length : 0;
      metaChips.push('<span class="j-chip j-chip-ordered">' + svg(ICONS.archiveCheck, 10) +
        'Ordered' + (orderCount > 1 ? ' ×' + orderCount : '') + '</span>');
    }
    entryTags.slice(0, 3).forEach(function (tag) {
      metaChips.push(
        '<span class="j-chip j-chip-tag" style="background:' + esc(tag.color || '#282828') + '">' + esc(tag.label) + '</span>'
      );
    });
    if (historyLabel) metaChips.push('<span class="j-chip j-chip-count">' + esc(historyLabel) + '</span>');
    if (reducedQtyCount > 0) metaChips.push('<span class="j-chip j-chip-count">Box -' + reducedQtyCount + '</span>');
    if (metaChips.length) html += '<div class="j-meta-row">' + metaChips.join('') + '</div>';

    var footerChips = [];
    var showBoxName = snapshot.boxName &&
      !(group.activeLocationLabel || '').toLowerCase().replace(/\s+/g, ' ').trim()
        .startsWith(String(snapshot.boxName).toLowerCase().replace(/\s+/g, ' ').trim());
    if (showBoxName) {
      footerChips.push('<span class="j-chip j-chip-box">' + svg(ICONS.archive, 10) + esc(snapshot.boxName) + '</span>');
    }
    if (group.activeLocationLabel) {
      footerChips.push('<span class="j-chip j-chip-active">' + svg(ICONS.archiveCheck, 10) + esc(group.activeLocationLabel) + '</span>');
    }
    if (footerChips.length) html += '<div class="j-card-footer">' + footerChips.join('') + '</div>';

    return html + '</article>';
  };

  // ── Wishlist (Swym) ────────────────────────────────────────────────────
  // The store runs Swym Wishlist Plus; page.journal.liquid renders the
  // Swym config snippet + loader, and we read the customer's lists via the
  // on-page SDK (window.SwymCallbacks → swat.fetchLists). Lists usually
  // arrive with `listcontents` inline; fetchListDetails covers the rest.

  var normalizeWishlistItems = function (raw) {
    var out = [];
    var seen = {};
    (raw || []).forEach(function (it) {
      if (!it) return;
      var title = String(it.dt || it.title || '').trim();
      var du = String(it.du || it.url || '');
      var handle = it.handle || productHandleFromUrl(du);
      var key = String(it.empi || handle || title).toLowerCase();
      if (!key || seen[key]) return;
      seen[key] = true;
      var image = String(it.iu || it.image || '');
      if (image.indexOf('//') === 0) image = 'https:' + image;
      var price = it.pr != null ? it.pr : it.price;
      if (Array.isArray(price)) price = price[0];
      price = typeof price === 'number' ? price : parseFloat(price);
      out.push({
        key: key,
        title: title,
        handle: handle,
        url: handle ? '/products/' + handle : du,
        image: image || null,
        price: isFinite(price) && price > 0 ? price : null,
        vendor: String(it.bt || it.vendor || '').trim(),
        variantTitle: String(it.vi || '').trim(),
        addedAt: it.cts || null,
        raw: it, // Swym item as-is — removal needs empi/epi/du
      });
    });
    // Newest saves first, like the store's wishlist page.
    out.sort(function (a, b) { return (b.addedAt || 0) - (a.addedAt || 0); });
    return out;
  };

  var refreshWishlistUI = function () {
    refreshHeaderStats();
    if (state.sourceTab === 'wishlist' && state.view === 'journal') renderList();
  };

  // One shared Swym boot promise: resolves the SDK, or null when it can't
  // load (ad blockers commonly block swymrelay.com — the Liquid script tag
  // sets __swymBlocked on error so we fail fast instead of waiting out the
  // timer). Dev harness (CONFIG.wishlist stub) never touches Swym.
  var swymPromise = null;
  var getSwym = function () {
    if (Array.isArray(CONFIG.wishlist)) return Promise.resolve(null);
    if (swymPromise) return swymPromise;
    swymPromise = new Promise(function (resolve) {
      if (window.__swymBlocked) return resolve(null);
      var bootTimer = setTimeout(function () { resolve(null); }, 12000);
      document.addEventListener('swym:blocked', function () {
        clearTimeout(bootTimer);
        resolve(null);
      });
      try {
        window.SwymCallbacks = window.SwymCallbacks || [];
        window.SwymCallbacks.push(function (swat) {
          clearTimeout(bootTimer);
          resolve(swat);
        });
      } catch (e) {
        clearTimeout(bootTimer);
        resolve(null);
      }
    });
    return swymPromise;
  };

  var fetchSwymItems = function (swat) {
    return new Promise(function (resolve) {
      try {
        swat.fetchLists({
          callbackFn: function (lists) {
            lists = Array.isArray(lists) ? lists : [];
            if (lists.length === 0) return resolve([]);
            var all = [];
            var pending = lists.length;
            var done = function () {
              pending -= 1;
              if (pending === 0) resolve(all);
            };
            lists.forEach(function (list) {
              var inline = list && (list.listcontents || list.items);
              if (Array.isArray(inline)) {
                all = all.concat(inline);
                return done();
              }
              try {
                swat.fetchListDetails({
                  lid: list.lid,
                  callbackFn: function (detail) {
                    all = all.concat((detail && (detail.listcontents || detail.items)) || []);
                    done();
                  },
                  errorFn: done,
                });
              } catch (e) { done(); }
            });
          },
          errorFn: function () { resolve(null); },
        });
      } catch (e) { resolve(null); }
    });
  };

  // The tab shows Swym's list merged with our own fallback adds
  // (collection_ui.webWishlistAdds) — favorites and wishlist saves made
  // while Swym is blocked still land somewhere, and sync up later.
  var recomputeWishlist = function () {
    var seen = {};
    var items = [];
    (state.swymItems || []).forEach(function (item) {
      if (seen[item.key]) return;
      seen[item.key] = true;
      items.push(item);
    });
    (state.collectionUI.webWishlistAdds || []).forEach(function (add) {
      var key = String(add.handle || add.title || '').toLowerCase();
      if (!key || seen[key]) return;
      seen[key] = true;
      items.push({
        key: key,
        title: add.title || '',
        handle: add.handle || null,
        url: add.handle ? '/products/' + add.handle : '',
        image: add.image || null,
        price: add.price || null,
        vendor: add.vendor || '',
        variantTitle: '',
        addedAt: add.addedAt || null,
        local: true,
      });
    });
    items.sort(function (a, b) { return (b.addedAt || 0) - (a.addedAt || 0); });
    state.wishlist.items = items;
    refreshWishlistUI();
  };

  // Product id + first variant for a handle — Swym's add API wants
  // empi/epi, we only track handles.
  var swymProductCache = {};
  var resolveSwymProduct = function (handle) {
    if (!handle) return Promise.resolve(null);
    if (swymProductCache[handle]) return Promise.resolve(swymProductCache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (body) {
        if (!body || !body.id) return null;
        var variant = (body.variants || [])[0] || {};
        var resolved = {
          empi: body.id,
          epi: variant.id,
          pr: (Number(variant.price) || 0) / 100,
          iu: (body.images || [])[0] || undefined,
          dt: body.title,
        };
        swymProductCache[handle] = resolved;
        return resolved;
      })
      .catch(function () { return null; });
  };

  // Push one fallback add into Swym (the wishlist drives marketing emails,
  // so journal favorites should reach it). Best-effort: failures leave the
  // record unsynced and the next load retries.
  // once per record per page load — a broken handle re-tries fresh next
  // visit (in-memory on purpose: persisting the flag would stop the record
  // from ever healing when the catalog changes)
  var rescuedRecs = typeof WeakSet === 'function' ? new WeakSet() : null;

  var pushWishlistAddToSwym = function (rec) {
    return getSwym().then(function (swat) {
      if (!swat || !rec || rec.synced) return;
      return resolveSwymProduct(rec.handle).then(function (p) {
        if (!p || !p.epi) {
          // stored handle can be stale (product renamed) — /products/x.js
          // 404s without redirecting. Rescue by title search.
          if (!rec.title || !rescuedRecs || rescuedRecs.has(rec)) return;
          rescuedRecs.add(rec);
          return searchStoreHandle(rec.title, rec.vendor || '').then(function (found) {
            if (!found || found === rec.handle) return;
            rec.handle = found;
            queueSave();
            return pushWishlistAddToSwym(rec);
          });
        }
        try {
          swat.addToWishList({
            empi: p.empi,
            epi: p.epi,
            du: location.origin + '/products/' + rec.handle,
            dt: rec.title || p.dt,
            iu: rec.image || p.iu,
            pr: p.pr,
          }, function () {
            rec.synced = true;
            queueSave();
          });
        } catch (e) { /* stays unsynced, retried next load */ }
      });
    });
  };

  var syncPendingWishlistAdds = function () {
    (state.collectionUI.webWishlistAdds || []).forEach(function (rec) {
      if (!rec.synced) pushWishlistAddToSwym(rec);
    });
  };

  // Find a store product for a favorite by title. Token-SET equality
  // (order- and punctuation-insensitive) matches outright; otherwise a
  // subset match in either direction (app names drop words like "Reserve"
  // or add vitolas) is accepted only when EXACTLY ONE product qualifies —
  // the wishlist drives marketing, so an ambiguous hit is treated as no
  // hit ("Perdomo 10th Anniversary" fits Champagne AND Sun-Grown: null).
  var searchStoreHandle = function (name, brand) {
    var tokens = function (s) {
      var t = String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      var out = [];
      (t ? t.split(' ') : []).forEach(function (w) {
        if (out.indexOf(w) === -1) out.push(w);
      });
      return out;
    };
    var subset = function (a, b) {
      return a.length > 0 && a.every(function (w) { return b.indexOf(w) !== -1; });
    };
    var qTok = tokens((brand || '') + ' ' + (name || ''));
    var nTok = tokens(name);
    if (!qTok.length) return Promise.resolve(null);
    var q = ((brand || '') + ' ' + (name || '')).trim();
    return fetch('/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=10', {
      headers: { Accept: 'application/json' },
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (body) {
        var products = (((body || {}).resources || {}).results || {}).products || [];
        var scored = products.map(function (p) {
          var tTok = tokens(p && p.title);
          var eq = function (x) { return subset(x, tTok) && subset(tTok, x); };
          return {
            handle: (p && p.handle) || null,
            exact: eq(nTok) || eq(qTok),
            partial: subset(qTok, tTok) || subset(tTok, qTok) ||
              subset(nTok, tTok) || subset(tTok, nTok),
          };
        }).filter(function (s) { return s.handle; });
        var exact = scored.filter(function (s) { return s.exact; });
        if (exact.length) return exact[0].handle;
        var partial = scored.filter(function (s) { return s.partial; });
        return partial.length === 1 ? partial[0].handle : null;
      })
      .catch(function () { return null; });
  };

  var searchStoreHandleForGroup = function (group) {
    return searchStoreHandle(
      L.getJournalDisplayTitle(group),
      (group.snapshot && group.snapshot.brand) || ''
    );
  };

  // App-side favorites arrive here as snapshot.favorite but were never
  // pushed to Swym (the toggle bridge only fires on web-side taps), so the
  // storefront's saved chips and Favorites view miss them. Reconcile after
  // the real Swym list loads. One-shot per handle — a reconciled handle is
  // never re-pushed, so a deliberate wishlist removal isn't fought on the
  // next load. Favorites with no linked handle (the app never sets one, and
  // the order match needs a near-exact title) fall back to store search;
  // a miss there stays UNmarked so it retries once the catalog catches up.
  var reconcileFavoritesToWishlist = function () {
    if (READ_ONLY) return;
    if (!Array.isArray(state.collectionUI.swymFavReconciled)) {
      state.collectionUI.swymFavReconciled = [];
    }
    var done = state.collectionUI.swymFavReconciled;
    var bridge = function (handle, group) {
      done.push(handle);
      queueSave();
      addToStoreWishlist({
        handle: handle,
        title: L.getJournalDisplayTitle(group),
        vendor: (group.snapshot && group.snapshot.brand) || '',
        image: imageUrlFor(groupImage(group)) || null,
      }, true);
    };
    var groups = L.buildJournalCigarGroups({
      journalEntries: state.collectionUI.journalEntries,
      inventoryRecords: state.inventoryRecords,
    });
    groups.forEach(function (group) {
      if (!isFavoriteGroup(group)) return;
      var handle = (group.snapshot && group.snapshot.storeHandle) ||
        ((orderHistoryForGroup(group) || {}).handle) || null;
      if (handle) {
        if (done.indexOf(handle) === -1) bridge(handle, group);
        return;
      }
      searchStoreHandleForGroup(group).then(function (found) {
        if (found && done.indexOf(found) === -1) bridge(found, group);
      });
    });
  };

  // Add a store product to the wishlist from journal-side actions
  // (quick favorite, form favorite). Additive by design — the wishlist is
  // the marketing list, so unfavoriting never silently removes anyone.
  var addToStoreWishlist = function (info, silent) {
    if (READ_ONLY || !info || !info.handle) return;
    var key = String(info.handle).toLowerCase();
    var already = (state.wishlist.items || []).some(function (i) { return i.key === key || i.handle === info.handle; }) ||
      (state.collectionUI.webWishlistAdds || []).some(function (a) { return a.handle === info.handle; });
    if (already) return;
    var rec = {
      handle: info.handle,
      title: info.title || '',
      vendor: info.vendor || '',
      image: info.image || null,
      price: info.price || null,
      addedAt: Date.now(),
      synced: false,
    };
    state.collectionUI.webWishlistAdds = (state.collectionUI.webWishlistAdds || []).concat([rec]);
    queueSave();
    if (state.wishlist.status === 'idle' || state.wishlist.status === 'unavailable') {
      state.wishlist.status = 'ready';
    }
    recomputeWishlist();
    pushWishlistAddToSwym(rec);
    if (!silent) showToast('Added to your wishlist too');
  };

  var removeWishlistItem = function (item) {
    if (!item) return;
    // Drop any matching fallback record.
    var adds = state.collectionUI.webWishlistAdds || [];
    var next = adds.filter(function (a) {
      return String(a.handle || a.title || '').toLowerCase() !== item.key;
    });
    if (next.length !== adds.length) {
      state.collectionUI.webWishlistAdds = next;
      queueSave();
    }
    // Swym-side removal, best-effort (raw carries empi/epi/du).
    if (item.raw) {
      getSwym().then(function (swat) {
        if (!swat) return;
        try {
          swat.removeFromWishList({
            empi: item.raw.empi,
            epi: item.raw.epi,
            du: item.raw.du,
          }, function () {}, function () {});
        } catch (e) {}
      });
    }
    state.swymItems = (state.swymItems || []).filter(function (i) { return i.key !== item.key; });
    recomputeWishlist();
    showToast('Removed from your wishlist');
  };

  var loadWishlist = function () {
    var wl = state.wishlist;
    // A restored wishlist tab that settled empty gets the same landing
    // check as the other tabs (deferred to here because it loads async).
    // Never while a sheet or search is open — the user is engaged with
    // the current view, and renderJournal() would wipe their work.
    var settled = function () {
      clearTimeout(watchdog);
      recomputeWishlist();
      if (state.sourceTab === 'wishlist' && !state.openSheet && !state.searchOpen &&
          validateInitialTab()) renderJournal();
    };
    if (wl.status === 'loading' || wl.status === 'ready') return;
    if (Array.isArray(CONFIG.wishlist)) { // dev harness stub
      state.swymItems = normalizeWishlistItems(CONFIG.wishlist);
      wl.status = 'ready';
      settled();
      return;
    }
    wl.status = 'loading';
    // Swym boots via page callbacks that can silently never fire — don't
    // let a restored wishlist tab sit on the spinner forever with the
    // user's data hidden behind it.
    var watchdog = setTimeout(function () {
      if (wl.status !== 'loading') return;
      state.swymItems = [];
      wl.status = (state.collectionUI.webWishlistAdds || []).length ? 'ready' : 'unavailable';
      settled();
    }, 15000);
    getSwym().then(function (swat) {
      if (!swat) {
        // Swym blocked/missing: our fallback adds still power the tab.
        state.swymItems = [];
        wl.status = (state.collectionUI.webWishlistAdds || []).length ? 'ready' : 'unavailable';
        settled();
        return;
      }
      syncPendingWishlistAdds();
      fetchSwymItems(swat).then(function (raw) {
        if (raw == null) {
          state.swymItems = [];
          wl.status = (state.collectionUI.webWishlistAdds || []).length ? 'ready' : 'unavailable';
        } else {
          state.swymItems = normalizeWishlistItems(raw);
          wl.status = 'ready';
        }
        settled();
        if (raw != null) reconcileFavoritesToWishlist();
      });
    });
  };

  // A wishlisted product the journal already knows renders as its journal
  // card — matched by the linked store handle first, full-title overlap
  // second (wishlist titles are complete product titles).
  var wishlistJournalGroupFor = function (item, groups) {
    var norm = function (s) {
      return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    };
    var t = norm(item.title);
    return groups.find(function (group) {
      var snap = group.snapshot || {};
      if (item.handle && snap.storeHandle === item.handle) return true;
      var gt = norm(L.getJournalDisplayTitle(group));
      return t && gt && (gt === t || gt.indexOf(t) !== -1 || t.indexOf(gt) !== -1);
    }) || null;
  };

  var wishlistCardHtml = function (item, index) {
    var compact = state.viewMode === 'compact';
    var grid = state.viewMode === 'grid';
    var photoUrl = compact ? '' : (item.image || '');
    var priceLabel = item.price != null ? formatPrice(item.price) : '';

    var html = '<article class="j-card j-card-wish' + (compact ? ' compact' : '') +
      (photoUrl && !grid ? ' has-thumb' : '') + '" data-wl-index="' + index + '">';
    if (grid) {
      html += '<div class="j-grid-strength-bar j-strength-none"></div>';
      if (photoUrl) {
        html += '<div class="j-card-photo"><img src="' + esc(thumbUrl(photoUrl, 800)) + '" alt="" loading="lazy"></div>';
      }
    } else if (photoUrl) {
      html += '<div class="j-card-thumb"><img src="' + esc(thumbUrl(photoUrl, 400)) + '" alt="" loading="lazy"></div>';
    }
    html += '<div class="j-card-header"><div class="j-card-name">' + esc(item.title) + '</div>' +
      (priceLabel ? '<div class="j-card-date">' + esc(priceLabel) + '</div>' : '') + '</div>';

    var metaChips = ['<span class="j-chip j-chip-wish">' + svg(ICONS.bookmark, 10) + 'Wishlist</span>'];
    if (item.vendor) metaChips.push('<span class="j-chip j-chip-brand">' + esc(item.vendor) + '</span>');
    if (item.variantTitle && !compact) {
      metaChips.push('<span class="j-chip j-chip-count">' + esc(item.variantTitle) + '</span>');
    }
    html += '<div class="j-meta-row">' + metaChips.join('') + '</div>';

    if (!compact) {
      html += '<div class="j-card-footer j-wish-footer">' +
        (READ_ONLY ? '' : '<span class="j-wish-hint">' + svg(ICONS.plus, 12) + 'Tap to add to your journal</span>') +
        '<span class="j-wish-footer-links">' +
          (item.url ? '<a class="j-wish-link" href="' + esc(item.url) + '" target="_blank" rel="noopener">View in store</a>' : '') +
          (READ_ONLY ? '' : '<button type="button" class="j-wish-remove" data-wl-remove>Remove</button>') +
        '</span>' +
        '</div>';
    }
    return html + '</article>';
  };

  var renderWishlist = function (region) {
    var wl = state.wishlist;
    if (wl.status === 'idle' || wl.status === 'loading') {
      region.innerHTML = '<div class="j-empty"><div class="j-spinner"></div>' +
        '<p>Loading your wishlist…</p></div>';
      return;
    }
    if (wl.status === 'unavailable') {
      region.innerHTML = '<div class="j-empty">' + svg(ICONS.bookmark, 44) +
        '<h3>Wishlist unavailable</h3>' +
        '<p>We couldn’t reach the store wishlist. Refresh the page to try again.</p></div>';
      return;
    }
    var q = state.search.trim().toLowerCase();
    var items = wl.items.filter(function (item) {
      return !q || (item.title + ' ' + item.vendor).toLowerCase().indexOf(q) !== -1;
    });
    if (items.length === 0) {
      region.innerHTML = '<div class="j-empty">' + svg(ICONS.bookmark, 44) +
        '<h3>' + (wl.items.length ? 'No wishlist match' : 'Your wishlist is empty') + '</h3>' +
        '<p>' + (wl.items.length
          ? 'Try a different search.'
          : 'Tap the wishlist button on any cigar at klarocigars.com and it shows up here. Cigars you saved or favorited in the Klaro app live under Favorites.') + '</p>' +
        (wl.items.length ? '' :
          '<a class="j-btn j-btn-primary" href="/collections/cigars" style="text-decoration:none">Browse cigars</a>') +
        '</div>';
      return;
    }

    // Pair wishlist rows with journal cigars where they exist — those get
    // the full journal card (tap → detail); the rest are store products
    // the journal hasn't met yet (tap → prefilled Add).
    var allGroups = L.buildJournalCigarGroups({
      journalEntries: state.collectionUI.journalEntries,
      inventoryRecords: state.inventoryRecords,
    });
    var tagLibrary = state.collectionUI.tagLibrary || [];
    var rows = items.map(function (item) {
      return { item: item, group: wishlistJournalGroupFor(item, allGroups) };
    });

    region.innerHTML =
      '<div class="j-list' +
        (state.viewMode === 'grid' ? ' grid' : state.viewMode === 'compact' ? ' compact' : '') + '">' +
      rows.map(function (row, index) {
        return row.group ? cardHtml(row.group, index, tagLibrary) : wishlistCardHtml(row.item, index);
      }).join('') +
      '</div>';

    region.querySelector('.j-list').addEventListener('click', function (e) {
      if (e.target.closest('a')) return; // "View in store" navigates
      var card = e.target.closest('[data-group-index],[data-wl-index]');
      if (!card) return;
      var indexAttr = card.getAttribute('data-group-index');
      if (indexAttr == null) indexAttr = card.getAttribute('data-wl-index');
      var row = rows[Number(indexAttr)];
      if (!row) return;
      if (e.target.closest('[data-wl-remove]')) {
        removeWishlistItem(row.item);
        return;
      }
      if (row.group) {
        state.detailGroup = row.group;
        openSheet('detail');
        return;
      }
      if (READ_ONLY) {
        showToast('Read-only mode — adding entries is disabled while production writes are off.');
        return;
      }
      state.editingStick = null;
      state.stickPrefill = row.item;
      openSheet('stick');
    });
  };

  // ── Sheets ─────────────────────────────────────────────────────────────

  var closeSheet = function () {
    state.openSheet = null;
    var region = document.getElementById('j-sheet-region');
    if (!region) return;
    var scrim = region.querySelector('.j-scrim');
    var sheet = region.querySelector('.j-sheet');
    if (scrim) scrim.classList.remove('open');
    if (sheet) sheet.classList.remove('open');
    setTimeout(function () { region.innerHTML = ''; }, 260);
  };

  var openSheet = function (kind) {
    state.openSheet = kind;
    var region = document.getElementById('j-sheet-region');
    if (!region) return;

    var bodyHtml = '';
    if (kind === 'sort') bodyHtml = sortSheetHtml();
    else if (kind === 'filter') bodyHtml = filterSheetHtml();
    else if (kind === 'entry') bodyHtml = entrySheetHtml();
    else if (kind === 'stick') bodyHtml = stickSheetHtml();
    else if (kind === 'detail') bodyHtml = detailSheetHtml();
    else if (kind === 'merge') bodyHtml = mergeSheetHtml();

    region.innerHTML = '<div class="j-scrim"></div>' + bodyHtml;

    var scrim = region.querySelector('.j-scrim');
    var sheet = region.querySelector('.j-sheet');
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        scrim.classList.add('open');
        sheet.classList.add('open');
      });
    });
    scrim.addEventListener('click', closeSheet);

    if (kind === 'sort') wireSortSheet(region);
    else if (kind === 'filter') wireFilterSheet(region);
    else if (kind === 'entry') wireEntrySheet(region);
    else if (kind === 'stick') wireStickSheet(region);
    else if (kind === 'detail') wireDetailSheet(region);
    else if (kind === 'merge') wireMergeSheet(region);
  };

  // Header convention: X close top-left (every sheet), optional action
  // top-right (Save / Add / Reset).
  var sheetShell = function (title, bodyHtml, rightAction, leftAction) {
    return (
      '<div class="j-sheet"><div class="j-sheet-grabber"></div>' +
        '<div class="j-sheet-header">' +
          (leftAction ||
            '<button class="j-sheet-close" data-close aria-label="Close">' + svg(ICONS.close, 18) + '</button>') +
          '<h3>' + esc(title) + '</h3>' +
          (rightAction || '<span></span>') +
        '</div>' +
        '<div class="j-sheet-body">' + bodyHtml + '</div>' +
      '</div>'
    );
  };

  // — Sort sheet —

  var sortSheetHtml = function () {
    var rows = L.JOURNAL_SORT_OPTIONS.map(function (option, i) {
      var selected = option.key === state.sort.key && option.dir === state.sort.dir;
      return (
        '<button class="j-radio-row' + (selected ? ' selected' : '') + '" data-sort-index="' + i + '">' +
          '<span>' + esc(option.label) + '</span>' +
          '<span class="j-radio-check">' + svg(ICONS.check, 18) + '</span>' +
        '</button>'
      );
    }).join('');
    return sheetShell('Sort by', rows);
  };

  var wireSortSheet = function (region) {
    region.querySelector('[data-close]').addEventListener('click', closeSheet);
    region.querySelector('.j-sheet-body').addEventListener('click', function (e) {
      var row = e.target.closest('[data-sort-index]');
      if (!row) return;
      state.sort = Object.assign({}, L.JOURNAL_SORT_OPTIONS[Number(row.getAttribute('data-sort-index'))]);
      savePrefs();
      closeSheet();
      renderJournal();
    });
  };

  // — Filter sheet (draft state: Apply commits, Reset wipes, Close discards) —

  var filterDraft = null;

  var filterSheetHtml = function () {
    filterDraft = JSON.parse(JSON.stringify(state.filters));
    var boxes = L.availableJournalBoxes(state.collectionUI.journalEntries, state.inventoryRecords);

    var ratingOps = [
      { op: 'all', label: 'Any rating' },
      { op: 'gt', label: 'Above' },
      { op: 'lt', label: 'Below' },
      { op: 'eq', label: 'Exactly' },
    ];

    return sheetShell(
      'Filters',
      '<div class="j-toggle-row"><div><div class="j-toggle-label">Favorites only</div></div>' +
        '<button class="j-switch" data-toggle="favoritesOnly"></button></div>' +
      '<div class="j-toggle-row"><div><div class="j-toggle-label">Has notes</div>' +
        '<div class="j-toggle-sub">Only cigars with written notes</div></div>' +
        '<button class="j-switch" data-toggle="hasNotes"></button></div>' +
      '<div class="j-section-label">Rating</div>' +
      '<div class="j-chip-row" id="j-rating-ops">' +
        ratingOps.map(function (o) {
          return '<button class="j-choice-chip" data-rating-op="' + o.op + '">' + o.label + '</button>';
        }).join('') +
      '</div>' +
      '<div class="j-star-input" id="j-rating-stars" style="margin-top:10px"></div>' +
      '<div class="j-section-label">Entries</div>' +
      '<div class="j-chip-row" id="j-entry-counts">' +
        L.ENTRY_COUNT_OPTIONS.map(function (o) {
          return '<button class="j-choice-chip" data-entry-count="' + o.value + '">' + o.label + '</button>';
        }).join('') +
      '</div>' +
      (boxes.length
        ? '<div class="j-section-label">Boxes</div><div class="j-chip-row" id="j-box-chips">' +
          boxes.map(function (name) {
            return '<button class="j-choice-chip" data-box="' + esc(name) + '">' + esc(name) + '</button>';
          }).join('') + '</div>'
        : '') +
      '<div style="margin-top:24px"><button class="j-btn j-btn-primary" id="j-filter-apply">Apply</button></div>',
      '<button class="j-sheet-action" data-reset>Reset</button>'
    );
  };

  var syncFilterSheetUI = function (region) {
    region.querySelectorAll('[data-toggle]').forEach(function (el) {
      el.classList.toggle('on', filterDraft[el.getAttribute('data-toggle')] === true);
    });
    region.querySelectorAll('[data-rating-op]').forEach(function (el) {
      el.classList.toggle('selected', filterDraft.ratingOp === el.getAttribute('data-rating-op'));
    });
    var starsWrap = region.querySelector('#j-rating-stars');
    if (filterDraft.ratingOp && filterDraft.ratingOp !== 'all') {
      starsWrap.style.display = 'flex';
      starsWrap.innerHTML = '';
      for (var i = 1; i <= 5; i++) {
        var btn = document.createElement('button');
        btn.setAttribute('data-star', i);
        btn.innerHTML =
          '<span style="color:' + (i <= (filterDraft.ratingMin || 0) ? 'var(--j-accent)' : 'var(--j-star-off)') + '">' +
          svg(ICONS.star, 26) + '</span>';
        starsWrap.appendChild(btn);
      }
    } else {
      starsWrap.style.display = 'none';
    }
    region.querySelectorAll('[data-entry-count]').forEach(function (el) {
      el.classList.toggle('selected', Number(filterDraft.minEntryCount || 0) === Number(el.getAttribute('data-entry-count')));
    });
    region.querySelectorAll('[data-box]').forEach(function (el) {
      el.classList.toggle('selected', (filterDraft.boxes || []).indexOf(el.getAttribute('data-box')) !== -1);
    });
  };

  var wireFilterSheet = function (region) {
    syncFilterSheetUI(region);
    region.querySelector('[data-close]').addEventListener('click', closeSheet);
    region.querySelector('[data-reset]').addEventListener('click', function () {
      filterDraft = Object.assign({}, L.DEFAULT_JOURNAL_FILTERS, { boxes: [] });
      syncFilterSheetUI(region);
    });
    region.querySelector('.j-sheet-body').addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-toggle]');
      if (toggle) {
        var key = toggle.getAttribute('data-toggle');
        filterDraft[key] = !filterDraft[key];
        syncFilterSheetUI(region);
        return;
      }
      var op = e.target.closest('[data-rating-op]');
      if (op) {
        filterDraft.ratingOp = op.getAttribute('data-rating-op');
        if (filterDraft.ratingOp !== 'all' && !filterDraft.ratingMin) filterDraft.ratingMin = 3;
        syncFilterSheetUI(region);
        return;
      }
      var star = e.target.closest('[data-star]');
      if (star) {
        filterDraft.ratingMin = Number(star.getAttribute('data-star'));
        syncFilterSheetUI(region);
        return;
      }
      var count = e.target.closest('[data-entry-count]');
      if (count) {
        filterDraft.minEntryCount = Number(count.getAttribute('data-entry-count'));
        syncFilterSheetUI(region);
        return;
      }
      var box = e.target.closest('[data-box]');
      if (box) {
        var name = box.getAttribute('data-box');
        var list = filterDraft.boxes || [];
        var at = list.indexOf(name);
        if (at === -1) list.push(name);
        else list.splice(at, 1);
        filterDraft.boxes = list;
        syncFilterSheetUI(region);
        return;
      }
      if (e.target.closest('#j-filter-apply')) {
        state.filters = filterDraft;
        closeSheet();
        renderJournal();
      }
    });
  };

  // — Add / edit entry sheet —

  var entryForm = null;

  var entrySheetHtml = function () {
    var editing = state.editingEntry;
    entryForm = editing
      ? {
          name: (editing.snapshot && editing.snapshot.name) || '',
          brand: (editing.snapshot && editing.snapshot.brand) || '',
          rating: editing.rating || 0,
          notes: editing.notes || '',
          contextNotes: editing.contextNotes || '',
          occurredAt: editing.occurredAt || Date.now(),
          tagIds: (editing.tagIds || []).slice(),
          reduceQty: false,
          reduceQtyCount: 1,
          linkedItemRecord: null,
        }
      : {
          name: '',
          brand: '',
          rating: 0,
          notes: '',
          contextNotes: '',
          occurredAt: Date.now(),
          tagIds: [],
          reduceQty: false,
          reduceQtyCount: 1,
          linkedItemRecord: null,
        };
    if (state.detailGroup && !editing) {
      // Adding from a group detail — prefill identity + link inventory item.
      entryForm.name = (state.detailGroup.snapshot && state.detailGroup.snapshot.name) || '';
      entryForm.brand = (state.detailGroup.snapshot && state.detailGroup.snapshot.brand) || '';
      entryForm.linkedItemRecord = state.inventoryRecords.find(function (record) {
        return record.item && String(record.item.id) === String(state.detailGroup.itemId);
      }) || null;
    }

    var dateValue = new Date(entryForm.occurredAt).toISOString().slice(0, 10);
    var tagLibrary = state.collectionUI.tagLibrary || [];

    // "Use Last Entry" — latest real (non-synthetic) entry for this cigar,
    // only when adding from a group detail (mirrors the app, which always
    // records against an item).
    var lastEntry = null;
    if (!editing && state.detailGroup) {
      (state.detailGroup.entries || []).forEach(function (e) {
        if (!e || e.synthetic || e.initialRating) return;
        if (!lastEntry || (e.occurredAt || 0) > (lastEntry.occurredAt || 0)) lastEntry = e;
      });
    }

    return sheetShell(
      editing ? 'Edit entry' : 'Record entry',
      (lastEntry
        ? '<button class="j-last-entry" id="j-use-last" type="button">' +
            '<span class="j-last-entry-icon">' + svg('M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z', 17) + '</span>' +
            '<span class="j-last-entry-body"><span class="j-last-entry-title">Use Last Entry</span>' +
            '<span class="j-last-entry-sub">Prefill rating, notes, tags, and context from ' +
            esc(L.formatEntryDate(lastEntry.occurredAt || lastEntry.createdAt || Date.now())) + '</span></span>' +
          '</button>'
        : '') +
      '<div class="j-field j-suggest"><label for="j-entry-name">Cigar</label>' +
        '<input id="j-entry-name" type="text" placeholder="e.g. Padron 1964 Anniversary" autocomplete="off" value="' + esc(entryForm.name) + '">' +
        '<div class="j-suggest-list" id="j-entry-suggest"></div></div>' +
      '<div class="j-field"><label for="j-entry-brand">Brand</label>' +
        '<input id="j-entry-brand" type="text" placeholder="e.g. Padron" autocomplete="off" value="' + esc(entryForm.brand) + '">' +
        '<div class="j-past-brands" id="j-past-brands" style="display:none">' +
          '<div class="j-section-label" style="margin:8px 0 6px">Past brands</div>' +
          '<div class="j-chip-row" id="j-past-brands-row"></div></div></div>' +
      '<div class="j-section-label">Tags</div>' +
      '<div class="j-chip-row" id="j-entry-tags">' +
        tagLibrary.map(function (tag) {
          return '<button class="j-choice-chip" data-tag="' + esc(tag.id) + '" data-color="' + esc(tag.color || '') + '">' + esc(tag.label) + '</button>';
        }).join('') +
        '<button class="j-choice-chip" id="j-new-tag">+ New tag</button>' +
      '</div>' +
      '<div class="j-field" style="margin-top:14px"><label for="j-entry-date">Entry date</label>' +
        '<input id="j-entry-date" type="date" value="' + dateValue + '" max="' + new Date().toISOString().slice(0, 10) + '"></div>' +
      '<div class="j-section-label">Rating</div>' +
      '<div class="j-star-input" id="j-entry-stars"></div>' +
      '<div class="j-field" style="margin-top:14px"><label for="j-entry-notes">Notes</label>' +
        '<textarea id="j-entry-notes" rows="4" placeholder="Cedar, honey, citrus… when, where, who with.">' + esc(entryForm.notes) + '</textarea></div>' +
      '<div class="j-field"><label for="j-entry-context">Place &amp; context</label>' +
        '<button class="j-context-add" id="j-context-add" type="button"' + (entryForm.contextNotes ? ' style="display:none"' : '') + '>' +
          '<span class="j-context-add-title">Add place note</span>' +
          '<span class="j-context-add-sub">Save where you had it or any extra context</span></button>' +
        '<textarea id="j-entry-context" rows="3" placeholder="Where were you, who were you with, what stood out?"' +
          (entryForm.contextNotes ? '' : ' style="display:none"') + '>' + esc(entryForm.contextNotes) + '</textarea></div>' +
      (editing
        ? ''
        : '<div class="j-toggle-row" id="j-reduce-row" style="margin-top:8px; display:none">' +
            '<div><div class="j-toggle-label">Reduce box count</div>' +
            '<div class="j-toggle-sub" id="j-reduce-sub"></div></div>' +
            '<button class="j-switch" id="j-reduce-toggle"></button></div>' +
          '<div id="j-reduce-stepper" style="display:none; padding: 4px 0 10px">' +
            '<div class="j-section-label" style="margin:0 0 8px">How many to remove</div>' +
            '<div class="j-stepper">' +
              '<button id="j-reduce-minus">−</button>' +
              '<span class="j-stepper-value" id="j-reduce-count">1</span>' +
              '<button id="j-reduce-plus">+</button>' +
            '</div></div>') +
      '<div style="margin-top:20px"><button class="j-btn j-btn-primary" id="j-entry-save">' +
        (editing ? 'Save changes' : 'Save entry') + '</button></div>'
    );
  };

  var wireEntrySheet = function (region) {
    region.querySelector('[data-close]').addEventListener('click', closeSheet);

    var nameInput = region.querySelector('#j-entry-name');
    var brandInput = region.querySelector('#j-entry-brand');
    var suggestList = region.querySelector('#j-entry-suggest');
    var starsWrap = region.querySelector('#j-entry-stars');
    var reduceRow = region.querySelector('#j-reduce-row');
    var reduceToggle = region.querySelector('#j-reduce-toggle');
    var reduceStepper = region.querySelector('#j-reduce-stepper');
    var reduceSub = region.querySelector('#j-reduce-sub');
    var reduceCount = region.querySelector('#j-reduce-count');

    var renderStars = function () {
      starsWrap.innerHTML = '';
      for (var i = 1; i <= 5; i++) {
        var btn = document.createElement('button');
        btn.setAttribute('data-star', i);
        btn.innerHTML =
          '<span style="color:' + (i <= entryForm.rating ? 'var(--j-accent)' : 'var(--j-star-off)') + '">' +
          svg(ICONS.star, 30) + '</span>';
        starsWrap.appendChild(btn);
      }
    };
    renderStars();
    starsWrap.addEventListener('click', function (e) {
      var star = e.target.closest('[data-star]');
      if (!star) return;
      var value = Number(star.getAttribute('data-star'));
      entryForm.rating = entryForm.rating === value ? 0 : value;
      renderStars();
    });

    var syncReduceUI = function () {
      if (!reduceRow) return;
      var record = entryForm.linkedItemRecord;
      var available = record && Number(record.item.quantity || 0) > 0;
      reduceRow.style.display = available ? 'flex' : 'none';
      if (available) {
        var qty = Number(record.item.quantity || 0);
        var after = Math.max(0, qty - (entryForm.reduceQty ? entryForm.reduceQtyCount : 0));
        reduceSub.textContent =
          'Use this when the entry means you had some: ' + qty + ' → ' + after +
          ' in ' + (record.boxName || 'this box');
        reduceToggle.classList.toggle('on', entryForm.reduceQty);
        reduceStepper.style.display = entryForm.reduceQty ? 'block' : 'none';
        reduceCount.textContent = entryForm.reduceQtyCount;
      } else {
        entryForm.reduceQty = false;
      }
    };
    syncReduceUI();
    if (reduceToggle) {
      reduceToggle.addEventListener('click', function () {
        entryForm.reduceQty = !entryForm.reduceQty;
        syncReduceUI();
      });
      region.querySelector('#j-reduce-minus').addEventListener('click', function () {
        entryForm.reduceQtyCount = Math.max(1, entryForm.reduceQtyCount - 1);
        syncReduceUI();
      });
      region.querySelector('#j-reduce-plus').addEventListener('click', function () {
        var max = entryForm.linkedItemRecord
          ? Number(entryForm.linkedItemRecord.item.quantity || 1)
          : 99;
        entryForm.reduceQtyCount = Math.min(max, entryForm.reduceQtyCount + 1);
        syncReduceUI();
      });
    }

    // Autocomplete from inventory: pick an item to link it (enables
    // quantity decrement + inherits brand/strength snapshot).
    var renderSuggestions = function () {
      var q = nameInput.value.trim().toLowerCase();
      if (!q || q.length < 2 || state.editingEntry) {
        suggestList.classList.remove('visible');
        return;
      }
      var matches = state.inventoryRecords.filter(function (record) {
        return record.item && (record.item.name || '').toLowerCase().indexOf(q) !== -1;
      }).slice(0, 6);
      if (matches.length === 0) {
        suggestList.classList.remove('visible');
        return;
      }
      suggestList.innerHTML = matches.map(function (record, i) {
        var rich = L.resolveCigarData(record.item);
        return (
          '<button type="button" data-suggest-index="' + i + '">' + esc(record.item.name) +
          '<span class="j-suggest-sub">' + esc([rich.maker, record.boxName].filter(Boolean).join(' · ')) + '</span></button>'
        );
      }).join('');
      suggestList.classList.add('visible');
      suggestList.querySelectorAll('[data-suggest-index]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var record = matches[Number(btn.getAttribute('data-suggest-index'))];
          entryForm.linkedItemRecord = record;
          var rich = L.resolveCigarData(record.item);
          nameInput.value = record.item.name;
          if (!brandInput.value) brandInput.value = rich.maker || '';
          suggestList.classList.remove('visible');
          syncReduceUI();
        });
      });
    };
    nameInput.addEventListener('input', function () {
      entryForm.linkedItemRecord = null;
      syncReduceUI();
      renderSuggestions();
    });
    nameInput.addEventListener('blur', function () {
      setTimeout(function () { suggestList.classList.remove('visible'); }, 180);
    });

    // "Use Last Entry" prefill (rating, notes, tags, context — not identity)
    var useLastBtn = region.querySelector('#j-use-last');
    if (useLastBtn && state.detailGroup) {
      useLastBtn.addEventListener('click', function () {
        var last = null;
        (state.detailGroup.entries || []).forEach(function (e) {
          if (!e || e.synthetic || e.initialRating) return;
          if (!last || (e.occurredAt || 0) > (last.occurredAt || 0)) last = e;
        });
        if (!last) return;
        entryForm.rating = Math.max(0, Math.min(5, Number(last.rating || 0)));
        entryForm.tagIds = (last.tagIds || []).slice();
        region.querySelector('#j-entry-notes').value = last.notes || '';
        var ctxField = region.querySelector('#j-entry-context');
        ctxField.value = last.contextNotes || '';
        if (last.contextNotes) {
          ctxField.style.display = '';
          region.querySelector('#j-context-add').style.display = 'none';
        }
        renderStars();
        syncTags();
        showToast('Prefilled from your last entry');
      });
    }

    // Past-brand suggestions (from journal history + inventory)
    var pastBrandsWrap = region.querySelector('#j-past-brands');
    var pastBrandsRow = region.querySelector('#j-past-brands-row');
    var allBrands = (function () {
      var seen = {};
      var list = [];
      (state.collectionUI.journalEntries || []).forEach(function (e) {
        var b = (e.snapshot && e.snapshot.brand || '').trim();
        if (b && !seen[b.toLowerCase()]) { seen[b.toLowerCase()] = 1; list.push(b); }
      });
      state.inventoryRecords.forEach(function (record) {
        var b = ((L.resolveCigarData(record.item) || {}).maker || '').trim();
        if (b && !seen[b.toLowerCase()]) { seen[b.toLowerCase()] = 1; list.push(b); }
      });
      return list;
    })();
    var brandFocused = false;
    var syncPastBrands = function () {
      var q = brandInput.value.trim().toLowerCase();
      var matches = allBrands.filter(function (b) {
        var lower = b.toLowerCase();
        return lower !== q && (!q || lower.indexOf(q) !== -1);
      }).slice(0, 5);
      if (!brandFocused || matches.length === 0) {
        pastBrandsWrap.style.display = 'none';
        return;
      }
      pastBrandsRow.innerHTML = matches.map(function (b) {
        return '<button class="j-choice-chip" type="button" data-brand="' + esc(b) + '">' + esc(b) + '</button>';
      }).join('');
      pastBrandsWrap.style.display = '';
    };
    brandInput.addEventListener('focus', function () { brandFocused = true; syncPastBrands(); });
    brandInput.addEventListener('input', syncPastBrands);
    brandInput.addEventListener('blur', function () {
      setTimeout(function () { brandFocused = false; syncPastBrands(); }, 180);
    });
    pastBrandsRow.addEventListener('mousedown', function (e) { e.preventDefault(); });
    pastBrandsRow.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-brand]');
      if (!chip) return;
      brandInput.value = chip.getAttribute('data-brand');
      brandFocused = false;
      syncPastBrands();
    });

    // Collapsed "Add place note" → expand context textarea
    var contextAdd = region.querySelector('#j-context-add');
    contextAdd.addEventListener('click', function () {
      contextAdd.style.display = 'none';
      var field = region.querySelector('#j-entry-context');
      field.style.display = '';
      field.focus();
    });

    // Tags
    var tagWrap = region.querySelector('#j-entry-tags');
    var syncTags = function () {
      tagWrap.querySelectorAll('[data-tag]').forEach(function (el) {
        var on = entryForm.tagIds.indexOf(el.getAttribute('data-tag')) !== -1;
        el.classList.toggle('selected', on);
        // Selected chips take the tag's own color, matching the app's pills.
        var color = el.getAttribute('data-color');
        if (on && color) {
          el.style.background = color;
          el.style.borderColor = 'transparent';
          el.style.color = '#121212';
        } else {
          el.style.background = '';
          el.style.borderColor = '';
          el.style.color = '';
        }
      });
    };
    syncTags();
    tagWrap.addEventListener('click', function (e) {
      var tagBtn = e.target.closest('[data-tag]');
      if (tagBtn) {
        var id = tagBtn.getAttribute('data-tag');
        var at = entryForm.tagIds.indexOf(id);
        if (at === -1) {
          if (entryForm.tagIds.length >= L.MAX_TAGS_PER_ENTRY) {
            showToast('Up to ' + L.MAX_TAGS_PER_ENTRY + ' tags per entry.', true);
            return;
          }
          entryForm.tagIds.push(id);
        } else {
          entryForm.tagIds.splice(at, 1);
        }
        syncTags();
        return;
      }
      if (e.target.closest('#j-new-tag')) {
        var label = prompt('New tag name');
        if (!label || !label.trim()) return;
        label = label.trim();
        var library = state.collectionUI.tagLibrary;
        if (library.length >= L.MAX_TAG_LIBRARY_SIZE) {
          showToast('Tag library is full.', true);
          return;
        }
        var existing = library.find(function (t) {
          return (t.label || '').toLowerCase() === label.toLowerCase();
        });
        var tag = existing;
        if (!tag) {
          // Same shape the app's TagPickerSheet creates: no updatedAt,
          // color defaults to the first palette swatch.
          tag = { id: L.generateTagId(), label: label, color: L.DEFAULT_TAG_COLOR, createdAt: Date.now() };
          library.push(tag);
          queueSave();
        }
        if (entryForm.tagIds.indexOf(tag.id) === -1) {
          if (entryForm.tagIds.length >= L.MAX_TAGS_PER_ENTRY) {
            showToast('Up to ' + L.MAX_TAGS_PER_ENTRY + ' tags per entry.', true);
            return;
          }
          entryForm.tagIds.push(tag.id);
        }
        var newBtn = document.createElement('button');
        newBtn.className = 'j-choice-chip selected';
        newBtn.setAttribute('data-tag', tag.id);
        newBtn.setAttribute('data-color', tag.color || '');
        newBtn.textContent = tag.label;
        tagWrap.insertBefore(newBtn, tagWrap.querySelector('#j-new-tag'));
        syncTags();
      }
    });

    region.querySelector('#j-entry-save').addEventListener('click', function () {
      var name = nameInput.value.trim();
      if (!name) {
        showToast('Give the entry a cigar name.', true);
        return;
      }
      var dateStr = region.querySelector('#j-entry-date').value;
      var occurredAt = dateStr
        ? new Date(dateStr + 'T12:00:00').getTime()
        : Date.now();
      var notes = region.querySelector('#j-entry-notes').value;
      var contextNotes = region.querySelector('#j-entry-context').value;

      if (state.editingEntry) {
        var entry = state.collectionUI.journalEntries.find(function (e) {
          return e.id === state.editingEntry.id;
        });
        if (entry) {
          entry.rating = Math.max(0, Math.min(5, entryForm.rating | 0));
          entry.notes = notes.trim();
          entry.contextNotes = contextNotes.trim();
          entry.occurredAt = occurredAt;
          entry.tagIds = entryForm.tagIds.slice();
          entry.updatedAt = Date.now();
          entry.snapshot = Object.assign({}, entry.snapshot, {
            name: name,
            brand: brandInput.value.trim(),
          });
        }
      } else {
        var record = entryForm.linkedItemRecord;
        var newEntry = L.createJournalEntry({
          item: record ? record.item : null,
          boxName: record ? record.boxName : '',
          storageUnitId: record ? record.storageUnitId : null,
          storageUnitName: record ? record.boxName : '',
          rating: entryForm.rating,
          notes: notes,
          contextNotes: contextNotes,
          tagIds: entryForm.tagIds,
          occurredAt: occurredAt,
          reducedQty: entryForm.reduceQty,
          reducedQtyCount: entryForm.reduceQtyCount,
          snapshotName: name,
          snapshotBrand: brandInput.value.trim(),
        });
        state.collectionUI.journalEntries.push(newEntry);

        // Mirror the app: optional inventory decrement rides along with
        // the entry but is an independent PATCH.
        if (entryForm.reduceQty && record) {
          var newQty = Math.max(0, Number(record.item.quantity || 0) - entryForm.reduceQtyCount);
          KlaroAPI.updateCollection(record.item.id, { quantity: newQty })
            .then(function () {
              record.item.quantity = newQty;
              renderList();
            })
            .catch(function (err) {
              if (err.status === 401) return handleUnauthorized();
              showToast('Entry saved, but the box quantity could not be updated.', true);
            });
        }
      }

      state.detailGroup = null;
      closeSheet();
      renderJournal();
      showToast(state.editingEntry ? 'Entry updated' : 'Entry added');
      state.editingEntry = null;
      queueSave();
    });
  };

  // — Tasting profile (port of the app's TastingProfileCard) —
  // Six flavor families + three palate sliders over the same 17-label
  // flavours array the app stores on cigar profiles. Values are 0–100
  // internally, displayed 0–10.

  var FLAVOR_FAMILIES = [
    { key: 'earth', label: 'Earth & Mineral', shortLabel: 'Earth', color: '#72D6A1', labels: ['Earthy', 'Leather'] },
    { key: 'wood', label: 'Wood & Plant', shortLabel: 'Wood', color: '#9F8454', labels: ['Woody', 'Herbal'] },
    { key: 'spice', label: 'Spice & Pepper', shortLabel: 'Spice', color: '#D99477', labels: ['Spicy', 'Bitter'] },
    { key: 'sweet', label: 'Sweet & Baked', shortLabel: 'Sweet', color: '#D6A85E', labels: ['Sweet', 'Vanilla', 'Toffee'] },
    { key: 'cocoa', label: 'Nut / Cocoa / Coffee', shortLabel: 'Cocoa', color: '#8F6B4C', labels: ['Nutty', 'Chocolate'] },
    { key: 'fruit', label: 'Fruit & Floral', shortLabel: 'Fruit', color: '#B85F72', labels: ['Dark Fruit', 'Tropical Fruit'] },
  ];

  var PALATE_SLIDERS = [
    { key: 'body', label: 'Body', left: 'Mild', right: 'Full', flavourLabel: 'Body' },
    { key: 'finish', label: 'Finish', left: 'Short', right: 'Long', flavourLabel: 'Linger/Finish' },
    { key: 'texture', label: 'Texture', left: 'Dry', right: 'Oily', flavourLabel: 'Mouthfeel' },
  ];

  var normalizeScore = function (value) {
    return Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
  };
  var toDisplayScore = function (value) {
    return Math.max(0, Math.min(10, Math.round(normalizeScore(value) / 10)));
  };
  var fromDisplayScore = function (value) {
    return normalizeScore(Math.round(Number(value) || 0) * 10);
  };
  var valueForLabel = function (flavours, label) {
    var found = (flavours || []).find(function (f) { return f && f.label === label; });
    return normalizeScore(found && found.value);
  };
  var familyScore = function (flavours, family) {
    var values = family.labels.map(function (label) {
      return valueForLabel(flavours, label);
    }).filter(function (v) { return v > 0; });
    if (values.length === 0) return 0;
    return normalizeScore(values.reduce(function (sum, v) { return sum + v; }, 0) / values.length);
  };
  var updateFlavourLabels = function (flavours, labels, value) {
    return flavours.map(function (f) {
      return labels.indexOf(f.label) !== -1 ? { label: f.label, value: normalizeScore(value) } : f;
    });
  };
  var updateSingleFlavour = function (flavours, label, value) {
    var nextValue = normalizeScore(value);
    var found = false;
    var next = flavours.map(function (f) {
      if (f.label !== label) return f;
      found = true;
      return { label: f.label, value: nextValue };
    });
    if (!found) next.push({ label: label, value: nextValue });
    return next;
  };
  var descriptorForScore = function (value, labels) {
    var score = toDisplayScore(value);
    if (score <= 0) return 'Not set';
    if (score <= 3) return labels[0];
    if (score <= 7) return labels[1];
    return labels[2];
  };

  // Radar chart — same geometry as the app's FlavorRadarChart, as an SVG string.
  var radarSvg = function (flavours, size) {
    size = size || 300;
    var center = size / 2;
    var radius = size * 0.35;
    var families = FLAVOR_FAMILIES.map(function (family) {
      return {
        key: family.key,
        shortLabel: family.shortLabel,
        color: family.color,
        score: familyScore(flavours, family),
      };
    });
    var polar = function (index, scale) {
      var angle = (Math.PI * 2 * index) / families.length - Math.PI / 2;
      return {
        x: center + Math.cos(angle) * radius * scale,
        y: center + Math.sin(angle) * radius * scale,
      };
    };
    var parts = [];
    [0.25, 0.5, 0.75, 1].forEach(function (scale) {
      parts.push('<circle cx="' + center + '" cy="' + center + '" r="' + (radius * scale) +
        '" stroke="rgba(255,255,255,0.12)" stroke-width="1" fill="none"/>');
    });
    families.forEach(function (family, i) {
      var end = polar(i, 1);
      var label = polar(i, 1.26);
      parts.push('<line x1="' + center + '" y1="' + center + '" x2="' + end.x + '" y2="' + end.y +
        '" stroke="rgba(255,255,255,0.10)" stroke-width="1"/>');
      parts.push('<text x="' + label.x + '" y="' + label.y + '" fill="' +
        (family.score > 0 ? family.color : 'rgba(255,255,255,0.42)') +
        '" font-size="11" font-weight="700" text-anchor="middle" dominant-baseline="middle">' +
        esc(family.shortLabel) + '</text>');
    });
    var active = families.filter(function (f) { return toDisplayScore(f.score) > 0; });
    if (active.length >= 3) {
      var points = active.map(function (family) {
        var i = families.findIndex(function (f) { return f.key === family.key; });
        var p = polar(i, family.score / 100);
        return p.x + ',' + p.y;
      }).join(' ');
      parts.push('<polygon points="' + points +
        '" stroke="#5BCB90" stroke-width="2" stroke-linejoin="round" fill="#5BCB90" fill-opacity="0.18"/>');
    }
    families.forEach(function (family, i) {
      var value = toDisplayScore(family.score);
      if (value <= 0) return;
      var p = polar(i, family.score / 100);
      parts.push('<circle cx="' + p.x + '" cy="' + p.y + '" r="4" fill="' + family.color +
        '" stroke="#FFFFFF" stroke-opacity="0.9" stroke-width="1"/>');
      parts.push('<text x="' + p.x + '" y="' + (p.y - 10) +
        '" fill="#FFFFFF" font-size="10" font-weight="900" text-anchor="middle" dominant-baseline="middle">' +
        value + '</text>');
    });
    return '<svg width="100%" viewBox="0 0 ' + size + ' ' + size + '" xmlns="http://www.w3.org/2000/svg">' +
      parts.join('') + '</svg>';
  };

  // 10-segment slider — click or drag across segments, like the app's rail.
  // `id` encodes the write target: family:<key> | label:<label> | palate:<key>
  var profSliderHtml = function (id, label, color, left, right) {
    var segs = '';
    for (var i = 1; i <= 10; i++) {
      segs += '<button type="button" class="j-prof-seg" data-seg="' + i + '"></button>';
    }
    return (
      '<div class="j-prof-slider" data-prof-slider="' + esc(id) + '" data-prof-color="' + esc(color) + '">' +
        '<div class="j-prof-slider-label">' + esc(label) + '</div>' +
        '<div class="j-prof-rail">' + segs + '</div>' +
        (left || right
          ? '<div class="j-prof-range"><span>' + esc(left || '') + '</span><span>' + esc(right || '') + '</span></div>'
          : '') +
      '</div>'
    );
  };

  var tastingProfileHtml = function () {
    return (
      '<div class="j-section-label" style="margin-top:14px">Profile</div>' +
      '<div id="j-prof">' +
        '<div class="j-prof-title">Item Profile</div>' +
        '<div class="j-prof-caption" id="j-prof-caption">No section scores yet</div>' +
        '<div class="j-prof-radar" id="j-prof-radar"></div>' +
        '<div class="j-prof-modes">' +
          '<button type="button" class="j-prof-mode active" data-prof-mode="simple">Simple</button>' +
          '<button type="button" class="j-prof-mode" data-prof-mode="detailed">Detailed</button>' +
        '</div>' +
        '<div id="j-prof-simple">' +
          FLAVOR_FAMILIES.map(function (family) {
            return profSliderHtml('family:' + family.key, family.label, family.color);
          }).join('') +
        '</div>' +
        '<div id="j-prof-detailed" style="display:none">' +
          FLAVOR_FAMILIES.map(function (family) {
            return (
              '<details class="j-prof-family">' +
                '<summary><span>' + esc(family.label) +
                  '</span><span class="j-prof-family-meta" data-family-meta="' + family.key + '"></span></summary>' +
                family.labels.map(function (label) {
                  return profSliderHtml('label:' + label, label, family.color);
                }).join('') +
              '</details>'
            );
          }).join('') +
        '</div>' +
        '<div class="j-prof-palate">' +
          '<div class="j-prof-eyebrow">Palate Overview</div>' +
          '<div class="j-prof-palate-summary" id="j-prof-palate-summary"></div>' +
          PALATE_SLIDERS.map(function (slider) {
            return profSliderHtml('palate:' + slider.key, slider.label, '#5BCB90', slider.left, slider.right);
          }).join('') +
        '</div>' +
      '</div>'
    );
  };

  // Wires the profile section. getFlavours/setFlavours close over the form.
  var wireTastingProfile = function (region, getFlavours, setFlavours) {
    var container = region.querySelector('#j-prof');
    if (!container) return { sync: function () {} };

    var valueForSlider = function (id) {
      var flavours = getFlavours();
      var kind = id.split(':')[0];
      var rest = id.slice(kind.length + 1);
      if (kind === 'family') {
        var family = FLAVOR_FAMILIES.find(function (f) { return f.key === rest; });
        return family ? familyScore(flavours, family) : 0;
      }
      if (kind === 'palate') {
        var slider = PALATE_SLIDERS.find(function (s) { return s.key === rest; });
        return slider ? valueForLabel(flavours, slider.flavourLabel) : 0;
      }
      return valueForLabel(flavours, rest);
    };

    var applySeg = function (sliderEl, segValue) {
      var id = sliderEl.getAttribute('data-prof-slider');
      var kind = id.split(':')[0];
      var rest = id.slice(kind.length + 1);
      var value = fromDisplayScore(segValue);
      var flavours = getFlavours();
      if (kind === 'family') {
        var family = FLAVOR_FAMILIES.find(function (f) { return f.key === rest; });
        if (family) setFlavours(updateFlavourLabels(flavours, family.labels, value));
      } else if (kind === 'palate') {
        var slider = PALATE_SLIDERS.find(function (s) { return s.key === rest; });
        if (slider) setFlavours(updateSingleFlavour(flavours, slider.flavourLabel, value));
      } else {
        setFlavours(updateSingleFlavour(flavours, rest, value));
      }
      sync();
    };

    var sync = function () {
      container.querySelectorAll('[data-prof-slider]').forEach(function (sliderEl) {
        var color = sliderEl.getAttribute('data-prof-color');
        var display = toDisplayScore(valueForSlider(sliderEl.getAttribute('data-prof-slider')));
        sliderEl.querySelectorAll('.j-prof-seg').forEach(function (seg, i) {
          var segValue = i + 1;
          var filled = segValue <= display;
          // App: an unset slider still marks its first segment with a "0".
          var selected = segValue === display || (display === 0 && i === 0);
          seg.style.background = filled ? color + '33' : '';
          seg.style.borderColor = selected ? color : '';
          seg.style.color = selected ? color : 'transparent';
          seg.textContent = selected ? String(display) : '';
        });
      });
      FLAVOR_FAMILIES.forEach(function (family) {
        var meta = container.querySelector('[data-family-meta="' + family.key + '"]');
        if (meta) meta.textContent = toDisplayScore(familyScore(getFlavours(), family)) + '/10';
      });
      var caption = container.querySelector('#j-prof-caption');
      if (caption) caption.style.display = L.hasFlavourSignal(getFlavours()) ? 'none' : '';
      var palateSummary = container.querySelector('#j-prof-palate-summary');
      if (palateSummary) {
        palateSummary.textContent = PALATE_SLIDERS.map(function (slider) {
          return slider.label + ' ' + descriptorForScore(
            valueForLabel(getFlavours(), slider.flavourLabel),
            [slider.left, 'Medium', slider.right]
          );
        }).join('  •  ');
      }
      var radar = container.querySelector('#j-prof-radar');
      if (radar) radar.innerHTML = radarSvg(getFlavours(), 300);
    };

    container.addEventListener('click', function (e) {
      var mode = e.target.closest('[data-prof-mode]');
      if (mode) {
        var next = mode.getAttribute('data-prof-mode');
        container.querySelectorAll('[data-prof-mode]').forEach(function (b) {
          b.classList.toggle('active', b === mode);
        });
        container.querySelector('#j-prof-simple').style.display = next === 'simple' ? '' : 'none';
        container.querySelector('#j-prof-detailed').style.display = next === 'detailed' ? '' : 'none';
        return;
      }
      var seg = e.target.closest('.j-prof-seg');
      if (seg) applySeg(seg.closest('[data-prof-slider]'), Number(seg.getAttribute('data-seg')));
    });
    // Drag across a rail paints segments, like the app's pan gesture.
    container.addEventListener('pointerover', function (e) {
      if (e.buttons !== 1) return;
      var seg = e.target.closest('.j-prof-seg');
      if (seg) applySeg(seg.closest('[data-prof-slider]'), Number(seg.getAttribute('data-seg')));
    });

    sync();
    return { sync: sync };
  };

  // — Cigar photos (web-only; Apple's rules keep them out of the app) —
  // snapshot.image = { url?, key?, source: 'store'|'order'|'upload' }.
  // 'store'/'order' images are durable Shopify CDN URLs. 'upload' images
  // live on Klaro's S3 as a stable key; keys resolve to short-lived URLs
  // through POST /journal/photo_urls once per load (photoUrlCache).

  var photoUrlCache = {};

  var imageUrlFor = function (image) {
    if (!image || typeof image !== 'object') return '';
    if (image.url) return image.url;
    if (image.key) return photoUrlCache[image.key] || '';
    return '';
  };

  // Shopify CDN resizes via a width param — keep thumbs small.
  var thumbUrl = function (url, width) {
    if (!url || url.indexOf('cdn.shopify.com') === -1) return url;
    var clean = url.replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    return clean + (clean.indexOf('?') === -1 ? '?' : '&') + 'width=' + (width || 240);
  };

  var groupImage = function (group) {
    if (!group) return null;
    var snapImage = group.snapshot && group.snapshot.image;
    if (imageUrlFor(snapImage)) return snapImage;
    // Ordered cigars fall back to the product shot from the live order
    // match, so entries that predate photo support need no migration.
    var history = orderHistoryForGroup(group);
    if (history && history.image) return { url: history.image, source: 'order' };
    return null;
  };

  // Batch-resolve uploaded photos' S3 keys to fresh URLs (once per load —
  // presigned URLs expire, keys don't). Failure just means placeholder cards.
  var resolveUploadPhotoUrls = function () {
    var keys = [];
    ((state.collectionUI && state.collectionUI.journalEntries) || []).forEach(function (entry) {
      var image = entry && entry.snapshot && entry.snapshot.image;
      if (image && image.key && !photoUrlCache[image.key] && keys.indexOf(image.key) === -1) {
        keys.push(image.key);
      }
    });
    if (keys.length === 0) return;
    KlaroAPI.apiFetch('/journal/photo_urls', { method: 'POST', body: { keys: keys } })
      .then(function (body) {
        var urls = (body && body.urls) || {};
        var found = false;
        keys.forEach(function (key) {
          if (urls[key]) { photoUrlCache[key] = urls[key]; found = true; }
        });
        if (found && state.view === 'journal') renderList();
      })
      .catch(function () { /* photos are progressive enhancement */ });
  };

  // Klaro catalog search — Shopify predictive search on the store's own
  // origin, no auth needed. The dev harness supplies CONFIG.catalog (array)
  // and gets local substring matching instead.
  var catalogCache = {};
  var searchCatalog = function (query) {
    var q = String(query || '').trim();
    if (q.length < 2) return Promise.resolve([]);
    var cacheKey = q.toLowerCase();
    if (catalogCache[cacheKey]) return Promise.resolve(catalogCache[cacheKey]);

    if (Array.isArray(CONFIG.catalog)) {
      var words = cacheKey.split(/\s+/);
      var local = CONFIG.catalog.filter(function (p) {
        var hay = ((p.title || '') + ' ' + (p.vendor || '')).toLowerCase();
        return words.every(function (w) { return hay.indexOf(w) !== -1; });
      }).slice(0, 8);
      catalogCache[cacheKey] = local;
      return Promise.resolve(local);
    }

    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=8';
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (body) {
        var products = (body && body.resources && body.resources.results &&
          body.resources.results.products) || [];
        var mapped = products.map(function (p) {
          return {
            title: p.title || '',
            vendor: p.vendor || '',
            // NOTE: predictive search price = the MINIMUM variant price
            // (usually the 5-pack). Full pack pricing comes from
            // fetchCatalogPrices(handle) when a product is linked.
            price: p.price != null ? String(p.price) : '',
            handle: p.handle || '',
            type: p.type || '',
            image: (p.featured_image && p.featured_image.url) || p.image || '',
          };
        }).filter(function (p) {
          return p.title && p.image && (p.type || '').toLowerCase().indexOf('cigar') !== -1;
        });
        catalogCache[cacheKey] = mapped;
        return mapped;
      })
      .catch(function () { return []; });
  };

  // Per-stick pricing. The store's premium singles scaffolding uses a
  // "Pack" option with a variant literally titled "Single" (e.g. Opus X) —
  // when that exists it's the real price. Otherwise derive one per size
  // from the 5-pack (÷5), marked "~" as approximate. Singles list first.
  var SINGLE_LABEL = /\bsingle\b/i;
  var FIVE_PACK_LABEL = /5\s*pack/i;
  var priceSizePrefix = function (label) {
    var parts = String(label || '').split(' / ');
    return parts.length > 1 ? parts.slice(0, -1).join(' / ') : '';
  };
  var withDerivedSingles = function (prices) {
    if (!Array.isArray(prices) || prices.length === 0) return prices;
    var singles = prices.filter(function (p) { return SINGLE_LABEL.test(p.label); });
    var packs = prices.filter(function (p) { return !SINGLE_LABEL.test(p.label); });
    var sized = {};
    singles.forEach(function (p) { sized[priceSizePrefix(p.label)] = 1; });
    var derived = [];
    packs.forEach(function (p) {
      if (!FIVE_PACK_LABEL.test(p.label)) return;
      var size = priceSizePrefix(p.label);
      if (sized[size]) return;
      var amount = Number(String(p.price).replace(/[^0-9.]/g, ''));
      if (!(amount > 0)) return;
      derived.push({
        label: (size ? size + ' / ' : '') + 'Single',
        price: '~' + formatPrice(amount / 5),
        approx: true,
      });
      sized[size] = 1;
    });
    return singles.concat(derived, packs);
  };

  // All pack prices for a catalog product ("Gran Toro / 5 Pack" $40.50,
  // "Full Box" $217.81, …) via the storefront product JSON. Predictive
  // search only exposes the minimum variant price. Dev harness products
  // carry a prices array directly.
  var catalogPricesCache = {};
  var fetchCatalogPrices = function (product) {
    if (!product) return Promise.resolve(null);
    if (Array.isArray(product.prices)) return Promise.resolve(withDerivedSingles(product.prices));
    var handle = product.handle;
    if (!handle) return Promise.resolve(null);
    if (Array.isArray(CONFIG.catalog)) { // dev harness — no /products/*.js
      var local = CONFIG.catalog.find(function (p) { return p.handle === handle; });
      return Promise.resolve(local && Array.isArray(local.prices) ? withDerivedSingles(local.prices) : null);
    }
    if (catalogPricesCache[handle]) return Promise.resolve(catalogPricesCache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (body) {
        var variants = (body && body.variants) || [];
        var prices = variants.map(function (v) {
          return {
            label: v.title === 'Default Title' ? '' : (v.title || ''),
            price: formatPrice((Number(v.price) || 0) / 100), // cents → "$40.50"
          };
        }).filter(function (v) { return v.price; });
        if (prices.length === 0) return null;
        prices = withDerivedSingles(prices);
        catalogPricesCache[handle] = prices;
        return prices;
      })
      .catch(function () { return null; });
  };

  // The store's own tasting description for a linked catalog product —
  // fetched live from the product JSON when a cigar detail opens (only the
  // handle is persisted; the copy stays fresh and the blob stays small).
  var catalogDescriptionCache = {};
  var fetchCatalogDescription = function (handle) {
    if (!handle) return Promise.resolve('');
    if (catalogDescriptionCache[handle] != null) {
      return Promise.resolve(catalogDescriptionCache[handle]);
    }
    if (Array.isArray(CONFIG.catalog)) {
      var local = CONFIG.catalog.find(function (p) { return p.handle === handle; });
      var text = (local && local.description) || '';
      catalogDescriptionCache[handle] = text;
      return Promise.resolve(text);
    }
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (body) {
        var holder = document.createElement('div');
        holder.innerHTML = (body && body.description) || '';
        var text = (holder.textContent || '').replace(/\s+/g, ' ').trim();
        catalogDescriptionCache[handle] = text;
        return text;
      })
      .catch(function () { return ''; });
  };

  // Downscale a photo client-side (longest edge capped) before upload —
  // journal thumbs never need the original.
  var downscalePhoto = function (file, maxDim) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        URL.revokeObjectURL(url);
        var scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        var canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        reject(new Error('That file does not look like an image.'));
      };
      img.src = url;
    });
  };

  // — Add / edit cigar (stick) sheet —
  // The app splits "add a stick" (ItemDetailForm: identity + details) from
  // "record an entry" (AddJournalEntrySheet). This is the web's stick form;
  // with no inventory item to live on it persists as a seed journal entry
  // (createStickSeedEntry) whose snapshot carries the cigar details.

  var stickForm = null;

  var findStickSeedEntry = function (group) {
    var entries = L.entriesForJournalGroup(state.collectionUI.journalEntries, group);
    return entries.find(function (e) {
      return e && e.initialRating === true && e.synthetic !== true;
    }) || null;
  };

  var allJournalGroups = function () {
    return L.buildJournalCigarGroups({
      journalEntries: state.collectionUI.journalEntries,
      inventoryRecords: state.inventoryRecords,
    });
  };

  // A leading "~" marks an approximated price (derived single-stick cost)
  // and survives reformatting.
  var formatPrice = function (value) {
    var raw = String(value == null ? '' : value).trim();
    if (!raw) return '';
    var approx = raw.charAt(0) === '~';
    if (approx) raw = raw.slice(1).trim();
    if (raw.charAt(0) === '$') return (approx ? '~' : '') + raw;
    var num = Number(raw.replace(/[^0-9.]/g, ''));
    return isFinite(num) && num > 0 ? (approx ? '~' : '') + '$' + num.toFixed(2) : '';
  };

  // Distinct past values for a stick field, from journal snapshots +
  // inventory — mirrors the app's "Past brands / origins / …" chips.
  var pastStickValues = function (field) {
    var seen = {};
    var list = [];
    var push = function (value) {
      var v = String(value || '').trim();
      if (!v) return;
      var k = v.toLowerCase();
      if (seen[k]) return;
      seen[k] = 1;
      list.push(v);
    };
    (state.collectionUI.journalEntries || []).forEach(function (e) {
      var s = (e && e.snapshot) || {};
      push(field === 'brand' ? s.brand : s[field]);
    });
    state.inventoryRecords.forEach(function (record) {
      var rich = L.resolveCigarData(record.item) || {};
      push(field === 'brand' ? rich.maker : rich[field]);
    });
    return list;
  };

  var stickSuggestFieldHtml = function (field, label, placeholder, value) {
    return (
      '<div class="j-field"><label for="j-stick-' + field + '">' + label + '</label>' +
        '<input id="j-stick-' + field + '" type="text" placeholder="' + placeholder + '" autocomplete="off" value="' + esc(value) + '">' +
        '<div class="j-past-values" id="j-stick-' + field + '-past" style="display:none">' +
          '<div class="j-section-label" style="margin:8px 0 6px">Past ' + label.toLowerCase() + 's</div>' +
          '<div class="j-chip-row" data-past-row="' + field + '"></div>' +
        '</div></div>'
    );
  };

  var stickSheetHtml = function () {
    var editing = state.editingStick;
    var snap = (editing && editing.snapshot) || {};
    var seed = editing ? findStickSeedEntry(editing) : null;
    // A wishlist row seeds the Add form — identity + photo arrive filled,
    // pack prices follow once the sheet wires up (linked by store handle).
    var prefill = (!editing && state.stickPrefill) || null;
    stickForm = {
      name: snap.name || (prefill && prefill.title) || '',
      brand: snap.brand || (prefill && prefill.vendor) || '',
      origin: snap.origin || '',
      strength: snap.strength || '',
      length: snap.length || '',
      ringSize: snap.ringSize || '',
      price: snap.price || '',
      quantity: Math.max(1, parseInt(snap.quantity, 10) || 1),
      favorite: editing ? editing.favorite === true : false,
      image: (editing && snap.image) ||
        (prefill && prefill.image ? { url: prefill.image, source: 'store' } : null),
      imageAuto: false,    // true while the photo tracks the typed name
      imageRemoved: false, // user said no photo — stop auto-matching
      prices: (editing && snap.prices) || null, // store pack pricing
      // Linked catalog product; follows the photo association (set on
      // match/pick, cleared on remove) and persists as snapshot.storeHandle.
      pricesHandle: (editing && snap.storeHandle) || null,
      priceAuto: null,     // last price WE filled in — never overwrite typing
      rating: seed ? Math.max(0, Math.min(5, Number(seed.rating || 0))) : 0,
      notes: seed ? (seed.notes || '') : '',
      tagIds: seed && Array.isArray(seed.tagIds) ? seed.tagIds.slice() : [],
      flavours: L.normalizeFlavours((snap.flavours || []).map(function (f) {
        return { label: f.label, value: f.value };
      })),
    };
    var tagLibrary = state.collectionUI.tagLibrary || [];

    // App's New Item layout: name → quick card (quantity / rating /
    // strength / favorite) → tags → details → notes → profile.
    return sheetShell(
      editing ? 'Edit cigar' : 'Add a cigar',
      '<div class="j-field j-suggest"><label for="j-stick-name">Name</label>' +
        '<input id="j-stick-name" type="text" placeholder="e.g. Padron 1964 Anniversary" autocomplete="off" value="' + esc(stickForm.name) + '">' +
        '<div class="j-suggest-list" id="j-stick-suggest"></div></div>' +
      '<div class="j-photo-block" id="j-photo-block">' +
        '<div class="j-photo-preview" id="j-photo-preview"></div>' +
        '<div class="j-photo-side">' +
          '<div class="j-photo-caption" id="j-photo-caption"></div>' +
          '<a class="j-buy-link" id="j-photo-buy" target="_blank" rel="noopener" style="display:none">' +
            svg(ICONS.cart, 12) + 'Buy now</a>' +
          '<div class="j-photo-actions">' +
            '<button class="j-photo-btn" type="button" id="j-photo-pick">Choose from catalog</button>' +
            '<button class="j-photo-btn" type="button" id="j-photo-upload">Use my photo</button>' +
            '<button class="j-photo-btn j-photo-btn-dim" type="button" id="j-photo-remove" style="display:none">Remove</button>' +
          '</div>' +
        '</div>' +
        '<input type="file" id="j-photo-file" accept="image/jpeg,image/png,image/webp" style="display:none">' +
      '</div>' +
      '<div class="j-photo-picker" id="j-photo-picker" style="display:none">' +
        '<div class="j-field"><label for="j-photo-search">Search the Klaro catalog</label>' +
          '<input id="j-photo-search" type="search" placeholder="Cigar or brand name" autocomplete="off"></div>' +
        '<div class="j-photo-grid" id="j-photo-grid"></div>' +
      '</div>' +
      '<div class="j-quick-card">' +
        '<div class="j-quick-row"><span class="j-quick-label">Quantity</span>' +
          '<div class="j-stepper j-stepper-sm">' +
            '<button type="button" id="j-stick-qty-minus">−</button>' +
            '<span class="j-stepper-value" id="j-stick-qty">' + stickForm.quantity + '</span>' +
            '<button type="button" id="j-stick-qty-plus">+</button>' +
          '</div></div>' +
        '<div class="j-quick-div"></div>' +
        '<div class="j-quick-row"><span class="j-quick-label">Rating</span>' +
          '<div class="j-star-input j-star-input-sm" id="j-stick-stars"></div></div>' +
        '<div class="j-quick-div"></div>' +
        '<div class="j-quick-row j-quick-row-col"><span class="j-quick-label">Strength</span>' +
          '<div class="j-chip-row" id="j-stick-strength">' +
            L.INTENSITY_OPTIONS.map(function (option) {
              return '<button class="j-choice-chip" type="button" data-strength="' + esc(option) + '">' +
                esc(shortStrengthLabel(option)) + '</button>';
            }).join('') +
          '</div></div>' +
        '<div class="j-quick-div"></div>' +
        '<div class="j-quick-row"><span class="j-quick-label">Favorite</span>' +
          '<button type="button" class="j-fav-toggle" id="j-stick-fav" aria-label="Toggle favorite">' +
            svg(ICONS.star, 18) + '</button></div>' +
      '</div>' +
      '<div class="j-section-label">Tags</div>' +
      '<div class="j-chip-row" id="j-stick-tags">' +
        tagLibrary.map(function (tag) {
          return '<button class="j-choice-chip" type="button" data-tag="' + esc(tag.id) + '" data-color="' + esc(tag.color || '') + '">' + esc(tag.label) + '</button>';
        }).join('') +
        '<button class="j-choice-chip" type="button" id="j-stick-new-tag">+ New tag</button>' +
      '</div>' +
      '<div class="j-section-label" style="margin-top:14px">Details</div>' +
      stickSuggestFieldHtml('brand', 'Brand', 'e.g. Padron', stickForm.brand) +
      stickSuggestFieldHtml('origin', 'Origin', 'e.g. Nicaragua', stickForm.origin) +
      '<div class="j-field-pair">' +
        '<div class="j-field"><label for="j-stick-length">Length</label>' +
          '<input id="j-stick-length" type="text" placeholder="e.g. 6 1/4" autocomplete="off" value="' + esc(stickForm.length) + '"></div>' +
        '<div class="j-field"><label for="j-stick-ring">Diameter</label>' +
          '<input id="j-stick-ring" type="text" inputmode="numeric" placeholder="e.g. 52" autocomplete="off" value="' + esc(stickForm.ringSize) + '"></div>' +
      '</div>' +
      '<div class="j-field"><label for="j-stick-price">Price</label>' +
        '<input id="j-stick-price" type="text" inputmode="decimal" placeholder="$0.00" autocomplete="off" value="' + esc(stickForm.price) + '">' +
        '<div class="j-chip-row j-price-chips" id="j-price-chips" style="display:none"></div></div>' +
      '<div class="j-field"><label for="j-stick-notes">Notes</label>' +
        '<textarea id="j-stick-notes" rows="3" placeholder="Notes, context, or anything you want to remember.">' + esc(stickForm.notes) + '</textarea></div>' +
      tastingProfileHtml() +
      '<div style="margin-top:20px; display:flex; flex-direction:column; gap:10px">' +
        '<button class="j-btn j-btn-primary" id="j-stick-save">' +
          (editing ? 'Save changes' : 'Add to journal') + '</button>' +
        (editing ? '' : '<button class="j-btn j-btn-secondary" id="j-stick-save-another">Add another</button>') +
      '</div>',
      // X close left / Save right in the header so a quick edit doesn't
      // require scrolling to the bottom (the bottom save stays too).
      '<button class="j-sheet-action" id="j-stick-save-top">' + (editing ? 'Save' : 'Add') + '</button>'
    );
  };

  var wireStickSheet = function (region) {
    region.querySelector('[data-close]').addEventListener('click', closeSheet);

    var profile = wireTastingProfile(region, function () {
      return stickForm.flavours;
    }, function (next) {
      stickForm.flavours = next;
    });

    // — Photo — auto-matched from the catalog as the name is typed; an
    // explicit pick, upload, or removal pins it (typing stops changing it).
    var photoNameInput = region.querySelector('#j-stick-name');
    var photoPreview = region.querySelector('#j-photo-preview');
    var photoCaption = region.querySelector('#j-photo-caption');
    var photoRemove = region.querySelector('#j-photo-remove');
    var photoPicker = region.querySelector('#j-photo-picker');
    var photoGrid = region.querySelector('#j-photo-grid');
    var photoSearchInput = region.querySelector('#j-photo-search');
    var photoFile = region.querySelector('#j-photo-file');
    var photoUploadBtn = region.querySelector('#j-photo-upload');

    var syncPhoto = function () {
      var url = imageUrlFor(stickForm.image);
      if (url) {
        photoPreview.innerHTML = '<img src="' + esc(thumbUrl(url, 360)) + '" alt="">';
        photoPreview.classList.add('has-photo');
        photoRemove.style.display = '';
      } else {
        photoPreview.innerHTML = svg(ICONS.photo, 22);
        photoPreview.classList.remove('has-photo');
        photoRemove.style.display = 'none';
      }
      var source = stickForm.image && stickForm.image.source;
      photoCaption.textContent = !stickForm.image
        ? 'Type a name to match a photo from the Klaro catalog.'
        : source === 'upload' ? 'Your photo'
          : source === 'order' ? 'From your klarocigars.com order'
            : stickForm.imageAuto ? 'Matched from the Klaro catalog' : 'From the Klaro catalog';
    };
    syncPhoto();

    // Store pack prices — chips under the Price field; tapping one fills it.
    var priceInput = region.querySelector('#j-stick-price');
    var priceChips = region.querySelector('#j-price-chips');
    // "Buy now" rides the catalog association — visible whenever the
    // cigar is linked to a store product, gone when the link is cleared.
    var photoBuy = region.querySelector('#j-photo-buy');
    var syncBuyLink = function () {
      if (!photoBuy) return;
      if (stickForm.pricesHandle) {
        photoBuy.href = '/products/' + encodeURIComponent(stickForm.pricesHandle);
        photoBuy.style.display = '';
      } else {
        photoBuy.style.display = 'none';
      }
    };
    var syncPriceChips = function () {
      syncBuyLink();
      var prices = stickForm.prices || [];
      if (prices.length === 0) {
        priceChips.style.display = 'none';
        priceChips.innerHTML = '';
        return;
      }
      var current = formatPrice(priceInput.value);
      priceChips.innerHTML = prices.map(function (p, i) {
        var on = p.price === current;
        return '<button class="j-choice-chip' + (on ? ' selected' : '') + '" type="button" data-price-index="' + i + '">' +
          esc([p.label, p.price].filter(Boolean).join(' · ')) + '</button>';
      }).join('');
      priceChips.style.display = '';
    };
    syncPriceChips();
    priceChips.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-price-index]');
      if (!chip) return;
      priceInput.value = (stickForm.prices || [])[Number(chip.getAttribute('data-price-index'))].price;
      syncPriceChips();
    });
    priceInput.addEventListener('input', syncPriceChips);

    var applyCatalogPrices = function (product) {
      if (!product || product.handle === stickForm.pricesHandle) return;
      stickForm.pricesHandle = product.handle || null;
      syncBuyLink(); // link is known before the price fetch lands
      fetchCatalogPrices(product).then(function (prices) {
        if (stickForm.pricesHandle !== (product.handle || null)) return; // stale
        stickForm.prices = prices;
        // Prefill with the single-stick price (real "Single" variant, or the
        // ~5-pack÷5 estimate — singles list first). Never overwrite typing.
        var current = priceInput.value.trim();
        if (prices && prices.length && (!current || current === stickForm.priceAuto)) {
          priceInput.value = prices[0].price;
          stickForm.priceAuto = prices[0].price;
        }
        syncPriceChips();
      });
    };
    var clearCatalogPrices = function () {
      stickForm.prices = null;
      stickForm.pricesHandle = null;
      // Take back a price we auto-filled; typed prices stay.
      if (stickForm.priceAuto && priceInput.value.trim() === stickForm.priceAuto) {
        priceInput.value = '';
      }
      stickForm.priceAuto = null;
      syncPriceChips();
    };

    var photoMatchTimer = null;
    photoNameInput.addEventListener('input', function () {
      if (photoMatchTimer) clearTimeout(photoMatchTimer);
      if (stickForm.imageRemoved) return;
      if (stickForm.image && !stickForm.imageAuto) return;
      var q = photoNameInput.value.trim();
      if (q.length < 3) return;
      photoMatchTimer = setTimeout(function () {
        searchCatalog(q).then(function (results) {
          if (photoNameInput.value.trim() !== q) return; // stale response
          if (stickForm.imageRemoved || (stickForm.image && !stickForm.imageAuto)) return;
          var hit = results[0];
          if (hit && hit.image) {
            stickForm.image = { url: hit.image, source: 'store' };
            stickForm.imageAuto = true;
            applyCatalogPrices(hit);
          } else if (stickForm.imageAuto) {
            stickForm.image = null;
            stickForm.imageAuto = false;
            clearCatalogPrices();
          }
          syncPhoto();
        });
      }, 300);
    });

    var renderPhotoGrid = function (results, query) {
      if (!results.length) {
        photoGrid.innerHTML = '<div class="j-photo-grid-empty">' +
          (query && query.length >= 2
            ? 'No catalog match for “' + esc(query) + '”.'
            : 'Search the catalog by cigar or brand name.') + '</div>';
        return;
      }
      photoGrid.innerHTML = results.map(function (p, i) {
        return '<button type="button" class="j-photo-cell" data-photo-index="' + i + '" title="' + esc(p.title) + '">' +
          '<img src="' + esc(thumbUrl(p.image, 240)) + '" alt="" loading="lazy">' +
          '<span class="j-photo-cell-title">' + esc(p.title) + '</span>' +
          '<span class="j-photo-cell-vendor">' +
            esc([p.vendor, p.price ? 'from ' + formatPrice(p.price) : ''].filter(Boolean).join(' · ')) +
          '</span>' +
        '</button>';
      }).join('');
      photoGrid.querySelectorAll('[data-photo-index]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var p = results[Number(btn.getAttribute('data-photo-index'))];
          stickForm.image = { url: p.image, source: 'store' };
          stickForm.imageAuto = false;
          stickForm.imageRemoved = false;
          // A catalog pick also fills identity gaps — never overwrites
          // what the user already typed.
          if (!photoNameInput.value.trim()) photoNameInput.value = p.title;
          var brandField = region.querySelector('#j-stick-brand');
          if (!brandField.value.trim() && p.vendor) brandField.value = p.vendor;
          if (!priceInput.value.trim() && p.price) {
            // Interim min-variant prefill; the singles-first prefill in
            // applyCatalogPrices replaces it once variants load.
            priceInput.value = formatPrice(p.price);
            stickForm.priceAuto = priceInput.value;
          }
          applyCatalogPrices(p);
          photoPicker.style.display = 'none';
          syncPhoto();
        });
      });
    };

    region.querySelector('#j-photo-pick').addEventListener('click', function () {
      var showing = photoPicker.style.display !== 'none';
      photoPicker.style.display = showing ? 'none' : '';
      if (showing) return;
      var prefill = photoNameInput.value.trim();
      photoSearchInput.value = prefill;
      renderPhotoGrid([], '');
      if (prefill) {
        searchCatalog(prefill).then(function (results) {
          if (photoSearchInput.value.trim() !== prefill) return; // stale
          renderPhotoGrid(results, prefill);
        });
      }
      photoSearchInput.focus();
    });

    var photoSearchTimer = null;
    photoSearchInput.addEventListener('input', function () {
      if (photoSearchTimer) clearTimeout(photoSearchTimer);
      var q = photoSearchInput.value.trim();
      photoSearchTimer = setTimeout(function () {
        searchCatalog(q).then(function (results) {
          if (photoSearchInput.value.trim() !== q) return;
          renderPhotoGrid(results, q);
        });
      }, 250);
    });

    photoRemove.addEventListener('click', function () {
      stickForm.image = null;
      stickForm.imageAuto = false;
      stickForm.imageRemoved = true;
      // Removing the photo rejects the product association — the pack
      // prices came from it, so they go too (the typed price stays).
      clearCatalogPrices();
      syncPhoto();
    });

    // Wishlist → Add: the store product is already known, so link its
    // pack pricing straight away (identity + photo were seeded in
    // stickSheetHtml). One-shot — consumed here so a plain Add stays blank.
    var wishPrefill = state.stickPrefill;
    state.stickPrefill = null;
    if (wishPrefill && !state.editingStick && wishPrefill.handle) {
      var linked = Array.isArray(CONFIG.catalog)
        ? CONFIG.catalog.find(function (p) { return p.handle === wishPrefill.handle; })
        : null;
      applyCatalogPrices(linked || { handle: wishPrefill.handle });
    }

    // "Use my photo" — downscale client-side, upload to the Klaro API,
    // keep the returned S3 key on the snapshot (URLs expire, keys don't).
    photoUploadBtn.addEventListener('click', function () {
      if (READ_ONLY) {
        showToast('Read-only mode — photo uploads are disabled.');
        return;
      }
      photoFile.click();
    });
    photoFile.addEventListener('change', function () {
      var file = photoFile.files && photoFile.files[0];
      photoFile.value = '';
      if (!file) return;
      photoUploadBtn.disabled = true;
      photoUploadBtn.textContent = 'Uploading…';
      var restore = function () {
        photoUploadBtn.disabled = false;
        photoUploadBtn.textContent = 'Use my photo';
      };
      downscalePhoto(file, 1200)
        .then(function (dataUrl) {
          return KlaroAPI.apiFetch('/journal/photo', { method: 'POST', body: { image: dataUrl } });
        })
        .then(function (body) {
          if (!body || !body.key) throw new Error('Upload failed');
          photoUrlCache[body.key] = body.url || '';
          stickForm.image = { key: body.key, source: 'upload' };
          stickForm.imageAuto = false;
          stickForm.imageRemoved = false;
          syncPhoto();
          restore();
        })
        .catch(function (err) {
          restore();
          if (err.status === 401) return handleUnauthorized();
          showToast(err.message || 'Could not upload that photo.', true);
        });
    });

    // Quantity stepper
    var qtyValue = region.querySelector('#j-stick-qty');
    region.querySelector('#j-stick-qty-minus').addEventListener('click', function () {
      stickForm.quantity = Math.max(1, stickForm.quantity - 1);
      qtyValue.textContent = stickForm.quantity;
    });
    region.querySelector('#j-stick-qty-plus').addEventListener('click', function () {
      stickForm.quantity = Math.min(999, stickForm.quantity + 1);
      qtyValue.textContent = stickForm.quantity;
    });

    // Favorite toggle
    var favBtn = region.querySelector('#j-stick-fav');
    var syncFav = function () {
      favBtn.classList.toggle('on', stickForm.favorite);
    };
    syncFav();
    favBtn.addEventListener('click', function () {
      stickForm.favorite = !stickForm.favorite;
      syncFav();
    });

    // Strength chips — selected chip takes the app's strength palette class.
    var strengthWrap = region.querySelector('#j-stick-strength');
    var syncStrength = function () {
      strengthWrap.querySelectorAll('[data-strength]').forEach(function (el) {
        var value = el.getAttribute('data-strength');
        var on = stickForm.strength === value;
        el.className = 'j-choice-chip' + (on ? ' selected ' + strengthClass(value) : '');
      });
    };
    syncStrength();
    strengthWrap.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-strength]');
      if (!chip) return;
      var value = chip.getAttribute('data-strength');
      stickForm.strength = stickForm.strength === value ? '' : value;
      syncStrength();
    });

    // Past-value chips for brand + origin (shown on focus, filter as you type)
    ['brand', 'origin'].forEach(function (field) {
      var input = region.querySelector('#j-stick-' + field);
      var wrap = region.querySelector('#j-stick-' + field + '-past');
      var row = region.querySelector('[data-past-row="' + field + '"]');
      var values = pastStickValues(field);
      var focused = false;
      var sync = function () {
        var q = input.value.trim().toLowerCase();
        var matches = values.filter(function (v) {
          var lower = v.toLowerCase();
          return lower !== q && (!q || lower.indexOf(q) !== -1);
        }).slice(0, 5);
        if (!focused || matches.length === 0) {
          wrap.style.display = 'none';
          return;
        }
        row.innerHTML = matches.map(function (v) {
          return '<button class="j-choice-chip" type="button" data-value="' + esc(v) + '">' + esc(v) + '</button>';
        }).join('');
        wrap.style.display = '';
      };
      input.addEventListener('focus', function () { focused = true; sync(); });
      input.addEventListener('input', sync);
      input.addEventListener('blur', function () {
        setTimeout(function () { focused = false; sync(); }, 180);
      });
      row.addEventListener('mousedown', function (e) { e.preventDefault(); });
      row.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-value]');
        if (!chip) return;
        input.value = chip.getAttribute('data-value');
        focused = false;
        sync();
      });
    });

    // Rating stars
    var starsWrap = region.querySelector('#j-stick-stars');
    var renderStars = function () {
      starsWrap.innerHTML = '';
      for (var i = 1; i <= 5; i++) {
        var btn = document.createElement('button');
        btn.setAttribute('data-star', i);
        btn.innerHTML =
          '<span style="color:' + (i <= stickForm.rating ? 'var(--j-accent)' : 'var(--j-star-off)') + '">' +
          svg(ICONS.star, 30) + '</span>';
        starsWrap.appendChild(btn);
      }
    };
    renderStars();
    starsWrap.addEventListener('click', function (e) {
      var star = e.target.closest('[data-star]');
      if (!star) return;
      var value = Number(star.getAttribute('data-star'));
      stickForm.rating = stickForm.rating === value ? 0 : value;
      renderStars();
    });

    // Inventory autocomplete on Name — picking a cigar you own pulls its
    // whole profile (brand, origin, strength, size, price, tasting notes).
    var nameInput = region.querySelector('#j-stick-name');
    var suggestList = region.querySelector('#j-stick-suggest');
    var renderStickSuggestions = function () {
      var q = nameInput.value.trim().toLowerCase();
      if (!q || q.length < 2 || state.editingStick) {
        suggestList.classList.remove('visible');
        return;
      }
      var matches = state.inventoryRecords.filter(function (record) {
        return record.item && (record.item.name || '').toLowerCase().indexOf(q) !== -1;
      }).slice(0, 6);
      if (matches.length === 0) {
        suggestList.classList.remove('visible');
        return;
      }
      suggestList.innerHTML = matches.map(function (record, i) {
        var rich = L.resolveCigarData(record.item);
        return (
          '<button type="button" data-suggest-index="' + i + '">' + esc(record.item.name) +
          '<span class="j-suggest-sub">' + esc([rich.maker, record.boxName].filter(Boolean).join(' · ')) + '</span></button>'
        );
      }).join('');
      suggestList.classList.add('visible');
      suggestList.querySelectorAll('[data-suggest-index]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var record = matches[Number(btn.getAttribute('data-suggest-index'))];
          var rich = L.resolveCigarData(record.item);
          nameInput.value = record.item.name;
          region.querySelector('#j-stick-brand').value = rich.maker || '';
          region.querySelector('#j-stick-origin').value = rich.origin || '';
          region.querySelector('#j-stick-length').value = rich.length || '';
          region.querySelector('#j-stick-ring').value = rich.ringSize || '';
          region.querySelector('#j-stick-price').value = rich.price || '';
          stickForm.strength = rich.intensity || '';
          syncStrength();
          stickForm.flavours = L.normalizeFlavours((rich.flavours || []).map(function (f) {
            return { label: f.label, value: f.value };
          }));
          profile.sync();
          if (imageUrlFor(rich.image)) {
            stickForm.image = rich.image;
            stickForm.imageAuto = false;
            syncPhoto();
          }
          suggestList.classList.remove('visible');
        });
      });
    };
    nameInput.addEventListener('input', renderStickSuggestions);
    nameInput.addEventListener('blur', function () {
      setTimeout(function () { suggestList.classList.remove('visible'); }, 180);
    });

    // Tags (same per-entry cap + tag coloring as the entry sheet)
    var tagWrap = region.querySelector('#j-stick-tags');
    var syncTags = function () {
      tagWrap.querySelectorAll('[data-tag]').forEach(function (el) {
        var on = stickForm.tagIds.indexOf(el.getAttribute('data-tag')) !== -1;
        el.classList.toggle('selected', on);
        var color = el.getAttribute('data-color');
        if (on && color) {
          el.style.background = color;
          el.style.borderColor = 'transparent';
          el.style.color = '#121212';
        } else {
          el.style.background = '';
          el.style.borderColor = '';
          el.style.color = '';
        }
      });
    };
    syncTags();
    tagWrap.addEventListener('click', function (e) {
      var tagBtn = e.target.closest('[data-tag]');
      if (tagBtn) {
        var id = tagBtn.getAttribute('data-tag');
        var at = stickForm.tagIds.indexOf(id);
        if (at === -1) {
          if (stickForm.tagIds.length >= L.MAX_TAGS_PER_ENTRY) {
            showToast('Up to ' + L.MAX_TAGS_PER_ENTRY + ' tags per entry.', true);
            return;
          }
          stickForm.tagIds.push(id);
        } else {
          stickForm.tagIds.splice(at, 1);
        }
        syncTags();
        return;
      }
      if (e.target.closest('#j-stick-new-tag')) {
        var label = prompt('New tag name');
        if (!label || !label.trim()) return;
        label = label.trim();
        var library = state.collectionUI.tagLibrary;
        if (library.length >= L.MAX_TAG_LIBRARY_SIZE) {
          showToast('Tag library is full.', true);
          return;
        }
        var existing = library.find(function (t) {
          return (t.label || '').toLowerCase() === label.toLowerCase();
        });
        var tag = existing;
        if (!tag) {
          tag = { id: L.generateTagId(), label: label, color: L.DEFAULT_TAG_COLOR, createdAt: Date.now() };
          library.push(tag);
          queueSave();
        }
        if (stickForm.tagIds.indexOf(tag.id) === -1) {
          if (stickForm.tagIds.length >= L.MAX_TAGS_PER_ENTRY) {
            showToast('Up to ' + L.MAX_TAGS_PER_ENTRY + ' tags per entry.', true);
            return;
          }
          stickForm.tagIds.push(tag.id);
        }
        var newBtn = document.createElement('button');
        newBtn.className = 'j-choice-chip selected';
        newBtn.setAttribute('type', 'button');
        newBtn.setAttribute('data-tag', tag.id);
        newBtn.setAttribute('data-color', tag.color || '');
        newBtn.textContent = tag.label;
        tagWrap.insertBefore(newBtn, tagWrap.querySelector('#j-stick-new-tag'));
        syncTags();
      }
    });

    var doSave = function (addAnother) {
      var name = region.querySelector('#j-stick-name').value.trim();
      if (!name) {
        showToast('Give the cigar a name.', true);
        return;
      }
      var brand = region.querySelector('#j-stick-brand').value.trim();
      var details = {
        origin: region.querySelector('#j-stick-origin').value.trim(),
        strength: stickForm.strength,
        length: region.querySelector('#j-stick-length').value.trim(),
        ringSize: region.querySelector('#j-stick-ring').value.trim(),
        price: formatPrice(region.querySelector('#j-stick-price').value),
      };
      var notes = region.querySelector('#j-stick-notes').value.trim();
      var signature = L.buildJournalSnapshotSignature({ name: name, brand: brand });

      // Same favorite → store-wishlist bridge as the quick toggle.
      if (stickForm.favorite && stickForm.pricesHandle) {
        addToStoreWishlist({
          handle: stickForm.pricesHandle,
          title: name,
          vendor: brand,
          image: imageUrlFor(stickForm.image) || null,
        });
      }

      if (state.editingStick) {
        // Identity + details apply to every entry in the group so the
        // rename survives group re-derivation.
        var group = state.editingStick;
        var entries = L.entriesForJournalGroup(state.collectionUI.journalEntries, group);
        var now = Date.now();
        entries.forEach(function (entry) {
          entry.snapshot = Object.assign({}, entry.snapshot, {
            name: name,
            brand: brand,
            origin: details.origin,
            strength: details.strength,
            length: details.length,
            ringSize: details.ringSize,
            price: details.price,
            image: stickForm.image || null,
            prices: (stickForm.prices && stickForm.prices.length ? stickForm.prices : null),
            storeHandle: stickForm.pricesHandle || null,
          });
          entry.updatedAt = now;
        });
        var seed = findStickSeedEntry(group);
        if (seed) {
          seed.rating = stickForm.rating;
          seed.notes = notes;
          seed.tagIds = stickForm.tagIds.slice();
          seed.updatedAt = now;
        }
        // Flavours + quantity + favorite ride on one snapshot (the seed's,
        // or the first entry's for groups born from free-form entries) —
        // group derivation picks the first snapshot with signal.
        var flavourHost = seed || entries[0] || null;
        if (flavourHost) {
          flavourHost.snapshot = Object.assign({}, flavourHost.snapshot, {
            flavours: L.normalizeFlavours(stickForm.flavours),
            quantity: stickForm.quantity,
            favorite: stickForm.favorite,
          });
        }
        state.editingStick = null;
        queueSave();
        closeSheet();
        renderJournal();
        state.detailGroup = allJournalGroups().find(function (g) {
          return L.buildJournalSnapshotSignature(g.snapshot || {}) === signature;
        }) || null;
        if (state.detailGroup) openSheet('detail');
        showToast('Cigar updated');
        return;
      }

      // Duplicate guard — mirror the app's "already in this unit" alert.
      var duplicate = allJournalGroups().find(function (g) {
        return g.groupKey === signature ||
          L.buildJournalSnapshotSignature(g.snapshot || {}) === signature;
      });
      if (duplicate) {
        state.detailGroup = duplicate;
        closeSheet();
        // renderJournal recreates the sheet region, so closeSheet's delayed
        // innerHTML wipe targets the detached old node, not the new sheet.
        renderJournal();
        openSheet('detail');
        showToast('Already in your journal — opened it.');
        return;
      }

      var seedEntry = L.createStickSeedEntry({
        name: name,
        brand: brand,
        origin: details.origin,
        strength: details.strength,
        length: details.length,
        ringSize: details.ringSize,
        price: details.price,
        image: stickForm.image,
        prices: stickForm.prices,
        storeHandle: stickForm.pricesHandle,
        flavours: stickForm.flavours,
        quantity: stickForm.quantity,
        favorite: stickForm.favorite,
        rating: stickForm.rating,
        notes: notes,
        tagIds: stickForm.tagIds,
      });
      state.collectionUI.journalEntries.push(seedEntry);
      queueSave();
      closeSheet();
      renderJournal();
      if (addAnother) {
        // App's "Add Another": save, then a fresh blank form.
        state.editingStick = null;
        openSheet('stick');
        showToast('Added ' + name + ' — add another');
        return;
      }
      state.detailGroup = allJournalGroups().find(function (g) {
        return L.buildJournalSnapshotSignature(g.snapshot || {}) === signature;
      }) || null;
      if (state.detailGroup) openSheet('detail');
      showToast('Added to your journal');
    };
    region.querySelector('#j-stick-save').addEventListener('click', function () { doSave(false); });
    region.querySelector('#j-stick-save-top').addEventListener('click', function () { doSave(false); });
    var saveAnother = region.querySelector('#j-stick-save-another');
    if (saveAnother) saveAnother.addEventListener('click', function () { doSave(true); });
  };

  // — Import from orders sheet —
  // CONFIG.orders is rendered by Liquid (latest 50 orders); the header
  // ticker shows CONFIG.ordersCount (lifetime total).

  var ordersAvailable = function () {
    return !READ_ONLY && Array.isArray(CONFIG.orders) && CONFIG.orders.some(function (order) {
      return order && Array.isArray(order.items) && order.items.length > 0;
    });
  };

  var buildOrderImportRows = function () {
    var map = new Map();
    (CONFIG.orders || []).forEach(function (order) {
      var at = Number((order && order.processedAt) || 0);
      ((order && order.items) || []).forEach(function (line) {
        if (!line || !line.title) return;
        var key = L.buildJournalSnapshotSignature({ name: line.title, brand: line.vendor });
        var row = map.get(key);
        if (!row) {
          row = {
            key: key,
            title: line.title,
            vendor: line.vendor || '',
            type: line.type || '',
            quantity: 0,
            lastAt: 0,
            price: '',
            image: '',
            handle: null,
          };
          map.set(key, row);
        }
        if (!row.handle) row.handle = productHandleFromUrl(line.url);
        row.quantity += Number(line.quantity || 0);
        if (at >= row.lastAt) {
          row.lastAt = at;
          if (line.price != null && line.price !== '') row.price = formatPrice(line.price);
        }
        if (!row.image && line.image) row.image = line.image;
      });
    });

    var existingKeys = {};
    allJournalGroups().forEach(function (g) {
      existingKeys[g.groupKey] = 1;
      existingKeys[L.buildJournalSnapshotSignature(g.snapshot || {})] = 1;
    });

    var rows = Array.from(map.values()).map(function (row) {
      row.inJournal = !!existingKeys[row.key];
      return row;
    }).sort(function (a, b) { return b.lastAt - a.lastAt; });

    // Journal about cigars, not lighters: keep product type "Cigars"
    // (accessories/humidors/lighters drop out). If the store's types don't
    // match at all, fall back to everything rather than an empty sheet.
    var cigars = rows.filter(function (row) {
      var type = (row.type || '').toLowerCase();
      return type.indexOf('cigar') !== -1 && type.indexOf('accessor') === -1;
    });
    return cigars.length > 0 ? cigars : rows;
  };

  // Order history per cigar, derived live from CONFIG.orders each load —
  // fresher than persisting it, and new orders appear automatically.
  // Map: snapshot signature → { lines: [...], totalQty, totalSpent, lastAt }
  var orderHistoryCache = null;
  // "/products/<handle>" out of any store URL (order lines, Swym items).
  var productHandleFromUrl = function (url) {
    var m = String(url || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  };

  var orderHistoryByKey = function () {
    if (orderHistoryCache) return orderHistoryCache;
    var map = new Map();
    (CONFIG.orders || []).forEach(function (order) {
      var at = Number((order && order.processedAt) || 0);
      ((order && order.items) || []).forEach(function (line) {
        if (!line || !line.title) return;
        var key = L.buildJournalSnapshotSignature({ name: line.title, brand: line.vendor });
        var bucket = map.get(key);
        if (!bucket) {
          bucket = { lines: [], totalQty: 0, totalSpent: 0, lastAt: 0, image: '', handle: null };
          map.set(key, bucket);
        }
        if (!bucket.image && line.image) bucket.image = line.image;
        if (!bucket.handle) bucket.handle = productHandleFromUrl(line.url);
        var qty = Number(line.quantity || 0);
        var price = Number(line.price || 0);
        bucket.lines.push({
          orderName: (order && order.name) || '',
          at: at,
          quantity: qty,
          price: price,
        });
        bucket.totalQty += qty;
        bucket.totalSpent += qty * price;
        if (at > bucket.lastAt) bucket.lastAt = at;
      });
    });
    map.forEach(function (bucket) {
      bucket.lines.sort(function (a, b) { return b.at - a.at; });
    });
    orderHistoryCache = map;
    return map;
  };

  var orderHistoryForGroup = function (group) {
    if (!group) return null;
    return orderHistoryByKey().get(
      L.buildJournalSnapshotSignature((group && group.snapshot) || {})
    ) || null;
  };

  // A group is "ordered" if its cigar appears in the purchase history —
  // live match on the latest 50 orders, or the persisted fromOrder flag
  // for older imports whose orders rotated out of that window.
  var isOrderedGroup = function (group) {
    if (!group) return false;
    if ((group.entries || []).some(function (e) { return e && e.fromOrder === true; })) return true;
    return !!orderHistoryForGroup(group);
  };

  // Auto-import: the customer is signed into klarocigars.com, so their
  // purchased cigars land in the journal automatically on load. Processed
  // order keys are recorded in collection_ui (webOrderImports) so a cigar
  // the user deletes stays deleted — we never re-import a key we've seen.
  var autoImportOrders = function () {
    if (READ_ONLY || !ordersAvailable()) return;
    var processed = Array.isArray(state.collectionUI.webOrderImports)
      ? state.collectionUI.webOrderImports.slice()
      : [];
    var rows = buildOrderImportRows();
    var changed = false;
    var added = 0;
    rows.forEach(function (row) {
      if (processed.indexOf(row.key) === -1) {
        // Already journaled by hand → just record the key; otherwise create
        // the cigar from the order line.
        if (!row.inJournal) {
          state.collectionUI.journalEntries.push(L.createStickSeedEntry({
            name: row.title,
            brand: row.vendor,
            price: row.price,
            quantity: row.quantity,
            image: row.image ? { url: row.image, source: 'order' } : null,
            // Purchased cigars are store products — link them so they get
            // Buy now / pricing / catalog notes like catalog-picked ones.
            storeHandle: row.handle || null,
            occurredAt: row.lastAt || Date.now(),
            fromOrder: true,
          }));
          added++;
        }
        processed.push(row.key);
        changed = true;
      }
    });
    if (changed) {
      state.collectionUI.webOrderImports = processed;
      state.autoImportedCount = added;
      queueSave();
    }
  };

  // — Merge sheet ("I have a Klaro app account under another email") —

  var mergeSheetHtml = function () {
    return sheetShell(
      'Link another account',
      '<p style="margin:0 0 14px; color:var(--j-text-dim); font-size:14px; line-height:1.5">' +
        'Used the Klaro app with a different email? Enter that account’s login and its ' +
        'journal, inventory and membership move into this one. This can’t be undone.</p>' +
      '<p class="j-auth-error" id="j-merge-error"></p>' +
      '<form id="j-merge-form">' +
        '<div class="j-field"><label for="j-merge-email">Other account email</label>' +
          '<input id="j-merge-email" type="email" autocomplete="off" required></div>' +
        '<div class="j-field"><label for="j-merge-password">That account’s password</label>' +
          '<input id="j-merge-password" type="password" autocomplete="off" placeholder="••••••••" required></div>' +
        '<button class="j-btn j-btn-primary" type="submit" id="j-merge-btn">Link accounts</button>' +
      '</form>'
    );
  };

  var wireMergeSheet = function (region) {
    region.querySelector('[data-close]').addEventListener('click', closeSheet);
    region.querySelector('#j-merge-form').addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = region.querySelector('#j-merge-btn');
      var errEl = region.querySelector('#j-merge-error');
      var email = region.querySelector('#j-merge-email').value.trim();
      var password = region.querySelector('#j-merge-password').value;
      if (!email || !password) return;
      btn.disabled = true;
      btn.textContent = 'Linking…';
      errEl.className = 'j-auth-error';
      KlaroAPI.mergeAccount(email, password)
        .then(function (body) {
          var merged = body && body.collection_ui;
          if (merged && typeof merged === 'object') {
            state.collectionUI = merged;
            if (!Array.isArray(state.collectionUI.journalEntries)) state.collectionUI.journalEntries = [];
            if (!Array.isArray(state.collectionUI.tagLibrary)) state.collectionUI.tagLibrary = [];
            var tombstones = state.collectionUI.deletedJournalEntryIds || [];
            if (tombstones.length > 0) {
              state.collectionUI.journalEntries = state.collectionUI.journalEntries.filter(function (entry) {
                return !entry || tombstones.indexOf(entry.id) === -1;
              });
            }
          }
          closeSheet();
          showToast('Accounts linked — everything is in one journal now.');
          // Inventory may have moved over too; reload it.
          loadData();
        })
        .catch(function (err) {
          btn.disabled = false;
          btn.textContent = 'Link accounts';
          errEl.textContent = err.message || 'Could not link that account.';
          errEl.className = 'j-auth-error visible';
        });
    });
  };

  // — Group detail sheet (entry history + edit/delete) —

  // On-hand rows for the open detail sheet (built in detailSheetHtml,
  // wired in wireDetailSheet — same lifecycle as entryForm/stickForm).
  var detailQtyRows = null;

  // Store pricing as a labeled grid — one cell per pack option (label over
  // price) instead of a run-on line. A vitola prefix shared by every option
  // ("Belicoso / …") moves up into the section label so the cells read
  // Single / 5 Pack / Full Box.
  var priceGridHtml = function (prices) {
    var labels = prices.map(function (p) { return p.label || ''; });
    var prefixMatch = labels[0].match(/^(.+?)\s*\/\s*/);
    var shared = prefixMatch && labels.length > 1 && labels.every(function (l) {
      return l.indexOf(prefixMatch[0]) === 0;
    }) ? prefixMatch : null;
    return '<div class="j-section-label">Store pricing' +
      (shared ? ' · ' + esc(shared[1]) : '') + '</div>' +
      '<div class="j-price-grid">' +
      prices.map(function (p) {
        var label = shared ? (p.label || '').slice(shared[0].length) : (p.label || '');
        return '<div class="j-price-cell">' +
          '<span class="j-price-cell-label">' + esc(label || 'Each') + '</span>' +
          '<span class="j-price-cell-value">' + esc(p.price) + '</span>' +
        '</div>';
      }).join('') +
      '</div>';
  };

  var detailSheetHtml = function () {
    var group = state.detailGroup;
    var entries = L.sortEntriesByOccurredDesc(L.entriesForJournalGroup(state.collectionUI.journalEntries, group));
    if (entries.length === 0) entries = L.sortEntriesByOccurredDesc(group.entries || []);
    var snapshot = group.snapshot || {};
    var sClass = strengthClass(snapshot.strength);
    var orderHistory = orderHistoryForGroup(group);
    // Effective store link: the saved handle, or — for purchased cigars
    // whose entries predate handle support — the live order-line handle.
    var linkedHandle = snapshot.storeHandle || (orderHistory && orderHistory.handle) || null;

    var storePrices = Array.isArray(snapshot.prices) && snapshot.prices.length ? snapshot.prices : null;
    var detailBits = [];
    if (snapshot.origin) detailBits.push(esc(snapshot.origin));
    if (snapshot.length || snapshot.ringSize) {
      detailBits.push(esc([snapshot.length, snapshot.ringSize].filter(Boolean).join(' × ')));
    }
    // The pricing section covers price whenever we have store options
    // (saved, or loading live via the linked handle) — repeating it in
    // the facts line was noise.
    if (snapshot.price && !storePrices && !linkedHandle) detailBits.push(esc(snapshot.price));

    var detailPhotoUrl = imageUrlFor(groupImage(group));
    var header =
      '<div style="padding-bottom:6px">' +
        (detailPhotoUrl
          ? '<div class="j-detail-photo"><img src="' + esc(thumbUrl(detailPhotoUrl, 600)) + '" alt=""></div>'
          : '') +
        '<div class="j-detail-title-row">' +
          '<h2 class="j-detail-title">' + esc(L.getJournalDisplayTitle(group)) + '</h2>' +
          (linkedHandle
            ? '<a class="j-buy-btn" href="/products/' + esc(encodeURIComponent(linkedHandle)) +
              '" target="_blank" rel="noopener">' + svg(ICONS.cart, 13) + 'Buy now</a>'
            : '') +
        '</div>' +
        (group.rating > 0 ? starsHtml(Math.round(group.rating), 15) : '') +
        '<div class="j-meta-row" style="margin-top:10px">' +
          (snapshot.strength && sClass ? '<span class="j-chip j-chip-strength ' + sClass + '">' + esc(snapshot.strength) + '</span>' : '') +
          (snapshot.brand ? '<span class="j-chip j-chip-brand">' + esc(snapshot.brand) + '</span>' : '') +
          (group.activeLocationLabel ? '<span class="j-chip j-chip-active">' + svg(ICONS.archiveCheck, 10) + esc(group.activeLocationLabel) + '</span>' : '') +
        '</div>' +
        (detailBits.length ? '<div class="j-detail-meta">' + detailBits.join(' · ') + '</div>' : '') +
      '</div>' +
      (storePrices
        ? priceGridHtml(storePrices)
        // Store-linked without saved pricing (e.g. order imports) — the
        // grid loads live from the product in wireDetailSheet.
        : linkedHandle ? '<div id="j-detail-prices-slot"></div>' : '');

    // Unified on-hand — one row per source: each app humidor/box holding
    // this cigar (live inventory, edited via the same PATCH the app uses)
    // plus the web journal's own count for cigars that live journal-side.
    // Quick edits happen right here, no trip into the full edit form.
    detailQtyRows = [];
    (group.items || []).forEach(function (boxItem) {
      var record = state.inventoryRecords.find(function (r) {
        return r.item && boxItem && String(r.item.id) === String(boxItem.id);
      });
      if (record) {
        detailQtyRows.push({
          kind: 'box',
          itemId: record.item.id,
          label: record.boxName || 'Humidor',
          qty: Math.max(0, parseInt(record.item.quantity, 10) || 0),
        });
      }
    });
    var qtyHost = findStickSeedEntry(group) ||
      (group.itemId == null
        ? (L.entriesForJournalGroup(state.collectionUI.journalEntries, group)[0] || null)
        : null);
    if (qtyHost) {
      detailQtyRows.push({
        kind: 'journal',
        host: qtyHost,
        // The section label above the card already says "On hand" — the
        // lone-row label names where the sticks live.
        label: detailQtyRows.length > 0 ? 'Journal (web)' : 'Journal',
        qty: Math.max(0, parseInt(qtyHost.snapshot && qtyHost.snapshot.quantity, 10) || 0),
      });
    }
    // Two boxes can share a name ("Travel Case" twice) — suffix repeats so
    // the rows stay tellable-apart.
    var labelCounts = {};
    detailQtyRows.forEach(function (row) {
      labelCounts[row.label] = (labelCounts[row.label] || 0) + 1;
    });
    var labelSeen = {};
    detailQtyRows.forEach(function (row) {
      if (labelCounts[row.label] < 2) return;
      labelSeen[row.label] = (labelSeen[row.label] || 0) + 1;
      row.label = row.label + ' (' + labelSeen[row.label] + ')';
    });
    // Quick favorite rides in the same card as the on-hand rows.
    var favoriteRow =
      '<div class="j-qty-row">' +
        '<span class="j-qty-row-label">Favorite</span>' +
        (READ_ONLY
          ? '<span class="j-qty-row-value">' + (isFavoriteGroup(group) ? '★' : '—') + '</span>'
          : '<button type="button" class="j-fav-toggle' + (isFavoriteGroup(group) ? ' on' : '') +
            '" id="j-detail-fav" aria-label="Toggle favorite">' + svg(ICONS.star, 18) + '</button>') +
      '</div>';
    var totalOnHand = detailQtyRows.reduce(function (sum, r) { return sum + r.qty; }, 0);
    header +=
      '<div class="j-section-label">On hand</div>' +
      '<div class="j-qty-card" id="j-qty-card">' +
        (detailQtyRows.length > 1
          ? '<div class="j-qty-total"><span>Total</span><span id="j-qty-total">' + totalOnHand + '</span></div>'
          : '') +
        detailQtyRows.map(function (row, i) {
          return '<div class="j-qty-row">' +
            '<span class="j-qty-row-label">' + esc(row.label) + '</span>' +
            (READ_ONLY
              ? '<span class="j-qty-row-value" data-qty-value="' + i + '">' + row.qty + '</span>'
              : '<div class="j-stepper j-stepper-sm">' +
                  '<button type="button" data-qty-minus="' + i + '">−</button>' +
                  '<span class="j-stepper-value" data-qty-value="' + i + '">' + row.qty + '</span>' +
                  '<button type="button" data-qty-plus="' + i + '">+</button>' +
                '</div>') +
          '</div>';
        }).join('') +
        favoriteRow +
      '</div>' +
      // Logging a smoke is the sheet's primary action — it lives up here
      // with the quick controls, not buried under the history list.
      '<button class="j-btn j-btn-secondary j-detail-add-top" id="j-detail-add">' +
        svg(ICONS.plus, 16) + 'Add entry for this cigar</button>';

    // Store tasting description — loaded async in wireDetailSheet from the
    // linked catalog product (snapshot only carries the handle).
    var storeNotesHtml = linkedHandle
      ? '<div id="j-store-notes" style="display:none">' +
          '<div class="j-section-label">From the Klaro catalog</div>' +
          '<p class="j-store-notes-text" id="j-store-notes-text"></p>' +
          '<button class="j-store-notes-more" id="j-store-notes-more" type="button">Read more</button>' +
        '</div>'
      : '';
    header += storeNotesHtml;

    // Tasting profile — radar + palate line whenever the cigar has flavour
    // data (from a linked inventory item or a web-added stick).
    var profileHtml = '';
    if (L.hasFlavourSignal(snapshot.flavours)) {
      var palateBits = PALATE_SLIDERS.map(function (slider) {
        var value = valueForLabel(snapshot.flavours, slider.flavourLabel);
        if (toDisplayScore(value) <= 0) return '';
        return esc(slider.label) + ' ' + esc(descriptorForScore(value, [slider.left, 'Medium', slider.right]));
      }).filter(Boolean);
      profileHtml =
        '<div class="j-section-label">Tasting profile</div>' +
        '<div class="j-prof-radar j-prof-radar-detail">' + radarSvg(snapshot.flavours, 300) + '</div>' +
        (palateBits.length ? '<div class="j-detail-meta" style="text-align:center; margin:0 0 14px">' + palateBits.join(' · ') + '</div>' : '');
    }

    var rows = entries.map(function (entry, i) {
      var isSynthetic = entry.synthetic === true;
      var isSeed = entry.initialRating === true && !isSynthetic;
      return (
        '<div class="j-history-row">' +
          '<div class="j-history-top">' +
            starsHtml(entry.rating || 0, 11) +
            '<span class="j-history-date">' + esc(L.formatEntryDate(entry.occurredAt || entry.createdAt)) + '</span>' +
          '</div>' +
          (entry.notes ? '<div class="j-history-notes">' + esc(entry.notes) + '</div>' : '') +
          (entry.contextNotes ? '<div class="j-history-notes" style="color:var(--j-text-dim)">' + esc(entry.contextNotes) + '</div>' : '') +
          (isSynthetic
            ? '<div class="j-history-synthetic">Generated from this item’s rating in your collection</div>'
            : isSeed
              ? '<div class="j-history-synthetic">Added to your journal</div>' +
                (READ_ONLY ? '' :
                  '<div class="j-history-actions">' +
                    '<button class="j-history-delete" data-delete-index="' + i + '">Delete</button>' +
                  '</div>')
              : READ_ONLY
                ? ''
                : '<div class="j-history-actions">' +
                    '<button class="j-history-edit" data-edit-index="' + i + '">Edit</button>' +
                    '<button class="j-history-delete" data-delete-index="' + i + '">Delete</button>' +
                  '</div>') +
        '</div>'
      );
    }).join('');

    // Cigar details are editable only for journal-side sticks — groups
    // backed by a real inventory item get their details from the app.
    var canEditStick = !READ_ONLY && group.itemId == null;

    // "Edit cigar details" lives in the header action (Edit ↔ Save), so the
    // bottom of the sheet keeps a single CTA.
    var journalPane =
      '<div class="j-section-label">History · ' + entries.length + ' ' + (entries.length === 1 ? 'entry' : 'entries') + '</div>' +
      rows;

    // Orders tab — the cigar's purchase history from klarocigars.com.
    // (orderHistory computed up top alongside linkedHandle.)
    var ordersPane = '';
    var tabsHtml = '';
    if (orderHistory) {
      var summaryBits = [
        orderHistory.totalQty + ' stick' + (orderHistory.totalQty === 1 ? '' : 's') + ' ordered',
        '$' + orderHistory.totalSpent.toFixed(2) + ' total',
      ];
      if (orderHistory.lastAt) summaryBits.push('last ' + L.formatEntryDate(orderHistory.lastAt));
      ordersPane =
        '<div class="j-order-summary">' + summaryBits.map(esc).join(' · ') + '</div>' +
        orderHistory.lines.map(function (line) {
          return (
            '<div class="j-order-row">' +
              '<div class="j-order-row-main">' +
                '<span class="j-order-row-name">Order ' + esc(line.orderName) + '</span>' +
                '<span class="j-order-row-date">' + esc(L.formatEntryDate(line.at)) + '</span>' +
              '</div>' +
              '<div class="j-order-row-qty">' + line.quantity + ' × $' + line.price.toFixed(2) + '</div>' +
            '</div>'
          );
        }).join('') +
        '<div class="j-order-note">From your klarocigars.com order history.</div>';
      tabsHtml =
        '<div class="j-detail-tabs">' +
          '<button class="j-detail-tab active" type="button" data-detail-tab="journal">Journal</button>' +
          '<button class="j-detail-tab" type="button" data-detail-tab="orders">Orders' +
            '<span class="j-detail-tab-count">' + orderHistory.lines.length + '</span></button>' +
        '</div>';
    }

    // Header action doubles: 'Edit' (cigar details) while the sheet is
    // clean, 'Save' once quantities change — one CTA, not two.
    var detailAction = READ_ONLY
      ? '<span></span>'
      : '<button class="j-sheet-action" id="j-detail-action"' +
          (canEditStick ? '' : ' style="display:none"') + '>' +
          (canEditStick ? 'Edit' : 'Save') + '</button>';

    // Title lives in the body (j-detail-title); the sheet header keeps
    // just X / Edit·Save so it isn't shown twice.
    return sheetShell(
      '',
      header +
        profileHtml +
        tabsHtml +
        '<div id="j-detail-pane-journal">' + journalPane + '</div>' +
        (orderHistory ? '<div id="j-detail-pane-orders" style="display:none">' + ordersPane + '</div>' : ''),
      detailAction
    );
  };

  var wireDetailSheet = function (region) {
    var group = state.detailGroup;
    var entries = L.sortEntriesByOccurredDesc(L.entriesForJournalGroup(state.collectionUI.journalEntries, group));
    if (entries.length === 0) entries = L.sortEntriesByOccurredDesc(group.entries || []);

    region.querySelector('[data-close]').addEventListener('click', function () {
      state.detailGroup = null;
      closeSheet();
    });
    var detailAdd = region.querySelector('#j-detail-add');
    if (detailAdd) {
      detailAdd.addEventListener('click', function () {
        if (READ_ONLY) {
          showToast('Read-only mode — adding entries is disabled while production writes are off.');
          return;
        }
        state.editingEntry = null;
        openSheet('entry');
      });
    }
    // Header action: 'Edit' while clean → opens cigar details; once any
    // quantity changes it becomes 'Save' (flushes pending writes, closes).
    var detailAction = region.querySelector('#j-detail-action');
    var detailDirty = false;
    var flushQtyPatches = function () {};
    var markDetailDirty = function () {
      if (detailDirty) return;
      detailDirty = true;
      if (detailAction) {
        detailAction.textContent = 'Save';
        detailAction.style.display = '';
      }
    };
    if (detailAction) {
      detailAction.addEventListener('click', function () {
        if (!detailDirty) {
          state.editingStick = group;
          openSheet('stick');
          return;
        }
        detailAction.disabled = true;
        flushQtyPatches();
        Promise.resolve(flushSave()).then(function () {
          state.detailGroup = null;
          closeSheet();
          showToast('Saved');
        });
      });
    }
    // Unified on-hand steppers. Journal rows write the snapshot the stick
    // form uses; box rows PATCH the app inventory item (optimistic UI,
    // debounced per item so held taps don't spam the API).
    var qtyCard = region.querySelector('#j-qty-card');
    if (qtyCard && !READ_ONLY && detailQtyRows) {
      var qtyPatchTimers = {};
      var pendingBoxPatches = {};
      var syncQtyTotal = function () {
        var totalEl = region.querySelector('#j-qty-total');
        if (totalEl) {
          totalEl.textContent = detailQtyRows.reduce(function (sum, r) { return sum + r.qty; }, 0);
        }
      };
      var fireBoxPatch = function (itemId, qty) {
        delete pendingBoxPatches[itemId];
        KlaroAPI.updateCollection(itemId, { quantity: qty })
          .then(function () { renderList(); })
          .catch(function (err) {
            if (err.status === 401) return handleUnauthorized();
            showToast('Could not update the humidor quantity.', true);
          });
      };
      flushQtyPatches = function () {
        Object.keys(pendingBoxPatches).forEach(function (itemId) {
          if (qtyPatchTimers[itemId]) clearTimeout(qtyPatchTimers[itemId]);
          fireBoxPatch(itemId, pendingBoxPatches[itemId]);
        });
      };
      var applyQtyDelta = function (i, delta) {
        var row = detailQtyRows[i];
        if (!row) return;
        var next = Math.max(0, Math.min(999, row.qty + delta));
        if (next === row.qty) return;
        row.qty = next;
        region.querySelector('[data-qty-value="' + i + '"]').textContent = next;
        syncQtyTotal();
        markDetailDirty();
        if (row.kind === 'journal') {
          row.host.snapshot = Object.assign({}, row.host.snapshot, { quantity: next });
          row.host.updatedAt = Date.now();
          group.snapshot = Object.assign({}, group.snapshot, { quantity: next });
          refreshHeaderStats();
          queueSave();
          return;
        }
        var record = state.inventoryRecords.find(function (r) {
          return r.item && String(r.item.id) === String(row.itemId);
        });
        if (record) record.item.quantity = next;
        refreshHeaderStats();
        pendingBoxPatches[row.itemId] = next;
        if (qtyPatchTimers[row.itemId]) clearTimeout(qtyPatchTimers[row.itemId]);
        qtyPatchTimers[row.itemId] = setTimeout(function () {
          fireBoxPatch(row.itemId, next);
        }, 600);
      };
      qtyCard.addEventListener('click', function (e) {
        var minus = e.target.closest('[data-qty-minus]');
        if (minus) return applyQtyDelta(Number(minus.getAttribute('data-qty-minus')), -1);
        var plus = e.target.closest('[data-qty-plus]');
        if (plus) return applyQtyDelta(Number(plus.getAttribute('data-qty-plus')), 1);
      });
    }

    var detailFav = region.querySelector('#j-detail-fav');
    if (detailFav) {
      detailFav.addEventListener('click', function () {
        var on = toggleGroupFavorite(group);
        detailFav.classList.toggle('on', on);
        showToast(on ? 'Added to favorites' : 'Removed from favorites');
      });
    }

    var linkedHandle = ((group.snapshot || {}).storeHandle) ||
      ((orderHistoryForGroup(group) || {}).handle) || null;
    // Order-linked cigars without saved pack pricing get it live.
    var pricesSlot = region.querySelector('#j-detail-prices-slot');
    if (pricesSlot && linkedHandle) {
      fetchCatalogPrices({ handle: linkedHandle }).then(function (prices) {
        if (!prices || !prices.length || !document.body.contains(pricesSlot)) return;
        pricesSlot.innerHTML = priceGridHtml(prices);
      });
    }
    var storeNotes = region.querySelector('#j-store-notes');
    if (storeNotes && linkedHandle) {
      fetchCatalogDescription(linkedHandle).then(function (text) {
        if (!text || !document.body.contains(storeNotes)) return;
        var textEl = storeNotes.querySelector('#j-store-notes-text');
        var moreBtn = storeNotes.querySelector('#j-store-notes-more');
        textEl.textContent = text;
        storeNotes.style.display = '';
        requestAnimationFrame(function () {
          // Short descriptions don't need the toggle.
          if (textEl.scrollHeight <= textEl.clientHeight + 2) moreBtn.style.display = 'none';
        });
        moreBtn.addEventListener('click', function () {
          var expanded = textEl.classList.toggle('expanded');
          moreBtn.textContent = expanded ? 'Show less' : 'Read more';
        });
      });
    }
    var detailTabs = region.querySelector('.j-detail-tabs');
    if (detailTabs) {
      detailTabs.addEventListener('click', function (e) {
        var tab = e.target.closest('[data-detail-tab]');
        if (!tab) return;
        var which = tab.getAttribute('data-detail-tab');
        detailTabs.querySelectorAll('[data-detail-tab]').forEach(function (b) {
          b.classList.toggle('active', b === tab);
        });
        region.querySelector('#j-detail-pane-journal').style.display = which === 'journal' ? '' : 'none';
        var ordersPane = region.querySelector('#j-detail-pane-orders');
        if (ordersPane) ordersPane.style.display = which === 'orders' ? '' : 'none';
      });
    }
    region.querySelector('.j-sheet-body').addEventListener('click', function (e) {
      var edit = e.target.closest('[data-edit-index]');
      if (edit) {
        state.editingEntry = entries[Number(edit.getAttribute('data-edit-index'))];
        openSheet('entry');
        return;
      }
      var del = e.target.closest('[data-delete-index]');
      if (del) {
        var entry = entries[Number(del.getAttribute('data-delete-index'))];
        if (!entry || !entry.id) return;
        if (entry.synthetic) {
          alert('This journal card is generated from the item in your collection. Remove its rating or journal it manually if you want a separate entry you can delete.');
          return;
        }
        if (!confirm('Delete journal entry? This cannot be undone.')) return;
        state.collectionUI.journalEntries = state.collectionUI.journalEntries.filter(function (x) {
          return x.id !== entry.id;
        });
        if (!Array.isArray(state.collectionUI.deletedJournalEntryIds)) {
          state.collectionUI.deletedJournalEntryIds = [];
        }
        state.collectionUI.deletedJournalEntryIds.push(entry.id);
        state.detailGroup = null;
        closeSheet();
        renderJournal();
        showToast('Entry deleted');
        queueSave();
      }
    });
  };

  // Hamburger lives in the chrome bar of every view — delegate once.
  // The floating bottom dock (bottom-dock.liquid, rendered outside the
  // root so re-renders don't touch it) rebinds here too: on the journal
  // its menu opens the JS drawer and the book slot scrolls to the top
  // instead of reloading the page.
  document.addEventListener('click', function (e) {
    if (e.target.closest('#j-menu-btn') || e.target.closest('#kc-dock-menu')) openDrawer();
    if (e.target.closest('.kc-dock-journal')) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (e.target.closest('#j-site-wishlist')) {
      // On-page jump to the Wishlist tab; inert on pre-auth screens
      // where the journal isn't rendered yet.
      if (state.view !== 'journal' || !state.collectionUI) return;
      tabTouched = true;
      state.sourceTab = 'wishlist';
      savePrefs();
      renderJournal();
    }
  });

  // Flush pending saves when leaving the page.
  window.addEventListener('beforeunload', function () {
    if (saveTimer) flushSave();
  });

  // A bfcache/tab-restored page carries the login state (and CONFIG) it was
  // first rendered with — reload so Liquid re-renders for the live session.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) location.reload();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
